/*
 * Product page (product-main.liquid) behaviour: gallery, variant switching,
 * purchase-type / bundle pricing, and the accordion drawer.
 *
 * Extracted verbatim from an inline <script> in sections/product-main.liquid
 * (IO-4700). That section sits ~8 KB over Shopify's 256 KB template limit, which
 * makes it un-pushable via the CLI - only the GitHub integration will deploy it -
 * and therefore impossible to QA on a test theme. Moving these 66 KB out puts the
 * section back under the limit, and this file gets CDN-cached across product pages
 * instead of being re-sent inline with every PDP response.
 *
 * The two Liquid values this needs arrive via the [data-product-main-config] JSON
 * block the section renders immediately before loading this file - keep in sync.
 */

(function () {
  var cfg = JSON.parse(document.querySelector('[data-product-main-config]').textContent);
  var SECTION_ID = cfg.sectionId;
  var PRODUCT_URL = cfg.productUrl;
  var scopeSel = '#section__' + SECTION_ID;
  // Kaching Bundles price / placeholder / badge helpers (assets/kaching-bundles-core.js, loaded first).
  // Without the core file only our own n+1 / kiekinis / base tiers work; Kaching deals are not drawn.
  const KB_OK = !!window.KikaKaching;
  const KB = window.KikaKaching || {
    esc: (s) => String(s == null ? '' : s).replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;'),
    moneyFormatter: () => (cents) => formatMoney(cents),
    fill: (s) => s,
    badgeBoxAttrs: () => ({ cls: '', style: '' }),
    badgesFor: () => [],
    layoutBorderBadges: () => {},
  };
  const TRUCK_ICON = '<svg viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M3 6h11v9H3zM14 9h4l3 3v3h-7zM6.5 18.5a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3zm11 0a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3z"/></svg>';

  if (!window.customElements.get('product-gallery')) {
    class ProductGallery extends HTMLElement {
      connectedCallback() {
        const id = this.dataset.sectionId;
        this.locale = this.dataset.locale;
        this.variantData = JSON.parse(this.querySelector('#VariantImgData-' + id).textContent);
        this.productData = JSON.parse(this.querySelector('#ProductImgData-' + id).textContent);
        this.shopifyData = JSON.parse(this.querySelector('#ShopifyImgData-' + id).textContent);
        const videoEl = this.querySelector('#ProductVideoData-' + id);
        this.videoData = videoEl ? JSON.parse(videoEl.textContent) : [];
        this.thumbs = this.querySelector('.product-gallery__thumbs');
        this.viewer = this.querySelector('.product-gallery__viewer');
        this.images = this.buildList(this.dataset.variantId);
        this.activeIndex = 0;
        this.thumbs.addEventListener('click', (e) => {
          const item = e.target.closest('.product-gallery__thumb');
          if (item) this.setActive(parseInt(item.dataset.index, 10));
        });
        document.addEventListener('change', (e) => {
          if (e.target.name === 'id') this.onVariantChange(e.target.value);
        });

        // Mobile: swipeable main image (thumb strip stays in sync).
        this.initSwipe();

        // Fullscreen gallery — except on a video, where the click plays it inline instead.
        this.viewer.addEventListener('click', () => {
          if (this.suppressClick) { this.suppressClick = false; return; }
          if (this.videoPlaying) return;
          const item = this.images[this.activeIndex];
          if (item && item.type === 'video') { this.playVideo(item); return; }
          this.openLightbox(this.activeIndex);
        });
        this.lightbox = this.querySelector('.product-gallery__lightbox');
        // Portal the overlay to <body> so it escapes the sticky gallery's stacking context
        // (nested, its z-index can't beat the info column). Drop a stale one from a prior render.
        const staleLightbox = document.getElementById(this.lightbox.id);
        if (staleLightbox && staleLightbox !== this.lightbox) staleLightbox.remove();
        document.body.appendChild(this.lightbox);
        this.lightboxImg = this.lightbox.querySelector('.product-gallery__lightbox-img');
        this.lightboxIndexEl = this.lightbox.querySelector('.product-gallery__lightbox-index');
        this.lightboxTotalEl = this.lightbox.querySelector('.product-gallery__lightbox-total');
        this.lightboxPrev = this.lightbox.querySelector('.product-gallery__lightbox-nav--prev');
        this.lightboxNext = this.lightbox.querySelector('.product-gallery__lightbox-nav--next');
        this.lightboxStage = this.lightbox.querySelector('.product-gallery__lightbox-stage');
        this.onKeydown = this.onKeydown.bind(this);
        this.lightbox.querySelector('.product-gallery__lightbox-close').addEventListener('click', () => this.closeLightbox());
        this.lightboxPrev.addEventListener('click', () => this.lightboxGo(-1));
        this.lightboxNext.addEventListener('click', () => this.lightboxGo(1));
        this.lightbox.addEventListener('click', (e) => {
          if (e.target === this.lightbox || e.target === this.lightboxStage) this.closeLightbox();
        });
        let touchX = null;
        this.lightbox.addEventListener('touchstart', (e) => { touchX = e.changedTouches[0].clientX; }, { passive: true });
        this.lightbox.addEventListener('touchend', (e) => {
          if (touchX === null) return;
          const dx = e.changedTouches[0].clientX - touchX;
          if (Math.abs(dx) > 40) this.lightboxGo(dx < 0 ? 1 : -1);
          touchX = null;
        }, { passive: true });
      }
      matchesLocale(lang) { return !lang || lang === this.locale; }
      filterSort(arr) {
        if (!Array.isArray(arr)) return [];
        return arr
          .filter((img) => {
            const type = img.type || img.ImageType;
            const lang = img.language || img.ImageLanguage;
            return type !== 'Icons_photo' && type !== 'Values_photo' && this.matchesLocale(lang);
          })
          .sort((a, b) => ((a.row_no != null ? a.row_no : a.ImageRowNo) || 0) - ((b.row_no != null ? b.row_no : b.ImageRowNo) || 0));
      }
      // Videos are product-level, so they tail whichever image set wins — matching the order the
      // Liquid renders the thumbs in, which is what data-index refers to.
      videoItems() {
        return (this.videoData || []).map((vid) => ({
          type: 'video',
          videoId: vid,
          src: 'https://img.youtube.com/vi/' + vid + '/hqdefault.jpg',
          thumb: 'https://img.youtube.com/vi/' + vid + '/hqdefault.jpg',
          alt: ''
        }));
      }
      // Which viewer treatment a PIM image gets — see the --bleed / --cover CSS for the reasoning.
      // Kept off the item's `type` field, which is reserved for the 'video' marker.
      fitFor(rawType) {
        if (rawType === 'Emotion_photo') return ' product-gallery__active-img--bleed product-gallery__active-img--cover';
        if (rawType === 'Visual_photo') return ' product-gallery__active-img--bleed';
        return '';
      }
      buildList(variantId) {
        const videos = this.videoItems();
        const v = this.filterSort(this.variantData[variantId]).map((i) => ({ src: i.image_url, thumb: i.image_url, alt: '', fit: this.fitFor(i.type) }));
        if (v.length) return v.concat(videos);
        const p = this.filterSort(this.productData).map((i) => ({ src: i.ImageUrl, thumb: i.ImageUrl, alt: '', fit: this.fitFor(i.ImageType) }));
        if (p.length) return p.concat(videos);
        return this.shopifyData.concat(videos);
      }
      onVariantChange(variantId) { this.images = this.buildList(variantId); this.render(); }
      viewerHTML(item) {
        if (!item) return '';
        const img = '<img class="product-gallery__active-img' + (item.fit || '') + '" src="' + item.src + '" alt="" width="1200" height="1200">';
        if (item.type === 'video') {
          return img + '<span class="product-gallery__play" aria-hidden="true">' +
            '<svg viewBox="0 0 24 24" width="28" height="28" fill="currentColor"><path d="M8 5.5v13l11-6.5-11-6.5Z"/></svg></span>';
        }
        return img + '<span class="product-gallery__expand" aria-hidden="true">' +
          '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 3 21 3 21 9"></polyline><polyline points="9 21 3 21 3 15"></polyline><line x1="21" y1="3" x2="14" y2="10"></line><line x1="3" y1="21" x2="10" y2="14"></line></svg></span>';
      }
      setActive(index) {
        this.activeIndex = index;
        this.thumbs.querySelectorAll('.product-gallery__thumb').forEach((t, i) => t.classList.toggle('is-active', i === index));
        const item = this.images[index];
        const main = this.viewer.querySelector('.product-gallery__active-img');
        const isVideo = !!(item && item.type === 'video');
        // Only a plain image -> plain image step can just swap the src (no flicker). Anything
        // involving a video poster or a playing iframe rebuilds the viewer's chrome.
        const viewerIsVideo = this.videoPlaying || !!this.viewer.querySelector('.product-gallery__play');
        if (main && !isVideo && !viewerIsVideo) {
          main.src = item.src;
          // The src swap reuses the <img>, so its fit classes have to be re-applied here too —
          // otherwise a packshot keeps a full-bleed image's treatment (or the reverse).
          main.className = 'product-gallery__active-img' + (item.fit || '');
        } else {
          this.videoPlaying = false;
          this.viewer.innerHTML = this.viewerHTML(item);
        }
        this.viewer.classList.toggle('is-video', isVideo);
        this.scrollActiveThumbIntoView();
      }
      playVideo(item) {
        this.viewer.innerHTML = '<iframe class="product-gallery__video" src="https://www.youtube.com/embed/' + item.videoId +
          '?autoplay=1&controls=1&modestbranding=1&playsinline=1&rel=0" title="' + (this.dataset.videoTitle || '') +
          '" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowfullscreen></iframe>';
        this.videoPlaying = true;
      }
      // Main-image horizontal swipe -> prev/next (mobile). A real swipe suppresses the tap so it
      // doesn't also open the lightbox; vertical drags fall through to normal page scrolling.
      initSwipe() {
        let x = null, y = null, moved = false;
        this.viewer.addEventListener('touchstart', (e) => {
          x = e.changedTouches[0].clientX; y = e.changedTouches[0].clientY; moved = false;
        }, { passive: true });
        this.viewer.addEventListener('touchmove', (e) => {
          if (x === null) return;
          const dx = e.changedTouches[0].clientX - x;
          const dy = e.changedTouches[0].clientY - y;
          if (Math.abs(dx) > 10 && Math.abs(dx) > Math.abs(dy)) moved = true;
        }, { passive: true });
        this.viewer.addEventListener('touchend', (e) => {
          if (x === null) return;
          const dx = e.changedTouches[0].clientX - x;
          const n = this.images.length;
          if (moved && Math.abs(dx) > 40 && n > 1) {
            this.setActive((this.activeIndex + (dx < 0 ? 1 : -1) + n) % n);
            this.suppressClick = true;
          }
          x = null;
        }, { passive: true });
      }
      scrollActiveThumbIntoView() {
        const active = this.thumbs.querySelector('.product-gallery__thumb.is-active');
        if (!active) return;
        const cRect = this.thumbs.getBoundingClientRect();
        const aRect = active.getBoundingClientRect();
        const target = this.thumbs.scrollLeft + (aRect.left - cRect.left) - (this.thumbs.clientWidth - aRect.width) / 2;
        this.thumbs.scrollTo({ left: Math.max(0, target), behavior: 'smooth' });
      }
      render() {
        this.activeIndex = 0;
        this.videoPlaying = false;
        this.thumbs.innerHTML = this.images.map((img, i) =>
          '<li class="product-gallery__thumb' + (img.type === 'video' ? ' product-gallery__thumb--video' : '') + (i === 0 ? ' is-active' : '') + '" data-index="' + i + '">' +
            '<button type="button" class="product-gallery__thumb-btn">' +
              '<img src="' + (img.thumb || img.src) + '" alt="" class="product-gallery__thumb-img" loading="lazy" width="56" height="56">' +
              (img.type === 'video'
                ? '<span class="product-gallery__thumb-play" aria-hidden="true"><svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor"><path d="M8 5.5v13l11-6.5-11-6.5Z"/></svg></span>'
                : '') +
            '</button>' +
          '</li>'
        ).join('');
        this.viewer.innerHTML = this.viewerHTML(this.images[0]);
        this.viewer.classList.toggle('is-video', !!(this.images[0] && this.images[0].type === 'video'));
        this.thumbs.scrollTo({ left: 0 });
      }
      openLightbox(index) {
        if (!this.images || !this.images.length) return;
        this.setActive(index);
        this.renderLightbox();
        this.lightbox.hidden = false;
        document.body.style.overflow = 'hidden';
        document.addEventListener('keydown', this.onKeydown);
        this.lightbox.querySelector('.product-gallery__lightbox-close').focus();
      }
      closeLightbox() {
        this.lightbox.hidden = true;
        document.body.style.overflow = '';
        document.removeEventListener('keydown', this.onKeydown);
        this.clearLightboxVideo();
      }
      clearLightboxVideo() {
        const frame = this.lightboxStage.querySelector('.product-gallery__lightbox-video');
        if (frame) frame.remove();
        this.lightboxImg.hidden = false;
      }
      lightboxGo(delta) {
        const n = this.images.length;
        if (!n) return;
        this.setActive((this.activeIndex + delta + n) % n);
        this.renderLightbox();
      }
      renderLightbox() {
        const img = this.images[this.activeIndex];
        if (!img) return;
        // Reachable for a video only by arrowing/swiping to it from an image — a click on the
        // video in the viewer plays inline instead of opening this.
        this.clearLightboxVideo();
        if (img.type === 'video') {
          this.lightboxImg.hidden = true;
          const frame = document.createElement('iframe');
          frame.className = 'product-gallery__lightbox-video';
          frame.src = 'https://www.youtube.com/embed/' + img.videoId + '?controls=1&modestbranding=1&playsinline=1&rel=0';
          frame.title = this.dataset.videoTitle || '';
          frame.setAttribute('frameborder', '0');
          frame.setAttribute('allow', 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture');
          frame.setAttribute('allowfullscreen', '');
          this.lightboxStage.appendChild(frame);
        } else {
          this.lightboxImg.src = img.src;
          this.lightboxImg.alt = img.alt || '';
        }
        this.lightboxIndexEl.textContent = this.activeIndex + 1;
        this.lightboxTotalEl.textContent = this.images.length;
        const single = this.images.length <= 1;
        this.lightboxPrev.hidden = single;
        this.lightboxNext.hidden = single;
      }
      onKeydown(e) {
        if (this.lightbox.hidden) return;
        if (e.key === 'Escape') this.closeLightbox();
        else if (e.key === 'ArrowLeft') this.lightboxGo(-1);
        else if (e.key === 'ArrowRight') this.lightboxGo(1);
      }
    }
    window.customElements.define('product-gallery', ProductGallery);
  }

  // Variant pills: sync form [name=id], SKU/label, gallery, and refresh the purchase blocks.
  if (!window.customElements.get('product-variants')) {
    class ProductVariants extends HTMLElement {
      connectedCallback() {
        this.scope = this.closest('.product-main') || document;
        this.currentLabel = this.querySelector('.product-main__variants-current');
        this.querySelectorAll('.product-main__variant-pill').forEach((pill) => {
          pill.addEventListener('click', () => {
            if (pill.getAttribute('aria-disabled') === 'true') return;
            this.select(pill);
          });
        });
      }
      select(pill) {
        const variantId = pill.dataset.variantId;
        this.querySelectorAll('.product-main__variant-pill').forEach((p) => p.classList.toggle('is-active', p === pill));
        // data-title holds the untruncated variant title (the pill label is capped at 16 chars).
        if (this.currentLabel) this.currentLabel.textContent = pill.dataset.title || pill.textContent.trim();

        const skuEl = this.scope.querySelector('.product-main__sku');
        const skuValue = this.scope.querySelector('.product-main__sku-value');
        if (skuValue) {
          const sku = pill.dataset.sku || '';
          skuValue.textContent = sku;
          if (skuEl) skuEl.hidden = sku === '';
        }

        const idInput = this.scope.querySelector('.product-buy__id') || this.scope.querySelector('[name="id"]');
        if (idInput) {
          idInput.value = variantId;
          idInput.dispatchEvent(new Event('change', { bubbles: true })); // gallery listens on document
        }
        const bundlesUI = this.scope.querySelector('kaching-bundles-ui'); // client-side, not server-refreshed
        if (bundlesUI && bundlesUI.setVariant) bundlesUI.setVariant(variantId);
        refreshVariant(this.scope, variantId);
      }
    }
    window.customElements.define('product-variants', ProductVariants);
  }

  // Subscription / one-time toggle + frequency select -> selling_plan input (in buy_buttons form).
  if (!window.customElements.get('product-purchase')) {
    class ProductPurchase extends HTMLElement {
      connectedCallback() { initPurchase(this); }
    }
    window.customElements.define('product-purchase', ProductPurchase);
  }

  function initPurchase(root) {
    const scope = root.closest('.product-main') || document;
    const freq = root.querySelector('.product-buy__freq');
    const subPriceEl = root.querySelector('.product-buy__sub-price');

    function apply() {
      const checked = root.querySelector('.product-buy__type-radio:checked');
      const value = checked ? checked.value : 'onetime';
      root.querySelectorAll('.product-buy__type').forEach((card) => {
        const radio = card.querySelector('.product-buy__type-radio');
        card.classList.toggle('is-active', radio && radio.checked);
      });
      // N+1 and subscription are mutually exclusive discounts. Selecting subscription while an
      // n+1 bundle tier is checked reverts that tier to the base tier so the n+1 isn't also added.
      // (This lives in apply() — not a scope `change` listener — because the card's click handler
      // sets the radio checked programmatically, which suppresses the native `change` event.)
      if (value === 'subscribe') {
        const nplusChecked = scope.querySelector('.product-buy__bundle-radio[data-nplus1]:checked');
        if (nplusChecked) {
          const base = scope.querySelector('.product-buy__bundle-radio[data-base]');
          const ui = scope.querySelector('kaching-bundles-ui');
          if (base) { base.checked = true; if (ui && ui.setActive) ui.setActive(); }
        }
      }
      const planId = value === 'subscribe' && freq ? freq.value : null;
      const planInput = scope.querySelector('.product-buy__selling-plan'); // lives in buy_buttons form
      if (planInput) {
        if (planId) {
          planInput.value = planId;
          planInput.name = 'selling_plan';       // product-form.js only submits when named exactly this
        } else {
          planInput.value = '';
          planInput.name = 'selling_plan_off';    // hidden from product-form.js -> no empty selling_plan
        }
      }
      // Reflect the n+1 <-> subscription visibility (and re-sync stepper/prices after any revert).
      syncIncompat(scope);
      syncBundleStepper(scope);
      updatePurchasePrices(scope);
      // Add-ons that follow the chosen selling plan (upsells, subscription-only gifts) re-price.
      scope.dispatchEvent(new CustomEvent('purchase:plan', { bubbles: true }));
    }

    root.syncType = apply; // let the scope re-derive selling_plan/is-active after a programmatic revert
    root.querySelectorAll('.product-buy__type-radio').forEach((r) => r.addEventListener('change', apply));
    root.querySelectorAll('.product-buy__type').forEach((card) => {
      card.addEventListener('click', (e) => {
        if (e.target.closest('.product-buy__freq') || e.target.closest('a') || e.target.closest('[data-info]')) return;
        const radio = card.querySelector('.product-buy__type-radio');
        if (radio && !radio.checked) { radio.checked = true; apply(); }
      });
    });
    if (freq) {
      freq.addEventListener('change', () => {
        const opt = freq.options[freq.selectedIndex];
        // Update the per-unit basis; updatePurchasePrices re-multiplies by the current qty.
        if (subPriceEl && opt && opt.dataset.price != null) subPriceEl.dataset.unitPrice = opt.dataset.price;
        updatePurchasePrices(scope);
        apply();
      });
    }
    apply();
    updatePurchasePrices(scope);
  }

  // Headless Kaching Bundles. Kaching is the DATA source for the deal (bars, gifts, upsells, badges,
  // prices); we render it in our own markup (assets/kaching-bundles-ui.css) and compute every price
  // with the same rules Kaching's checkout Function applies (assets/kaching-bundles-core.js).
  // A plain quantity-break / bxgy bar is still a bare qty-N add that Kaching's own request
  // interceptor decorates (verified live), so our radios are name="bundles" and product-form.js
  // multiplies the cart quantity as usual. Anything the interceptor cannot know about (gifts,
  // upsells, personalisation, collection-break slots, bundle / mix-and-match / sku bars) is posted
  // as explicit __kaching_bundles lines by getKachingGiftLines() below.
  if (!window.customElements.get('kaching-bundles-ui')) {
    class KachingBundlesUI extends HTMLElement {
      connectedCallback() {
        this.optionsEl = this.querySelector('[data-bundle-options]');
        this.host = this.querySelector('.product-buy__kaching-host');
        this.variantId = this.dataset.variantId;
        this.savePrefix = this.dataset.savePrefix || '';
        this.freeWord = this.dataset.freeWord || '';
        this.qtyMin = parseInt(this.dataset.qtyMin, 10) || 1;
        // N+1 tier is resolved per variant: {variantId: {q: totalQty, f: freeQty}}. From the
        // variant's custom.nplus1_qty (f = 1) or, IO-5753, the collection's nplus_buy/nplus_get
        // (e.g. 2+2 -> q 4, f 2). See the data-variant-nplus1 map in product-main.liquid.
        try { this.nplus1ByVariant = JSON.parse(this.querySelector('[data-variant-nplus1]').textContent); }
        catch (e) { this.nplus1ByVariant = {}; }
        this.readNplus1(this.variantId);
        try { this.prices = JSON.parse(this.querySelector('[data-variant-prices]').textContent); }
        catch (e) { this.prices = {}; }
        // Per-variant loyalty price (base tier displays this instead of the plain price).
        try { this.loyaltyPrices = JSON.parse(this.querySelector('[data-variant-loyalty]').textContent); }
        catch (e) { this.loyaltyPrices = {}; }
        // Per-variant kiekinis quantity-break tiers ({variantId: [{qty, disc}, ...]}) from
        // custom.discount_data — another quantity-only source alongside Kaching + n+1.
        try { this.kiekinisByVariant = JSON.parse(this.querySelector('[data-variant-kiekinis]').textContent); }
        catch (e) { this.kiekinisByVariant = {}; }
        // Set before anything can render (our own tiers price through it too); a deal replaces it.
        this.moneyFn = KB.moneyFormatter(null, false);
        this.kachingBars = []; // every Kaching bar (any type), sans our own base tier
        this.tiers = [];
        this.kMedia = {};
        this.loadPageProduct();
        // Show the card immediately for an n+1 or kiekinis deal — don't gate on Kaching resolving.
        if (this.nplus1Qty > this.qtyMin || this.hasKiekinis(this.variantId)) this.rebuild();
        this.resolveDeals();
        // A checkbox upsell that matches the deal quantity follows the stepper on the base tier
        // (initQty updates the base radio's data-add-qty on the same input event — run after it).
        const scope = this.closest('.product-main');
        if (scope) {
          scope.addEventListener('input', (e) => {
            if (!e.target.matches || !e.target.matches('.product-buy__qty-input')) return;
            if (!this.kCheckboxUpsells || !this.kCheckboxUpsells.upsells.some((u) => u.matchQty)) return;
            setTimeout(() => { this.renderCheckboxUpsells(); this.renderSavings(); }, 0);
          });
          // The purchase-type card chose / dropped a selling plan: add-ons priced under the plan follow.
          scope.addEventListener('purchase:plan', () => this.onPlanChange(true));
        }
      }

      // The page product in Kaching's shape: the app embed ships it as
      // script.kaching-bundles-product (variants with selling plans and inventory, the product's
      // metafields for {{metafield}}); other products on the page ride along. Liquid's own
      // per-variant prices stay authoritative for the page product.
      loadPageProduct() {
        this.kProd = {};
        document.querySelectorAll('script.kaching-bundles-product').forEach((s) => {
          try {
            const p = JSON.parse(s.textContent);
            if (p && p.id) this.kProd['gid://shopify/Product/' + p.id] = p;
          } catch (e) { /* skip a malformed product */ }
        });
        const gid = 'gid://shopify/Product/' + this.dataset.productId;
        let own = this.kProd[gid];
        if (!own) {
          own = {
            id: Number(this.dataset.productId), title: this.dataset.productTitle || '', image: this.dataset.productImage || null,
            availableForSale: true, requiresSellingPlan: false, sellingPlans: [], metafields: {}, options: [],
            variants: Object.keys(this.prices).map((id) => ({
              id: Number(id), availableForSale: true, price: this.prices[id], compareAtPrice: null, options: [], image: null, sellingPlans: [], inventoryQuantity: null,
            })),
          };
        }
        own.title = this.dataset.productTitle || own.title;
        own.gid = gid;
        own.variants.forEach((v) => { if (this.prices[v.id] != null) v.price = this.prices[v.id]; });
        this.kProd[gid] = own;
        this.pageProd = own;
      }

      pageVariant() {
        return this.pageProd.variants.find((v) => String(v.id) === String(this.variantId)) || this.pageProd.variants[0];
      }

      // The selling plan the purchase-type card has chosen, as the page product's plan object.
      currentPlan() {
        const scope = this.closest('.product-main') || document;
        const input = scope.querySelector('.product-buy__selling-plan');
        if (!input || input.name !== 'selling_plan' || !input.value) return null;
        const id = Number(input.value);
        return this.pageProd.sellingPlans.find((p) => p.id === id) || { id: id, name: '', priceAdjustment: null };
      }

      // Kaching's own system strings (translated per locale, LT defaults in the config).
      sys(key) {
        return (this.kTr && this.kTr[key]) || ((this.kCfg && this.kCfg.defaultTranslations) || {})[key] || '';
      }

      media(gid) { return gid ? (this.kMedia[gid] || '') : ''; }

      money(cents, opts) { return this.moneyFn ? this.moneyFn(cents, opts) : formatMoney(cents); }

      // external = the purchase card announced a change (not us finishing our own switch of it).
      onPlanChange(external) {
        if (!this.kDeal) return;
        // The card left the subscription while a bar that carries a plan is checked: the line would still
        // carry the plan, so the card goes back to it (the shopper picks another bar to buy one-time).
        const checked = this.optionsEl.querySelector('.product-buy__bundle-radio:checked');
        const ct = checked && checked.dataset.barId && this.tiers.find((t) => t.barId === checked.dataset.barId);
        // A fresh announcement from the card (e.g. its blocks were just swapped for a new variant): try again.
        if (external && !this.kSyncing) this.kSyncFailed = null;
        if (external && !this.kSyncing && ct && ct.raw && ct.raw.sellingPlanEnabled && !this.currentPlan() && this.tierPlan(ct)) { this.syncCardToBar(ct); return; }
        // Bar prices follow the plan when bars (or the deal) are priced under it.
        if (this.tiers.length && !this.kSyncing && (this.kBars.some((b) => b.raw.sellingPlanEnabled) || this.kDeal.subscriptionsEnabled)) { this.render(); return; }
        this.renderGifts();
        this.renderCheckboxUpsells();
        this.renderProgressive();
        this.renderSavings();
      }

      // IO-5815: the Kaching Bundles app embed server-renders this product's FULL deal config in
      // script.kaching-bundles-deal-block-settings (empty when no deal applies). It carries what
      // the trimmed __kachingCartBundlesDeals copy below lacks: block title, bar title/subtitle/
      // label/badge, preselected bar, discount type, gift selectors. Read it first; the polling
      // path is only a fallback for a page without the embed's settings.
      resolveDeals() {
        if (!KB_OK) return; // core not loaded: no Kaching deal, our own tiers still show
        const read = this.readKachingDeal();
        if (read === 'ok') {
          // Bars that need other products (bundle / mix-and-match / sku) can't be drawn until the
          // Storefront products arrive: hold the first paint for them rather than reflow later.
          if (this.needsProductsFirst()) {
            this.loadGiftProducts();
            if (this.giftProducts) this.rebuild(); // nothing to fetch after all
            return;
          }
          this.rebuild();
          return;
        }
        // 'gated' = the deal exists but not for this market / customer: no Kaching tiers (the
        // polling fallback can't apply that gating). connectedCallback already showed our own.
        if (read === 'gated') return;
        this.pollDeals();
      }

      needsProductsFirst() {
        const token = (this.kCfg && this.kCfg.storefrontAccessToken) || window.shopifyStorefrontToken;
        return !!token && this.kachingBars.some((b) => !b.qtyBased);
      }

      readKachingDeal() {
        const json = (el) => { try { return el ? JSON.parse(el.textContent) : null; } catch (e) { return null; } };
        const pid = this.dataset.productId;
        let deal = null;
        document.querySelectorAll('script.kaching-bundles-deal-block-settings[data-product-id="' + pid + '"]').forEach((s) => {
          if (!deal && s.textContent.trim()) deal = json(s);
        });
        const cfg = json(document.getElementById('kaching-bundles-config'));
        if (!deal || !cfg || !Array.isArray(deal.dealBars)) return 'none';
        // Same client-side gating the native widget applies (market, B2B). Kaching does not
        // filter these server-side.
        const market = String(cfg.marketId);
        if (deal.marketId && String(deal.marketId) !== market) return 'gated';
        if ((deal.excludedMarketIds || []).map(String).indexOf(market) !== -1) return 'gated';
        if (deal.excludeB2bCustomers && cfg.b2bCustomer) return 'gated';
        // Where the deal applies (Kaching's own product filter): selected products / collections, or all but the
        // excluded products and collections. Collections come from the page product's own list.
        const pidN = Number(pid);
        const cols = this.pageProd && this.pageProd.collectionIds;
        // Ids compared as strings on both sides: a number-vs-string mismatch must never hide a deal Kaching shows.
        const inList = (a, v) => (a || []).map(String).indexOf(String(v)) !== -1;
        if (deal.blockVisibility === 'selected-products' && !inList(deal.selectedProductIds, pidN)) return 'gated';
        if (deal.blockVisibility === 'selected-collections' && cols && !cols.some((c) => inList(deal.selectedCollectionIds, c))) return 'gated';
        if (deal.blockVisibility === 'excluded-products' && (inList(deal.excludedProductIds, pidN) || (cols && cols.some((c) => inList(deal.excludedCollectionIds, c))))) return 'gated';
        // Colour settings may be "brand_*" tokens: resolve them once, like the widget.
        deal = KB.resolveBrand(deal, cfg.brandColors);
        // Translations: #kaching-bundles-translations maps ORIGINAL text -> translated text per
        // locale, under dealBlocks[<deal uuid>] (+ shared "system.*" keys). LT is the source
        // language (empty map); kika.lv is a market on this store and gets its LV strings here.
        let tr = {};
        const all = json(document.getElementById('kaching-bundles-translations')) || [];
        const loc = all.find && all.find((t) => t.locale === cfg.locale);
        if (loc && loc.translations) tr = Object.assign({}, loc.translations.system, (loc.translations.dealBlocks || {})[deal.id]);
        const t = (s) => (s && tr[s] != null ? tr[s] : s || '');
        this.kTr = tr;
        this.kCfg = cfg;
        this.kDeal = deal;
        this.kFlags = cfg.featureFlags || {};
        this.kRate = cfg.currencyRate || 1;
        this.moneyFn = KB.moneyFormatter(cfg.moneyFormat, !!deal.showPricesWithoutDecimals);
        this.kRounding = KB.roundingOf(deal, cfg);
        // "Update native price" (per item): our own price card shows the selected bar's price per unit.
        this.toggleAttribute('data-native-item', !!(deal.updateNativePrice && deal.updateNativePriceType === 'item'));
        this.kFreeWord = tr['system.free'] || this.dataset.giftFreeWord || '';
        this.kTitle = t(deal.blockTitle);
        this.kPreselect = deal.preselectedDealBarId || '';
        this.kT = t;
        // "You're saving {{saved_total}} on this order" line under the bars, for the checked bar.
        const ss = deal.savingsSummaryEnabled && deal.savingsSummary;
        this.kSavings = ss ? {
          title: t(ss.title), hideZero: ss.hideWhenZeroSavings !== false, includeGift: !!ss.includeGiftValue,
          iconType: ss.iconType || null, iconGID: ss.customIconGID || null, s: ss,
        } : null;
        this.kLowStock = deal.lowStockAlertEnabled && deal.lowStockAlert ? {
          message: t(deal.lowStockAlert.message), color: deal.lowStockAlert.textColor, threshold: Number(deal.lowStockAlert.threshold) || 0,
        } : null;
        this.kBars = deal.dealBars.map((b, i) => this.parseBar(b, i));
        // Kaching keys a bar by `bid` unless it can't be told apart from another bar by quantity,
        // then by `bar` (Kaching's dm()): sku / bundle / mix-and-match bars always are.
        this.kBars.forEach((b) => {
          b.barKey = !b.qtyBased || this.kBars.some((o) => o !== b && o.qtyBased && o.qty === b.qty) ? 'bar' : 'bid';
        });
        this.kachingBars = this.kBars.filter((b) => !b.qtyBased || b.qty >= this.qtyMin);
        // Progressive gifts: free shipping / free products unlocked by the selected bar's POSITION
        // in the deal (1-based, all bar types counted), `unlockAtBarOnly` = exactly that bar.
        const pg = deal.progressiveGiftsEnabled && deal.progressiveGifts;
        this.kProgressive = pg && (pg.gifts || []).length ? {
          title: t(pg.title),
          subtitle: t(pg.subtitle),
          hideLocked: !!pg.hideLockedGifts,
          showLockedLabels: !!pg.showLockedGiftLabels,
          showLockedProducts: !!pg.showLockedGiftProducts,
          dealBarLayout: pg.layout === 'deal-bar',
          lockedGID: (!pg.inheritStyle && pg.style && pg.style.lockedMediaImageGID) || null,
          dealBarLockedGID: (pg.dealBarStyle && pg.dealBarStyle.lockedMediaImageGID) || null,
          gifts: pg.gifts.filter((g) => g.giftType === 'shipping' || g.productGID).map((g) => ({
            id: g.id,
            type: g.giftType,
            productGID: g.productGID || null,
            variantGIDs: g.variantGIDs || null,
            at: parseInt(g.unlockAtBar, 10) || 1,
            only: !!g.unlockAtBarOnly,
            label: t(g.label),
            crossed: t(g.labelCrossedOut),
            title: t(g.title),
            locked: t(g.lockedTitle),
            qty: parseInt(g.quantity, 10) || 1,
            onlySub: !!g.applyOnlyForSubscriptions,
            mediaGID: g.mediaImageGID || null,
          })),
        } : null;
        this.kPersoAlert = this.sys('system.personalisation_required_alert') || this.dataset.persoRequired || '';
        const rgba = KB.rgba;
        // Countdown pill above the bars. custom = to endTimestamp (then the pill goes, the deal
        // stays); midnight = to local 00:00; anything else = `minutes`, looping. No storage — Kaching
        // restarts it on every load too.
        const tm = deal.timer;
        this.kTimer = deal.timerEnabled && tm ? {
          type: tm.type || 'fixed',
          minutes: parseFloat(tm.minutes) || 15,
          end: Number(tm.endTimestamp) || 0,
          title: t(tm.title) || '{{timer}}',
          vars: KB.vars({
            'kb-timer-bg': rgba(tm.backgroundColor), 'kb-timer-color': rgba(tm.textColor),
            'kb-timer-align': tm.titleAlignment || '', 'kb-timer-weight': tm.titleStyle ? KB.fontWeight(tm.titleStyle) : '',
            'kb-timer-style': tm.titleStyle ? KB.fontStyle(tm.titleStyle) : '', 'kb-timer-size': tm.titleSize ? tm.titleSize + 'px' : '',
          }),
        } : null;
        // Scratch-off card over consecutive bars flagged hideBehindScratchOff (vertical/plain layout
        // only, as in Kaching). Display-only: no cart effect, not persisted.
        const so = deal.scratchOff;
        this.kScratch = deal.scratchOffEnabled && so && ['vertical', 'plain'].indexOf(deal.blockLayout || 'vertical') !== -1 ? {
          title: t(so.title),
          bg: rgba(so.backgroundColor) || '#ffb200',
          color: rgba(so.titleColor) || '#533c0a',
          size: Number(so.titleSize) || 24,
        } : null;
        const hidden = {};
        deal.dealBars.forEach((b) => { if (b.hideBehindScratchOff) hidden[b.id] = true; });
        this.kBars.forEach((b) => { b.scratch = !!(this.kScratch && hidden[b.barId]); });
        // Deal-level checkbox upsells under the bars (independent of the selected bar).
        const cu = deal.checkboxUpsellsEnabled && deal.checkboxUpsells;
        this.kCheckboxUpsells = cu && (cu.upsells || []).length ? {
          title: t(cu.title),
          upsells: cu.upsells.filter((u) => u.productGID || u.productSource === 'complementary').map((u) => ({
            id: u.id,
            source: u.productSource || 'selected',
            productGID: u.productGID || null,
            variantGIDs: u.variantGIDs || null,
            defaultVariantGID: u.defaultVariantGID || null,
            dtype: u.discountType || 'default',
            dval: parseFloat(u.discountValue) || 0,
            title: t(u.title) || '{{product}}',
            subtitle: t(u.subtitle),
            preselected: !!u.preselected,
            matchQty: !!u.matchQuantityWithDealBar,
            qtySelector: !!u.quantitySelector,
            optionsOnlyWhenSelected: u.showProductOptionsOnlyWhenSelected !== false,
            sub: u.subscriptionEnabled !== false,
            subType: u.subscriptionType || 'deal',
            mediaGID: u.mediaImageGID || null,
            badgeStyle: u.badgeStyle, badgeText: t(u.badgeText), badgeImageGID: u.badgeImageGID,
          })),
        } : null;
        // Sticky bar at the bottom once the card has scrolled away; its button scrolls back to it.
        const sa = deal.stickyAtc;
        const px = (n) => (n != null && n !== '' ? n + 'px' : '');
        const smob = sa && !sa.sameSizeOnMobile && sa.mobile ? sa.mobile : null;
        this.kSticky = deal.stickyAtcEnabled && sa ? {
          title: t(sa.title) || '{{product}}',
          button: t(sa.buttonText),
          // Sizes, padding, fonts and corner radii the merchant set on the sticky bar (colours stay ours).
          vars: KB.vars({
            'kb-st-btn-pad': px(sa.buttonPadding), 'kb-st-btn-radius': px(sa.buttonCornerRadius),
            'kb-st-btn-size': sa.buttonFont ? px(sa.buttonFont.size) : '', 'kb-st-btn-weight': sa.buttonFont ? KB.fontWeight(sa.buttonFont.style) : '',
            'kb-st-btn-style': sa.buttonFont ? KB.fontStyle(sa.buttonFont.style) : '',
            'kb-st-title-size': sa.titleFont ? px(sa.titleFont.size) : '', 'kb-st-title-weight': sa.titleFont ? KB.fontWeight(sa.titleFont.style) : '',
            'kb-st-title-style': sa.titleFont ? KB.fontStyle(sa.titleFont.style) : '',
            'kb-st-photo': px(sa.productPhotoSize), 'kb-st-photo-radius': px(sa.productPhotoCornerRadius),
            'kb-st-photo-display': sa.productPhotoSize === 0 ? 'none' : '',
            'kb-st-btn-pad-m': smob ? px(smob.buttonPadding) : '', 'kb-st-btn-radius-m': smob ? px(smob.buttonCornerRadius) : '',
            'kb-st-btn-size-m': smob ? px(smob.buttonFontSize) : '', 'kb-st-title-size-m': smob ? px(smob.titleFontSize) : '',
            'kb-st-photo-m': smob ? px(smob.productPhotoSize) : '', 'kb-st-photo-radius-m': smob ? px(smob.productPhotoCornerRadius) : '',
            'kb-st-photo-display-m': smob ? (smob.productPhotoSize > 0 ? 'block' : 'none') : '',
          }),
        } : null;
        // "Volume discount with other products": the selected bar's units become slots; slot 0 is
        // this product, the rest can be swapped for another eligible product.
        const cb = deal.collectionBreaksEnabled && deal.collectionBreaks;
        if (cb) {
          const own = cb.visibility === 'deal-products';
          const modal = deal.chooseProductModal || {};
          this.kCollection = {
            button: t(cb.buttonText) || 'Choose',
            showName: cb.showProductName !== false,
            productIds: (own ? (deal.blockVisibility === 'selected-products' ? deal.selectedProductIds : []) : cb.selectedProducts) || [],
            collectionIds: (own ? (deal.blockVisibility === 'selected-collections' ? deal.selectedCollectionIds : []) : cb.selectedCollections) || [],
            excluded: (own ? deal.excludedProductIds : cb.excludedProducts) || [],
            mergeOwn: own,
            modalTitle: t(modal.heading) || '',
            modalButton: t(modal.buttonText) || t(cb.buttonText) || 'Choose',
            autoFill: !!cb.autoFillNotChosenItems,
            requireAll: !!cb.requireItemSelectionEnabled,
            requireAlert: t((cb.requireItemSelectionAlert || {}).text) || this.sys('system.select_variant_alert'),
            // The alert's own colours (Kaching: --kaching-collection-breaks-require-selection-*).
            requireColors: cb.requireItemSelectionAlert ? { background: KB.rgba(cb.requireItemSelectionAlert.backgroundColor), color: KB.rgba(cb.requireItemSelectionAlert.textColor) } : null,
            chosenPriceOnly: !!cb.showChosenProductsPriceOnly,
            mediaGID: cb.mediaImageGID || null,
            photoSize: Number(cb.productPhotoSize) || 40,
            showSearch: !!modal.showSearchField,
            sort: modal.defaultSortOption || 'relevance',
            variantImage: !!modal.showVariantImageByDefault,
            modalPhoto: Number(modal.productPhotoSize) || 100,
            // The merchant's colours / sizes for the product picker (overlay, button, price, text).
            vars: KB.vars({
              'kb-pk-overlay': KB.rgba(modal.overlayColor), 'kb-pk-btn-bg': KB.rgba(modal.buttonColor), 'kb-pk-btn-color': KB.rgba(modal.buttonTextColor),
              'kb-pk-btn-size': modal.buttonSize ? modal.buttonSize + 'px' : '', 'kb-pk-price': KB.rgba(modal.priceColor), 'kb-pk-compare': KB.rgba(modal.compareAtPriceColor),
              'kb-pk-text': KB.rgba(modal.textColor), 'kb-pk-text-size': modal.textSize ? modal.textSize + 'px' : '',
            }),
          };
        } else this.kCollection = null;
        // Deal-wide extras.
        this.kBlockBadges = KB.badgesFor(deal, { type: 'block' }).map((b) => Object.assign({}, b, { text: t(b.text) }));
        return 'ok';
      }

      // One deal bar -> our tier model. `qty` is the quantity a plain add carries (Kaching's Lg()):
      // quantity-break = quantity, bxgy = buy + get, bundle = its first product's quantity, the
      // others 1. Texts stay un-filled here: placeholders depend on the live price.
      parseBar(b, i) {
        const t = this.kT;
        const type = !b.dealBarType || b.dealBarType === 'quantity-break' ? 'qb'
          : b.dealBarType === 'bxgy' ? 'bxgy' : b.dealBarType === 'bundle' ? 'bundle' : b.dealBarType === 'mix-and-match' ? 'mix' : 'sku';
        let qty = 1;
        if (type === 'qb') qty = Number(b.quantity) || 1;
        else if (type === 'bxgy') qty = (Number(b.buyQuantity) || 0) + (Number(b.getQuantity) || 0);
        else if (type === 'bundle') qty = Number((b.bundleProducts && b.bundleProducts[0] && b.bundleProducts[0].quantity) || 1);
        const hl = b.highlights;
        const so = b.showAsSoldOutEnabled && b.showAsSoldOut;
        return {
          kaching: true,
          type: type,
          qtyBased: type === 'qb' || type === 'bxgy',
          barId: b.id,
          idx: i,
          raw: b,
          qty: qty,
          bq: qty, // the bar's own quantity; qty may grow with the bar's quantity stepper
          dtype: b.discountType || 'default',
          dval: parseFloat(b.discountValue) || 0,
          disc: b.discountType === 'percentage' ? parseFloat(b.discountValue) || 0 : 0,
          title: t(b.title),
          subtitle: t(b.subtitle),
          label: t(b.label),
          mediaGID: b.mediaImageGID || null,
          soldOut: so ? { label: t(so.label), vars: KB.vars({
            'kb-so-opacity': so.opacity != null ? String(so.opacity) : '', 'kb-so-bg': KB.rgba(so.backgroundColor),
            'kb-so-color': KB.rgba(so.textColor), 'kb-so-size': so.textSize ? so.textSize + 'px' : '',
          }) } : null,
          quantitySelector: type === 'qb' && !!b.quantitySelector,
          requireVariant: !!b.requireVariantSelectionEnabled,
          // Display-only bullet list under the bar ("highlights").
          highlights: hl && (hl.items || []).length ? {
            items: hl.items.map(t).filter(Boolean),
            selectedOnly: !!hl.showOnlyWhenSelected,
            horizontal: hl.layout === 'horizontal',
            iconType: hl.iconType || 'checkmark',
            iconGID: hl.customIconGID || null,
            vars: KB.vars({
              'kb-hl-icon-color': KB.rgba(hl.iconColor), 'kb-hl-text-color': KB.rgba(hl.textColor),
              'kb-hl-text-size': hl.size ? hl.size + 'px' : '', 'kb-hl-icon-size': hl.size ? (hl.size + 2) + 'px' : '',
            }),
          } : null,
          freeGifts: (b.freeGifts || []).filter((g) => g.productGID || g.giftType === 'shipping').map((g) => ({
            id: g.id,
            type: g.giftType || 'product',
            productGID: g.productGID || null,
            variantGIDs: g.variantGIDs || null,
            qty: parseInt(g.quantity, 10) || 1,
            text: t(g.text),
            mediaGID: g.mediaImageGID || null,
            imageSize: g.imageSize != null ? Number(g.imageSize) : 30,
            showPrice: !!g.showPrice,
            onlySub: !!g.applyOnlyForSubscriptions,
          })),
          giftsSummary: b.freeGiftsSummary && b.freeGiftsSummary.enabled ? { title: t(b.freeGiftsSummary.title), showImages: !!b.freeGiftsSummary.showImages } : null,
          gifts: (b.multipleGiftsSelectors || []).filter((g) => g.products && g.products.length).map((g) => ({
            id: g.id,
            products: g.products,
            max: parseInt(g.maxQuantity, 10) || 1,
            before: t(g.textBeforeSelection),
            after: t(g.textAfterSelection),
          })),
          // Bar upsells: another product added at a Kaching discount when ticked (bar selected).
          upsells: (b.upsells || []).filter((u) => u.productGID || u.productSource === 'complementary').map((u, ui) => ({
            id: u.id,
            index: ui,
            source: u.productSource || 'selected',
            productGID: u.productGID || null,
            variantGIDs: u.variantGIDs || null,
            defaultVariantGID: u.defaultVariantGID || null,
            dtype: u.discountType || 'default',
            dval: parseFloat(u.discountValue) || 0,
            text: t(u.text),
            qty: parseInt(u.quantity, 10) || 1,
            preselected: !!u.preselected,
            mediaGID: u.mediaImageGID || null,
            imageSize: u.imageSize != null ? Number(u.imageSize) : 48,
            selectedOnly: !!u.visibleOnlyWhenDealBarSelected,
            sub: u.subscriptionEnabled !== false,
            subType: u.subscriptionType || 'deal',
          })),
          // Product personalisation: one priced add-on line per bar unit, carrying the text.
          perso: b.productPersonalisation && b.productPersonalisation.variantGID ? {
            id: b.productPersonalisation.id,
            variantId: Number(String(b.productPersonalisation.variantGID).split('/').pop()),
            price: parseFloat(b.productPersonalisation.pricePerItem) || 0,
            compareAt: parseFloat(b.productPersonalisation.compareAtPrice) || 0,
            unitLabel: t(b.productPersonalisation.pricePerItemUnitLabel),
            valueLabel: t(b.productPersonalisation.valueLabel || 'Value') || 'Value',
            title: t(b.productPersonalisation.title),
            subtitle: t(b.productPersonalisation.subtitle),
            required: !!b.productPersonalisation.required,
            mediaGID: b.productPersonalisation.mediaImageGID || null,
          } : null,
        };
      }

      // Fallback: wait for the app to inject <kaching-bundles-block> (gives the product's
      // deal-block-id) and to populate window.__kachingCartBundlesDeals (a trimmed deal config:
      // quantities + percentage only). N+1 doesn't need this.
      pollDeals() {
        let tries = 0;
        const iv = setInterval(() => {
          const block = this.host && this.host.querySelector('kaching-bundles-block');
          const dealBlockId = block && block.getAttribute('deal-block-id');
          const deals = window.__kachingCartBundlesDeals;
          if (dealBlockId && Array.isArray(deals)) {
            const dealBlock = deals.find((d) => d.id === dealBlockId);
            if (dealBlock) {
              clearInterval(iv);
              // Kaching quantity-break tiers only. Its BXGY blocks add free line items via the
              // app's own function — not a simple qty multiply — so we skip them here (our native
              // n+1 below is the supported "get 1 free" path). Drop tiers below quantity_rule.min.
              this.initFallbackDeal(dealBlock);
              this.rebuild();
              return;
            }
          }
          if (++tries > 40) clearInterval(iv); // ~10s; no Kaching deal -> n+1 (if any) still shows
        }, 250);
      }

      // The trimmed deal copy has no texts and no add-ons: a minimal deal so the tiers still render.
      initFallbackDeal(dealBlock) {
        this.kCfg = this.kCfg || {};
        this.kTr = {};
        this.kT = (s) => s || '';
        this.kFlags = {};
        this.kRate = 1;
        this.moneyFn = KB.moneyFormatter(null, false);
        this.kDeal = { dealBars: [], colors: {}, id: dealBlock.id };
        this.kRounding = undefined;
        const bars = (dealBlock.dealBars || [])
          .filter((b) => b.dealBarType === 'quantity-break' && b.quantity >= this.qtyMin)
          .map((b, i) => this.parseBar({
            id: 'poll-' + b.quantity, dealBarType: 'quantity-break', quantity: b.quantity,
            discountType: b.discount && b.discount.value ? 'percentage' : 'default', discountValue: b.discount ? b.discount.value : 0,
          }, i));
        bars.forEach((b) => { b.barKey = 'bid'; });
        this.kBars = this.kachingBars = bars;
      }

      // True if the given variant has at least one applicable kiekinis tier above the min qty.
      hasKiekinis(variantId) {
        return (this.kiekinisByVariant[variantId] || []).some((k) => k.qty > this.qtyMin);
      }

      // A Kaching product by GID (page product included), in Kaching's shape; the Storefront
      // fetch fills the others.
      kp(gid) { return (this.kProd && this.kProd[gid]) || null; }

      // Can this bar be drawn? Bars built on other products need them loaded and in stock.
      tierReady(b) {
        if (b.qtyBased) return true;
        if (!this.giftProducts) return false;
        if (b.type === 'sku') {
          const p = this.kp(b.raw.productGID);
          return !!(p && p.availableForSale && KB.allowedVariants(p, b.raw.variantGIDs).length);
        }
        const slots = b.raw.bundleProducts.filter((bp) => bp.productGID);
        if (!slots.length) return false;
        if (b.type === 'bundle') {
          return slots.every((bp) => {
            const p = bp.productGID === 'default' ? this.pageProd : this.kp(bp.productGID);
            return !!(p && p.availableForSale && this.bundleVariants(bp, p).length);
          });
        }
        return slots.every((bp) => this.mixOptions(bp).length > 0);
      }

      // Merge the Kaching bars (every type), the kiekinis tiers, and the n+1 tier into one
      // sorted tier list, add the base "single" tier, render, and reveal. Quantity tiers are ABSOLUTE
      // quantities (a "6" tier = add 6).
      rebuild() {
        // The other products come first: bars built on them are only drawn once they are in. (A deal
        // with nothing to fetch resolves right here.)
        if (this.kDeal) this.loadGiftProducts();
        // Kaching bars keep the merchant's order from the deal (IO-5815); our own tiers (kiekinis,
        // n+1, base) follow, ascending. With no Kaching deal that is just the ascending list.
        const kaching = this.kachingBars.filter((b) => this.tierReady(b)).map((b) => Object.assign({ kaching: true }, b));
        const tiers = [];
        const taken = (q) => kaching.some((t) => t.qtyBased && t.qty === q) || tiers.some((t) => t.qty === q);
        // Kiekinis quantity-break tiers for the CURRENT variant (custom.discount_data). Same shape
        // as a Kaching bar ({qty, disc}); dedupe by qty so it doesn't double a Kaching tier.
        (this.kiekinisByVariant[this.variantId] || []).forEach((k) => {
          if (k.qty >= this.qtyMin && !taken(k.qty)) {
            tiers.push({ qty: k.qty, disc: k.disc, msg: k.msg, qtyBased: true });
          }
        });
        // N+1 tier for the CURRENT variant: total-to-add (buy nplus1Qty-free, get free). Only add
        // if it isn't already an existing tier qty (a variant rarely has both at the same qty).
        if (this.nplus1Qty > this.qtyMin && !taken(this.nplus1Qty)) {
          tiers.push({ qty: this.nplus1Qty, free: this.nplus1Free, nplus1: true, qtyBased: true });
        }
        if (!tiers.length && !kaching.length) {
          // Nothing to show for this variant (e.g. switched to a variant with no n+1 and no
          // Kaching deal) -> clear and hide; the observer brings the manual stepper back.
          this.tiers = [];
          if (this.optionsEl) this.optionsEl.innerHTML = '';
          this.setAttribute('hidden', '');
          return;
        }
        // Base "no discount" tier is the minimum buyable quantity, not a hardcoded 1
        // (case-pack products have quantity_rule.min > 1).
        if (!taken(this.qtyMin)) tiers.push({ qty: this.qtyMin, disc: 0, qtyBased: true });
        tiers.sort((a, b) => a.qty - b.qty);
        this.tiers = kaching.concat(tiers);
        const heading = this.querySelector('.product-buy__card-heading');
        if (heading) {
          if (heading.dataset.default == null) heading.dataset.default = heading.textContent;
          heading.textContent = (kaching.length && this.kTitle) || heading.dataset.default;
        }
        this.render();
        this.removeAttribute('hidden'); // top-level observer hides the manual stepper on this flip
      }

      // ---------------------------------------------------------------------------------------
      // Pricing of one tier.
      // ---------------------------------------------------------------------------------------
      // The quantity a quantity-break bar adds: its own, or what the shopper set on its stepper.
      tierQty(t) {
        if (t.kaching && t.quantitySelector) return Math.max(t.bq, (this.kQty && this.kQty[t.barId]) || t.bq);
        return t.qty;
      }

      // A product variant picked from the Storefront data as a Kaching variant (collection-break
      // slots keep the lighter pickVariant() shape).
      slotVariant(s) {
        return { id: s.id, price: s.price, compareAtPrice: s.compareAt || null, options: [], sellingPlans: [], image: s.img, title: s.title, inventoryQuantity: null };
      }

      // [{variant, quantity}] of a quantity-break / bxgy bar's units, with collection-break slots
      // swapped for other products.
      tierVQ(t, qty) {
        const pv = this.pageVariant();
        const units = Array.from({ length: qty }, () => pv);
        const c = this.kCollection;
        if (c && t.kaching && qty > 1 && this.kSlots) {
          for (let k = 1; k < qty; k++) if (this.kSlots[k]) units[k] = this.slotVariant(this.kSlots[k]);
          // Only the chosen products are priced when merchant says so (slot 0 is always chosen).
          if (c.chosenPriceOnly) {
            const chosen = units.filter((u, k) => k === 0 || this.kSlots[k]);
            units.length = 0;
            chosen.forEach((u) => units.push(u));
          }
        }
        const vq = [];
        units.forEach((v) => {
          const hit = vq.find((x) => x.variant.id === v.id);
          if (hit) hit.quantity += 1; else vq.push({ variant: v, quantity: 1 });
        });
        return vq;
      }

      kState(barId) {
        this.kStates = this.kStates || {};
        return (this.kStates[barId] = this.kStates[barId] || {});
      }

      // Variants of a bundle product the shopper may buy (variantGIDs restricts; no availability
      // filter on the page product, as in the widget).
      bundleVariants(bp, product) {
        const ids = bp.variantGIDs && bp.variantGIDs.length ? bp.variantGIDs.map(KB.numId) : null;
        const list = ids ? product.variants.filter((v) => ids.indexOf(v.id) !== -1) : product.variants;
        return list.filter((v) => v.availableForSale || (bp.productGID === 'default' && !(this.kDeal && this.kDeal.hideUnavailableOptions)));
      }

      // bundle: {bundleProduct.id: {product, variant, quantity}} for the bar's current picks.
      bundleSel(t) {
        const st = this.kState(t.barId);
        st.bv = st.bv || {};
        const sel = {};
        t.raw.bundleProducts.filter((bp) => bp.productGID).forEach((bp) => {
          const isOwn = bp.productGID === 'default';
          const product = isOwn ? this.pageProd : this.kp(bp.productGID);
          if (!product) return;
          const list = this.bundleVariants(bp, product);
          // The deal's per-bar default variant (defaultVariantsV2) wins over the page's variant for this product.
          const v2 = isOwn ? ((this.kDeal.defaultVariantsV2 || []).find((x) => KB.numId(x.productGID) === product.id && x.dealBarId === t.barId) || {}).variantGIDs : null;
          const want = st.bv[bp.id] || (v2 && v2[0] && KB.numId(v2[0])) || (bp.defaultVariantGID && KB.numId(bp.defaultVariantGID)) || (isOwn ? Number(this.variantId) : null);
          const variant = list.find((v) => v.id === want) || list.find((v) => v.availableForSale) || list[0];
          if (variant) sel[bp.id] = { product: product, variant: variant, quantity: Number(bp.quantity) || 1 };
        });
        return sel;
      }

      // mix-and-match: the products one slot can hold: [{product, variants}] (its own product, then selectedProducts).
      mixOptions(bp) {
        const out = [];
        const add = (product, variantGIDs) => {
          if (!product || !product.availableForSale || out.some((o) => o.product.id === product.id)) return;
          const variants = KB.allowedVariants(product, variantGIDs);
          if (variants.length) out.push({ product: product, variants: variants });
        };
        add(bp.productGID === 'default' ? this.pageProd : this.kp(bp.productGID), bp.variantGIDs);
        (bp.selectedProducts || []).forEach((sp) => add(this.kp(sp.id), sp.variantGIDs));
        return out;
      }

      mixSel(t) {
        const st = this.kState(t.barId);
        st.mx = st.mx || {};
        const sel = {};
        t.raw.bundleProducts.filter((bp) => bp.productGID).forEach((bp) => {
          const opts = this.mixOptions(bp);
          const pick = st.mx[bp.id];
          let o = pick && opts.find((x) => x.product.id === pick.pid);
          o = o || opts[0];
          if (!o) return;
          const variant = (pick && o.variants.find((v) => v.id === pick.vid)) || o.variants[0];
          sel[bp.id] = { product: o.product, variant: variant, quantity: 1 };
        });
        return sel;
      }

      skuSel(t) {
        const product = this.kp(t.raw.productGID);
        if (!product) return null;
        const list = KB.allowedVariants(product, t.raw.variantGIDs);
        const st = this.kState(t.barId);
        const variant = list.find((v) => v.id === st.sv) || list[0];
        return variant ? { product: product, variant: variant, quantity: 1 } : null;
      }

      // The priced view of a tier: `total` = what Kaching charges, `regular` = the plain price of
      // the same units (the strike-through, IO-5661), `savedPct`, and the placeholder context.
      calcTier(t) {
        const base = this.prices[this.variantId] || 0;
        const c = { qty: t.qty, phQty: t.qty, vq: null, sel: null, regular: 0, total: 0 };
        // The plan the bar itself buys under (sellingPlanEnabled / sellingPlanGid), if any.
        const plan = this.tierPlan(t);
        c.plan = plan;
        if (!t.kaching) {
          if (t.nplus1) {
            // Buy (qty-free), pay for qty-free; `free` units free (1 for 2+1, 2 for 2+2). The
            // native automatic BXGY discount makes them free at checkout — here we only mirror
            // that in the displayed total.
            c.total = base * (t.qty - t.free);
          } else {
            c.total = Math.round(base * (1 - (t.disc || 0) / 100)) * t.qty;
          }
          c.regular = base * t.qty;
        } else if (t.qtyBased) {
          c.qty = c.phQty = this.tierQty(t);
          c.vq = this.tierVQ(t, c.qty);
          c.regular = c.vq.reduce((a, x) => a + x.variant.price * x.quantity, 0);
          c.total = t.type === 'qb'
            ? KB.breakTotal({ discountType: t.dtype, discountValue: t.raw.discountValue, quantity: t.bq }, c.vq, this.kRate, this.kRounding, plan,
              this.kFlags.percentage_cents_rounding_workaround)
            : KB.bxgyTotal(t.raw, c.vq, this.kRate, this.kRounding, plan);
        } else if (t.type === 'bundle') {
          c.sel = this.bundleSel(t);
          const items = Object.keys(c.sel).map((k) => c.sel[k]);
          c.vq = items.map((x) => ({ variant: x.variant, quantity: x.quantity }));
          c.regular = items.reduce((a, x) => a + x.variant.price * x.quantity, 0);
          c.total = KB.bundleTotal(t.raw, c.sel, this.kRate, this.kRounding, undefined);
          c.qty = items.reduce((a, x) => a + x.quantity, 0) || 1;
          c.phQty = 1;
        } else if (t.type === 'mix') {
          c.sel = this.mixSel(t);
          const items = Object.keys(c.sel).map((k) => c.sel[k]);
          c.vq = items.map((x) => ({ variant: x.variant, quantity: 1 }));
          c.regular = items.reduce((a, x) => a + x.variant.price, 0);
          c.total = KB.mixTotal(t.raw, c.sel, this.kRate, this.kRounding, undefined);
          if (t.raw.showProductPrices) c.shares = KB.mixShares(t.raw, c.sel, this.kRate, this.kRounding, undefined);
          c.qty = c.phQty = 1;
        } else { // sku
          const s = this.skuSel(t);
          c.sel = s;
          c.vq = s ? [{ variant: s.variant, quantity: 1 }] : [];
          c.regular = s ? s.variant.price : 0;
          c.total = s ? KB.planPrice(s.variant, plan) : 0;
          c.qty = c.phQty = 1;
        }
        c.total = Math.max(0, Math.round(c.total));
        c.per = Math.ceil(c.total / (c.qty || 1));
        c.perRegular = Math.ceil(c.regular / (c.qty || 1));
        c.saved = Math.max(0, c.regular - c.total);
        c.savedPct = c.regular > 0 && c.total < c.regular ? Math.round((c.regular - c.total) / c.regular * 100) : 0;
        const product = t.type === 'sku' && c.sel ? c.sel.product : this.pageProd;
        c.ctx = {
          money: this.moneyFn ? this.moneyFn : KB.moneyFormatter(null, false), product: product, selectedVariants: c.vq,
          totalFull: c.regular, totalCompareAt: c.regular, totalDiscounted: c.total, quantity: c.phQty, dealBar: t.raw,
          legacySavedPercentage: !!(this.kFlags && this.kFlags.legacy_saved_percentage), unitQuantity: null, sellingPlan: plan,
          products: this.slotProductMap(t),
        };
        return c;
      }

      // What the checked Kaching bar costs when bought under the plan chosen in the card's frequency select:
      // Kaching discounts the plan's own price (Cd / Id / Vo / ws with the plan applied), so the subscription
      // card shows that, not the plain plan price x quantity. null = no Kaching bar (or a bar this does not apply to).
      subTotalCents() {
        const r = this.optionsEl && this.optionsEl.querySelector('.product-buy__bundle-radio:checked');
        const t = r && r.dataset.barId && this.tiers.find((x) => x.barId === r.dataset.barId);
        if (!t || !t.kaching || t.type === 'sku') return null;
        const scope = this.closest('.product-main');
        const freq = scope && scope.querySelector('.product-buy__freq');
        const plan = freq && this.pageProd.sellingPlans.find((p) => String(p.id) === freq.value);
        // A plan the current variant is not sold under: no plan price to discount, keep plan x quantity.
        if (!plan || !this.pageVariant().sellingPlans.some((s) => s.id === plan.id)) return null;
        // A swapped collection-break unit gets the plan the way its cart line does (planFor on its own product).
        const underPlan = (v) => {
          if (v.sellingPlans.some((s) => s.id === plan.id)) return v;
          const product = this.productOfVariant(v.id);
          const real = product && product.variants.find((x) => x.id === v.id);
          const mapped = real && KB.planFor(product, real, plan);
          return mapped ? Object.assign({}, v, { sellingPlans: [{ id: plan.id, price: KB.planPrice(real, mapped) }] }) : v;
        };
        let total;
        if (t.qtyBased) {
          const vq = this.tierVQ(t, this.tierQty(t)).map((x) => ({ variant: underPlan(x.variant), quantity: x.quantity }));
          total = t.type === 'qb'
            ? KB.breakTotal({ discountType: t.dtype, discountValue: t.raw.discountValue, quantity: t.bq }, vq, this.kRate, this.kRounding, plan, this.kFlags.percentage_cents_rounding_workaround)
            : KB.bxgyTotal(t.raw, vq, this.kRate, this.kRounding, plan);
        } else if (t.type === 'bundle') total = KB.bundleTotal(t.raw, this.bundleSel(t), this.kRate, this.kRounding, plan);
        else total = KB.mixTotal(t.raw, this.mixSel(t), this.kRate, this.kRounding, plan);
        return Math.max(0, Math.round(total));
      }

      // The plan a quantity-break / bxgy / sku bar buys under: its fixed plan (sellingPlanGid) or the one
      // the shopper picks in the bar, else the first the variant offers (Kaching's Do()). Bundle and
      // mix-and-match bars only ever follow the page's own plan. undefined = the bar sets no plan.
      tierPlan(t) {
        if (!t || !t.kaching || !t.raw.sellingPlanEnabled || !this.pageProd) return undefined;
        if (this.kDeal.subscriptionsEnabled && !this.currentPlan()) return undefined;
        // No subscription card to follow: the bar is bought one-time (card and cart must never disagree).
        const scope = this.closest('.product-main');
        if (!scope || !scope.querySelector('.product-buy__type-radio[value="subscribe"]')) return undefined;
        const plans = this.barPlans(t);
        if (!plans.length) return undefined;
        let a;
        if (t.raw.sellingPlanGid) a = plans.find((p) => p.id === KB.numId(t.raw.sellingPlanGid));
        else {
          // The plan the shopper already chose on our card (when this product offers it), else their pick in the bar.
          const cur = this.currentPlan();
          if (cur) this.kLastPlan = cur.id;
          a = (cur && plans.find((p) => p.id === cur.id)) || plans.find((p) => p.id === this.kLastPlan) || plans.find((p) => p.id === this.kState(t.barId).plan) || plans[0];
        }
        a = a || plans[0];
        // The card could not take this plan: price and add the bar one-time instead.
        if (!this.currentPlan() && this.kSyncFailed === t.barId + ':' + a.id + ':' + this.variantId) return undefined;
        return a;
      }

      // Our purchase card must never show one-time while the cart line carries a plan: a checked bar with its
      // own plan switches the card to subscription with that plan, through the card's own radio / frequency
      // select / syncType(). Re-entrant calls (the card's change event coming back) are ignored.
      syncCardToBar(t, planId) {
        if (this.kSyncing) return;
        const scope = this.closest('.product-main');
        const plan = planId ? this.barPlans(t).find((p) => p.id === planId) : this.tierPlan(t);
        const sub = scope && scope.querySelector('.product-buy__type-radio[value="subscribe"]');
        if (!plan || !sub) return;
        const cur = this.currentPlan();
        if (cur && cur.id === plan.id) return;
        // One attempt per bar / plan / variant: a card that cannot take the plan must not be retried in a loop.
        const key = t.barId + ':' + plan.id + ':' + this.variantId;
        if (this.kSyncFailed === key) return;
        const freq = scope.querySelector('.product-buy__freq');
        const hasOption = freq && Array.prototype.some.call(freq.options, (o) => o.value === String(plan.id));
        this.kSyncing = true;
        try {
          sub.checked = true;
          if (hasOption) { freq.value = String(plan.id); freq.dispatchEvent(new Event('change', { bubbles: true })); }
          else {
            const purchase = scope.querySelector('product-purchase');
            if (purchase && purchase.syncType) purchase.syncType(); else sub.dispatchEvent(new Event('change', { bubbles: true }));
          }
        } finally { this.kSyncing = false; }
        const now = this.currentPlan();
        this.kSyncFailed = now && now.id === plan.id ? null : key;
      }

      barPlans(t) {
        let product, variant;
        if (t.type === 'sku') {
          const s = this.skuSel(t);
          if (!s) return [];
          product = s.product; variant = s.variant;
        } else if (t.qtyBased) { product = this.pageProd; variant = this.pageVariant(); } else return [];
        return variant ? KB.plansOf(product, [variant]) : [];
      }

      // The plan add-ons follow: the checked bar's own, else what the purchase-type card chose.
      dealPlan() {
        const r = this.optionsEl && this.optionsEl.querySelector('.product-buy__bundle-radio:checked');
        const t = r && r.dataset.barId && this.tiers.find((x) => x.barId === r.dataset.barId);
        return (t && this.tierPlan(t)) || this.currentPlan();
      }

      // {{product_N}}: the product in collection-break slot N (1-based); slot 1 is this product.
      slotProductMap(t) {
        const map = new Map();
        if (!(this.kCollection && t.kaching && t.qtyBased && this.kSlots)) return map;
        map.set(1, this.pageProd);
        this.kSlots.forEach((s, k) => { if (s && k > 0) map.set(k + 1, { title: s.productTitle || s.title }); });
        return map;
      }

      // ---------------------------------------------------------------------------------------
      // Rendering.
      // ---------------------------------------------------------------------------------------
      render() {
        // Crossed-out "regular" reference uses the PLAIN pre-loyalty price, so it matches the
        // OTP card's "Įprasta kaina" sub-line (updatePurchasePrices() below: data-unit-regular
        // × qty) for the same tier. IO-5661: it used to be loyaltyBase × qty so it read as a
        // multiple of the base tile's loyalty price, which made the same "regular" total show
        // two different values on one buy box (e.g. 6 × €9,50 = €57,00 vs 6 × €9,99 = €59,94).
        // total below stays on the plain price — IO-5250: it is what's actually added to
        // cart and must keep matching the checkout Function's own (best-of, non-stacked) total.
        const calcs = this.tiers.map((t) => this.calcTier(t));
        // A re-render (slot choice, variant switch) keeps the shopper's own pick when it still exists.
        const prev = this.kTouched && this.optionsEl.querySelector('.product-buy__bundle-radio:checked');
        const open = (t) => !(t.soldOut);
        let checkedIdx = prev
          ? this.tiers.findIndex((t) => open(t) && (prev.dataset.barId ? t.barId === prev.dataset.barId : !t.barId && String(t.qty) === prev.value))
          : -1;
        if (checkedIdx < 0) checkedIdx = this.tiers.findIndex((t) => open(t) && t.barId && t.barId === this.kPreselect);
        // No (usable) merchant preselect -> the base tier, as before; else the first tier.
        if (checkedIdx < 0) checkedIdx = this.tiers.findIndex((t) => open(t) && t.qtyBased && t.qty === this.qtyMin && !t.nplus1 && (!t.kaching || t.type === 'qb'));
        // A sold-out bar is never preselected.
        if (checkedIdx < 0) checkedIdx = this.tiers.findIndex(open);
        if (checkedIdx < 0) checkedIdx = 0;
        // Scratch-off: a preselected bar still hidden behind the card is not applied until the card
        // is revealed (Kaching does the same) — the base tier stands in meanwhile.
        if (this.tiers[checkedIdx] && this.tiers[checkedIdx].scratch && !this.kRevealed) {
          const b = this.tiers.findIndex((t) => open(t) && !t.scratch && t.qtyBased && t.qty === this.qtyMin && !t.nplus1);
          checkedIdx = b >= 0 ? b : this.tiers.findIndex((t) => open(t) && !t.scratch);
          if (checkedIdx < 0) checkedIdx = 0;
        }
        this.kChecked = this.tiers[checkedIdx] ? this.tiers[checkedIdx].barId || '' : '';
        const parts = this.tiers.map((t, i) => this.tierHTML(t, i, i === checkedIdx, calcs[i]));
        // Consecutive scratch-off bars share one card; once revealed they render plainly.
        let html = '';
        let openCard = false;
        this.tiers.forEach((t, i) => {
          const hide = t.scratch && !this.kRevealed;
          if (hide && !openCard) { html += '<div class="product-buy__scratch" data-scratch>'; openCard = true; }
          if (!hide && openCard) { html += '</div>'; openCard = false; }
          html += parts[i];
        });
        if (openCard) html += '</div>';
        // The base tier's data-add-qty mirrors the qty stepper (initQty); a re-render (slot choice,
        // scratch reveal) must not reset it to qtyMin while the stepper still shows the shopper's qty.
        const prevBase = this.optionsEl.querySelector('.product-buy__bundle-radio[data-base]');
        const baseQty = prevBase && prevBase.dataset.addQty;
        this.optionsEl.innerHTML = html;
        this.blockBadges();
        const newBase = this.optionsEl.querySelector('.product-buy__bundle-radio[data-base]');
        if (newBase && baseQty) newBase.dataset.addQty = baseQty;
        this.optionsEl.querySelectorAll('.product-buy__bundle-radio').forEach((r) =>
          r.addEventListener('change', () => { this.kTouched = true; this.setActive(); }));
        this.initScratch();
        this.renderTimer();
        this.setActive(true);
        this.renderGifts();
        KB.layoutBorderBadges(this.optionsEl);
        // Let the scope reconcile the n+1 <-> subscription mutual exclusion against the freshly
        // rendered tiers (a re-render doesn't flip [hidden], so the MutationObserver won't fire).
        this.dispatchEvent(new CustomEvent('bundles:rendered', { bubbles: true }));
      }

      // Badges attached to the whole block (attachedTo.type "block"): a frame around the bars.
      blockBadges() {
        const wrap = this.optionsEl;
        wrap.classList.remove('has-badge-border-top', 'has-badge-border-bottom', 'has-badge-border-left', 'has-badge-border-right', 'has-badge-border-all');
        wrap.removeAttribute('style');
        const old = wrap.querySelector(':scope > [data-block-badges]');
        if (old) old.remove();
        const badges = (this.kBlockBadges || []).map((b) => {
          const c = this.tiers.find((t) => t.barId === this.kChecked);
          return Object.assign({}, b, { text: KB.fill(b.text, c ? this.calcTier(c).ctx : { money: this.moneyFn, quantity: 1, totalFull: 0, totalDiscounted: 0 }) });
        });
        if (!badges.length || !this.tiers.some((t) => t.kaching)) return;
        const attrs = KB.badgeBoxAttrs(badges);
        attrs.cls.split(' ').filter(Boolean).forEach((c) => wrap.classList.add(c));
        if (attrs.style) wrap.setAttribute('style', attrs.style);
        const holder = document.createElement('div');
        holder.setAttribute('data-block-badges', '');
        holder.innerHTML = badges.map((b) => KB.badgeHTML(b, this.kDeal, (g) => this.media(g))).join('');
        wrap.insertBefore(holder, wrap.firstChild);
      }

      // The name a bar without a title falls back to.
      tierName(t, c) {
        if (t.type === 'sku' && c.sel) return c.sel.product.title;
        if (t.type === 'bundle' && c.sel) return Object.keys(c.sel).map((k) => c.sel[k].product.title).join(' + ');
        const word = (q) => (q === 1 ? this.dataset.unitWord : this.dataset.unitsWord);
        return c.qty + ' ' + word(c.qty);
      }

      stockOf(variant) {
        return variant && variant.inventoryQuantity != null ? variant.inventoryQuantity : null;
      }

      // One bar: wrapper (badges, sold-out pill) > label (radio, price) + the box under it.
      tierHTML(t, i, checked, c) {
        const esc = KB.esc;
        const money = (v, o) => this.money(v, o);
        const fill = (s) => (t.kaching ? KB.fill(s, c.ctx) : s);
        const isBase = (!t.kaching || t.type === 'qb') && t.qtyBased && t.qty === this.qtyMin && !t.nplus1;
        let unitCents = c.per;
        let total = c.total;
        // Base "single" tier displays the loyalty price (consistent with the OTP "Lojalumo
        // kaina" card), not the plain variant price. No strikethrough — the loyalty price is
        // shown alone per design; the regular price is disclosed on the OTP card's sub-line.
        if (isBase) {
          const lp = this.loyaltyPrices[this.variantId];
          if (lp != null) { unitCents = lp; total = lp * c.qty; }
        }
        // The base tier stays un-struck (per design); amount/specific bars get a strikethrough too.
        const discounted = !isBase && (t.nplus1 || (c.regular > c.total));
        const view = Object.assign({}, c, { total: total, per: isBase ? unitCents : c.per });
        let badge = '';
        if (t.nplus1) {
          badge = '<span class="product-buy__bundle-badge">' + (t.qty - t.free) + '+' + t.free + (this.freeWord ? ' ' + this.freeWord : '') + '</span>';
        } else if (!isBase) {
          // A Kaching bar's own label (e.g. "Sutaupykite {{saved_percentage}}") wins over ours.
          if (t.label) badge = '<span class="product-buy__bundle-badge">' + esc(fill(t.label)) + '</span>';
          else if (t.qtyBased && c.savedPct > 0) badge = '<span class="product-buy__bundle-badge">' +
            (this.savePrefix ? this.savePrefix + ' ' : '-') + c.savedPct + '%</span>';
        }
        // Deal text this tier feeds into the OTP "Lojalumo kaina" card header when selected
        // (base tier carries none -> the header restores its loyalty default). Kiekinis prefers
        // its merchant discount_message; Kaching/n+1 mirror their badge text.
        let header = '';
        if (t.nplus1) header = (t.qty - t.free) + '+' + t.free + (this.freeWord ? ' ' + this.freeWord : '');
        else if (t.msg) header = t.msg;
        else if (t.qtyBased && t.label && !isBase) header = fill(t.label);
        else if (t.qtyBased && c.savedPct > 0 && !isBase) header = (this.savePrefix ? this.savePrefix + ' ' : '-') + c.savedPct + '%';
        // Kaching bar title (e.g. "6 vnt") replaces our "6 vienetai"; its subtitle stacks under it.
        const label = t.title ? esc(fill(t.title)) : esc(this.tierName(t, c));
        const sub = t.subtitle ? '<span class="product-buy__bundle-sub">' + esc(fill(t.subtitle)) + '</span>' : '';
        // Low stock: this bar's variant is running out (deal-level setting).
        let stock = '';
        const ls = this.kLowStock;
        if (t.kaching && ls && (t.qtyBased || t.type === 'sku')) {
          const v = t.type === 'sku' ? (c.sel && c.sel.variant) : this.pageVariant();
          const inv = this.stockOf(v);
          if (inv && inv > 0 && inv < ls.threshold) {
            const msg = String(ls.message || '').replace(/\{\{\s*(\w+)\s*\}\}/g, (m, k) => (k === 'stock' ? String(inv) : ''));
            const style = ls.color ? ' style="--kb-low-stock-color:' + KB.rgba(ls.color) + '"' : '';
            if (msg) stock = '<span class="product-buy__bundle-lowstock"' + style + '>' + esc(msg) + '</span>';
          }
        }
        const highlights = t.kaching && t.highlights ? this.highlightsHTML(t, fill) : '';
        const badges = t.kaching ? this.barBadges(t, c) : [];
        const bbox = KB.badgeBoxAttrs(badges);
        const img = t.kaching && t.mediaGID && this.media(t.mediaGID)
          ? '<img class="product-buy__bundle-img" src="' + esc(this.media(t.mediaGID)) + '" alt="" width="40" height="40" loading="lazy">' : '';
        const kind = t.kaching ? t.type : (t.nplus1 ? 'nplus1' : 'own');
        const needsBox = t.kaching && (t.gifts.length || t.upsells.length || t.perso || t.freeGifts.length || t.quantitySelector ||
          !t.qtyBased || t.raw.sellingPlanEnabled || (this.kCollection && t.qtyBased && t.qty > 1) || (this.kProgressive && this.kProgressive.dealBarLayout));
        const box = needsBox
          ? '<div class="product-buy__bundle-gifts" data-gifts-for="' + esc(t.barId) + '"' + (checked ? '' : ' data-collapsed') + ' hidden></div>' : '';
        const prices = this.pricesHTML(t, view, discounted);
        const so = t.kaching && t.soldOut;
        return '<div class="product-buy__bar' + (so ? ' is-sold-out' : '') + (bbox.cls ? ' ' + bbox.cls : '') + '"' +
          ' style="' + esc((so ? so.vars + ';' : '') + bbox.style) + '"' + (t.barId ? ' data-bar-wrap="' + esc(t.barId) + '"' : '') + '>' +
          badges.map((b) => KB.badgeHTML(b, this.kDeal, (g) => this.media(g))).join('') +
          '<label class="product-buy__bundle-opt' + (checked ? ' is-active' : '') + (img ? ' has-image' : '') + '">' +
            '<input class="product-buy__bundle-radio" type="radio" name="bundles" value="' + t.qty + '"' +
              ' data-add-qty="' + (t.kaching && !t.qtyBased ? t.qty : c.qty) + '"' + ' data-unit-cents="' + unitCents + '" data-total-cents="' + total + '"' +
              ' data-kind="' + kind + '"' + (t.kaching && !t.qtyBased ? ' data-fixed-qty' : '') +
              (t.kaching && c.plan ? ' data-plan-priced' : '') + (isBase ? ' data-base' : '') + (t.nplus1 ? ' data-nplus1' : '') + (t.barId ? ' data-bar-id="' + esc(t.barId) + '"' : '') +
              ' data-saved-cents="' + (discounted ? Math.max(0, c.regular - total) : 0) + '"' + (header ? ' data-header="' + esc(header) + '"' : '') +
              (checked ? ' checked' : '') + (so ? ' disabled' : '') + '>' +
            '<span class="product-buy__radio-dot" aria-hidden="true"></span>' + img +
            // Figma review 1443:497 / 1450:497 #5 — the deal badge ("2+1" / "Sutaupykite N%") is
            // grouped with the qty label (it stacks UNDER it, see the CSS) and the price group is
            // always alone on the right. It used to trail the prices on the same line.
            '<span class="product-buy__bundle-main">' +
              '<span class="product-buy__bundle-label">' + label + '</span>' +
              sub + badge + stock + highlights +
            '</span>' +
            prices +
          '</label>' + box +
          (so && so.label ? '<div class="product-buy__bar-soldout">' + esc(fill(so.label)) + '</div>' : '') +
        '</div>';
      }

      // Badges of one bar: new-style badges[] (or the legacy badgeStyle/badgeText/badgeImageGID
      // when the deal has none), texts translated and placeholder-filled.
      barBadges(t, c) {
        const raw = t.raw;
        const list = KB.badgesFor(this.kDeal, { type: 'dealBar', dealBarId: t.barId },
          { id: t.barId, badgeStyle: raw.badgeStyle, badgeText: this.kT(raw.badgeText), badgeImageGID: raw.badgeImageGID });
        return list.map((b) => Object.assign({}, b, { text: KB.fill(this.kT(b.text), c.ctx) }));
      }

      highlightsHTML(t, fill) {
        const h = t.highlights;
        const items = h.items.map((s) => fill(s)).filter((s) => s !== '');
        if (!items.length) return '';
        const url = h.iconType === 'custom' ? this.media(h.iconGID) : '';
        const icon = url ? '<img src="' + KB.esc(url) + '" alt="">' : (KB.ICONS[h.iconType] || KB.ICONS.checkmark || '');
        return '<ul class="product-buy__bundle-highlights' + (h.selectedOnly ? ' is-selected-only' : '') + (h.horizontal ? ' is-horizontal' : '') + '" style="' + KB.esc(h.vars) + '">' +
          items.map((s) => '<li><span class="product-buy__bundle-hl-icon">' + icon + '</span><span>' + KB.esc(s) + '</span></li>').join('') + '</ul>';
      }

      // Price group of a bar: total (or per item), strike-through, unit label, and both rows when
      // the deal shows both.
      pricesHTML(t, c, discounted) {
        const esc = KB.esc;
        const d = this.kDeal || {};
        const m = (v) => this.money(v);
        const perItem = !!d.showPricesPerItem && t.kaching;
        const unit = perItem && d.unitLabel ? '<span class="product-buy__bundle-unit">' + esc(this.kT(d.unitLabel)) + '</span>' : '';
        const strike = (v) => (discounted ? '<s class="product-buy__bundle-regular">' + m(v) + '</s>' : '');
        if (t.kaching && d.showBothPrices && c.qty > 1) {
          return '<span class="product-buy__bundle-prices is-both">' +
            '<span class="product-buy__bundle-row">' + strike(c.perRegular) + '<span class="product-buy__bundle-price">' + m(c.per) + '</span>' + unit + '</span>' +
            '<span class="product-buy__bundle-row is-secondary">' + strike(c.regular) + '<span class="product-buy__bundle-total">' + m(c.total) + '</span></span></span>';
        }
        const main = perItem ? c.per : c.total;
        return '<span class="product-buy__bundle-prices">' + strike(perItem ? c.perRegular : c.regular) +
          '<span class="product-buy__bundle-price">' + m(main) + '</span>' + unit + '</span>';
      }

      setActive(rendered) {
        let barId = '';
        this.optionsEl.querySelectorAll('.product-buy__bundle-opt').forEach((opt) => {
          const radio = opt.querySelector('.product-buy__bundle-radio');
          opt.classList.toggle('is-active', radio && radio.checked);
          if (radio && radio.checked) barId = radio.dataset.barId || '';
        });
        this.kChecked = barId;
        // The box under a bar (gifts, upsells, stepper...) reads differently for the checked bar.
        this.optionsEl.querySelectorAll('[data-gifts-for]').forEach((box) => {
          box.toggleAttribute('data-collapsed', box.dataset.giftsFor !== barId);
        });
        if (!rendered && this.kBoxFor !== barId) this.renderGifts();
        this.kBoxFor = barId;
        this.renderCheckboxUpsells();
        this.renderSavings();
        this.renderProgressive();
        this.renderSticky();
        // A bar that buys under its own selling plan puts our purchase card on that plan.
        const ct = barId && this.tiers.find((t) => t.barId === barId);
        if (ct && ct.kaching && ct.raw.sellingPlanEnabled) this.syncCardToBar(ct);
      }

      // Under-the-bars blocks live in one container in a fixed order (Kaching's): checkbox upsells,
      // progressive gifts, savings summary.
      afterSlot(cls, tag) {
        let wrap = this.querySelector('.product-buy__bundle-after');
        if (!wrap) {
          wrap = document.createElement('div');
          wrap.className = 'product-buy__bundle-after';
          this.optionsEl.after(wrap);
        }
        const order = ['product-buy__checkbox-upsells', 'product-buy__progressive', 'product-buy__bundle-savings'];
        let el = wrap.querySelector(':scope > .' + cls);
        if (!el) {
          el = document.createElement(tag || 'div');
          el.className = cls;
          const next = order.slice(order.indexOf(cls) + 1).map((x) => wrap.querySelector(':scope > .' + x)).find(Boolean);
          wrap.insertBefore(el, next || null);
        }
        return el;
      }

      // Kaching scratch-off: a canvas card over the hidden bars, its title a DOM overlay (bold, sized
      // by the merchant) with Kaching's animated "scratch here" hint. Scratching (pointer), 1.5 s
      // after the first touch, or Enter/Space reveals them; the preselected bar is applied then if
      // the shopper hasn't picked one. In memory only — back on every page load, like Kaching's.
      initScratch() {
        const box = this.optionsEl.querySelector('[data-scratch]');
        if (!box || !this.kScratch) return;
        const s = this.kScratch;
        const canvas = document.createElement('canvas');
        canvas.className = 'product-buy__scratch-canvas';
        canvas.tabIndex = 0;
        canvas.setAttribute('role', 'button');
        canvas.setAttribute('aria-label', s.title || 'Scratch');
        const hint = document.createElement('div');
        hint.className = 'product-buy__scratch-hint';
        hint.setAttribute('aria-hidden', 'true');
        hint.innerHTML = KB.SCRATCH_HINT_SVG;
        const title = document.createElement('div');
        title.className = 'product-buy__scratch-title';
        title.setAttribute('aria-hidden', 'true');
        title.style.setProperty('--kb-scratch-color', s.color);
        title.style.setProperty('--kb-scratch-size', s.size + 'px');
        title.textContent = s.title || '';
        box.appendChild(canvas);
        box.appendChild(hint);
        box.appendChild(title);
        // The canvas only blocks the pointer: keep the hidden bars out of keyboard reach (arrow keys
        // in the radio group) until the card is revealed.
        box.querySelectorAll('input, button, select').forEach((el) => { el.disabled = true; });
        let texture = null;
        const draw = () => {
          const r = box.getBoundingClientRect();
          const dpr = window.devicePixelRatio || 1;
          canvas.width = Math.max(1, Math.round(r.width * dpr));
          canvas.height = Math.max(1, Math.round(r.height * dpr));
          const ctx = canvas.getContext('2d');
          if (!ctx) return;
          ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
          ctx.globalCompositeOperation = 'source-over';
          ctx.fillStyle = s.bg;
          ctx.fillRect(0, 0, r.width, r.height);
          if (texture && texture.complete && texture.naturalWidth) {
            ctx.globalAlpha = 0.6;
            ctx.drawImage(texture, 0, 0, r.width, r.height);
            ctx.globalAlpha = 1;
          }
        };
        requestAnimationFrame(draw);
        // Kaching's own foil texture, drawn over the colour when it arrives (the colour stands in).
        texture = new Image();
        texture.addEventListener('load', () => { if (!this.kRevealed && canvas.isConnected) draw(); }, { once: true });
        texture.src = 'https://bundles-assets.kachingappz.app/widget/scratch-off-background-v2.webp';
        let down = false, scratched = false, timer = null;
        const touched = () => { hint.classList.add('is-hidden'); title.classList.add('is-hidden'); };
        const reveal = () => {
          if (this.kRevealed) return;
          this.kRevealed = true;
          clearTimeout(timer);
          touched();
          canvas.classList.add('is-revealed');
          setTimeout(() => {
            const pre = this.kPreselect && this.optionsEl.querySelector('.product-buy__bundle-radio[data-bar-id="' + this.kPreselect + '"]');
            const picked = this.optionsEl.querySelector('.product-buy__bundle-radio:checked');
            const untouched = !this.kTouched;
            this.render();
            // The bar the merchant preselected becomes the choice, unless the shopper chose already.
            if (pre && untouched) {
              const r = this.optionsEl.querySelector('.product-buy__bundle-radio[data-bar-id="' + this.kPreselect + '"]');
              if (r && !r.disabled) { r.checked = true; r.dispatchEvent(new Event('change', { bubbles: true })); }
            } else if (picked && picked.dataset.barId) {
              const r = this.optionsEl.querySelector('.product-buy__bundle-radio[data-bar-id="' + picked.dataset.barId + '"]');
              if (r) { r.checked = true; r.dispatchEvent(new Event('change', { bubbles: true })); }
            }
          }, 300);
        };
        const scratch = (e) => {
          const r = canvas.getBoundingClientRect();
          const ctx = canvas.getContext('2d');
          if (!ctx) return;
          ctx.globalCompositeOperation = 'destination-out';
          ctx.beginPath();
          ctx.arc(e.clientX - r.left, e.clientY - r.top, 22, 0, Math.PI * 2);
          ctx.fill();
          scratched = true;
        };
        canvas.addEventListener('pointerdown', (e) => {
          down = true;
          touched();
          canvas.setPointerCapture && canvas.setPointerCapture(e.pointerId);
          if (!timer) timer = setTimeout(reveal, 1500);
          scratch(e);
        });
        canvas.addEventListener('pointermove', (e) => { if (down) scratch(e); });
        canvas.addEventListener('pointerup', () => { down = false; if (scratched) reveal(); });
        canvas.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); reveal(); }
        });
      }

      // Kaching countdown pill above the bars ("Sale ends in {{timer}}"), ticking every second.
      renderTimer() {
        clearInterval(this.kTimerTick);
        let el = this.querySelector('.product-buy__bundle-timer');
        const tm = this.kTimer;
        if (!tm || !this.kachingBars.length) { if (el) el.remove(); return; }
        if (!el) {
          el = document.createElement('p');
          el.className = 'product-buy__bundle-timer';
          el.setAttribute('aria-live', 'off');
          this.optionsEl.before(el);
        }
        el.setAttribute('style', tm.vars);
        // A looping (fixed) timer starts once per page view, not on every re-render.
        const start = this.kTimerStart = this.kTimerStart || Date.now();
        const remaining = () => {
          const now = Date.now();
          if (tm.type === 'custom') return Math.max(0, Math.floor((tm.end - now) / 1000));
          if (tm.type === 'midnight') {
            const m = new Date(now); m.setHours(24, 0, 0, 0);
            return Math.max(0, Math.floor((m - now) / 1000));
          }
          const period = Math.max(1, Math.round(tm.minutes * 60));
          return period - (Math.floor((now - start) / 1000) % period); // loops, like Kaching's
        };
        const pad = (n) => String(n).padStart(2, '0');
        let dayFmt = null;
        try { dayFmt = new Intl.NumberFormat((this.kCfg && this.kCfg.locale) || 'lt', { style: 'unit', unit: 'day', unitDisplay: 'narrow' }); } catch (e) { dayFmt = null; }
        const esc = (s) => String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
        const tick = () => {
          const left = remaining();
          if (tm.type === 'custom' && left <= 0) { clearInterval(this.kTimerTick); el.remove(); return; }
          const d = Math.floor(left / 86400), h = Math.floor(left % 86400 / 3600), m = Math.floor(left % 3600 / 60), s = left % 60;
          let v = (h > 0 || d > 0 ? pad(h) + ':' : '') + pad(m) + ':' + pad(s);
          if (d > 0) v = (dayFmt ? dayFmt.format(d) : d + 'd') + ' ' + v;
          el.innerHTML = esc(tm.title).replace(/\{\{[^}]*\}\}/g, '<span class="product-buy__bundle-timer-value" translate="no">' + v + '</span>');
        };
        tick();
        this.kTimerTick = setInterval(tick, 1000);
      }

      // ---------------------------------------------------------------------------------------
      // Storefront data: the other products, complementary picks, media images.
      // ---------------------------------------------------------------------------------------
      // Every product GID the deal needs besides the page product, and every MediaImage GID.
      dealProductGIDs() {
        const ids = [];
        const add = (id) => { if (id && ids.indexOf(id) === -1) ids.push(id); };
        this.kachingBars.forEach((t) => {
          (t.gifts || []).forEach((g) => g.products.forEach((p) => add(p.id)));
          (t.upsells || []).forEach((u) => add(u.productGID));
          (t.freeGifts || []).forEach((g) => add(g.productGID));
          if (t.type === 'sku') add(t.raw.productGID);
          if (t.type === 'bundle' || t.type === 'mix') {
            t.raw.bundleProducts.forEach((bp) => {
              if (bp.productGID && bp.productGID !== 'default') add(bp.productGID);
              (bp.selectedProducts || []).forEach((sp) => add(sp.id));
            });
          }
        });
        if (this.kProgressive) this.kProgressive.gifts.forEach((g) => add(g.productGID));
        if (this.kCheckboxUpsells) this.kCheckboxUpsells.upsells.forEach((u) => add(u.productGID));
        return ids.filter((g) => !this.kp(g));
      }

      dealMediaGIDs() {
        const out = [];
        const add = (g) => { if (g && String(g).indexOf('placeholder') !== 0 && out.indexOf(g) === -1) out.push(g); };
        const d = this.kDeal;
        this.kachingBars.forEach((t) => {
          add(t.mediaGID); add(t.raw.badgeImageGID);
          if (t.highlights) add(t.highlights.iconGID);
          (t.freeGifts || []).forEach((g) => add(g.mediaGID));
          (t.upsells || []).forEach((u) => add(u.mediaGID));
          if (t.perso) add(t.perso.mediaGID);
          if (t.type === 'bundle') t.raw.bundleProducts.forEach((bp) => add(bp.mediaImageGID));
        });
        (d.badges || []).forEach((b) => { if (b.badgeType === 'custom') add(b.imageGID); });
        if (this.kProgressive) {
          this.kProgressive.gifts.forEach((g) => add(g.mediaGID));
          add(this.kProgressive.lockedGID); add(this.kProgressive.dealBarLockedGID);
        }
        if (this.kCheckboxUpsells) this.kCheckboxUpsells.upsells.forEach((u) => { add(u.mediaGID); add(u.badgeImageGID); });
        if (this.kCollection) add(this.kCollection.mediaGID);
        if (this.kSavings) add(this.kSavings.iconGID);
        return out;
      }

      storefront(query, variables) {
        const token = (this.kCfg && this.kCfg.storefrontAccessToken) || window.shopifyStorefrontToken;
        return fetch('/api/2026-04/graphql.json', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Shopify-Storefront-Access-Token': token },
          body: JSON.stringify({ query: query, variables: variables }),
        }).then((r) => r.json());
      }

      // Gift-selector, upsell, progressive-gift, bundle and sku products aren't server-rendered by
      // Kaching (the widget fetches them too), so load them all once via the Storefront API, in the
      // shopper's market/locale. Resolves to {} when there is nothing to fetch, so personalisation-
      // only bars still render.
      loadGiftProducts() {
        if (this.giftProducts || this.giftLoading) return;
        if (!this.kDeal) return;
        const ids = this.dealProductGIDs();
        const gid = (x, type) => KB.gid(x && typeof x === 'object' ? (x.id || x.productGID || x.gid) : x, type);
        const add = (id) => { if (id && ids.indexOf(id) === -1 && !this.kp(id)) ids.push(id); };
        const collectionGids = [];
        if (this.kCollection) {
          this.kCollection.productIds.forEach((p) => add(gid(p, 'Product')));
          this.kCollection.collectionIds.forEach((c) => { const g = gid(c, 'Collection'); if (g) collectionGids.push(g); });
          this.kCollection.excludedGids = this.kCollection.excluded.map((p) => gid(p, 'Product'));
        }
        const wantsComplementary = this.kachingBars.some((t) => (t.upsells || []).some((u) => u.source === 'complementary')) ||
          (this.kCheckboxUpsells && this.kCheckboxUpsells.upsells.some((u) => u.source === 'complementary'));
        const mediaGids = this.dealMediaGIDs();
        const token = (this.kCfg && this.kCfg.storefrontAccessToken) || window.shopifyStorefrontToken;
        // The products arrived: bars that wait for them are drawn now; otherwise just refresh what
        // the products feed (gifts, upsells, savings) without redrawing the bars.
        const done = () => {
          this.giftProducts = this.giftProducts || {};
          if (this.kachingBars.some((b) => !b.qtyBased)) { this.rebuild(); return; }
          if (!this.tiers.length) return;
          this.renderGifts();
          this.renderCheckboxUpsells();
          this.renderProgressive();
          this.renderSavings();
        };
        if (!token || (!ids.length && !collectionGids.length && !wantsComplementary && !mediaGids.length)) {
          this.giftProducts = {};
          return;
        }
        this.giftLoading = true;
        const cfg = this.kCfg;
        const locale = { country: cfg.country, language: String(cfg.locale || 'lt').toUpperCase() };
        const ctxVars = '$country:CountryCode,$language:LanguageCode';
        const inCtx = '@inContext(country:$country,language:$language)';
        // Media images (badge art, bar images, gift photos...) arrive on their own and redraw.
        if (mediaGids.length) {
          this.storefront('query($ids:[ID!]!){nodes(ids:$ids){... on MediaImage{id image{url(transform:{maxWidth:300,maxHeight:300})}}}}', { ids: mediaGids })
            .then((res) => {
              ((res.data && res.data.nodes) || []).forEach((n) => { if (n && n.image) this.kMedia[n.id] = n.image.url; });
              if (this.tiers.length) this.render();
              this.renderCheckboxUpsells();
              this.renderProgressive();
              this.renderSavings();
            })
            .catch(() => {});
        }
        // Complementary products (productRecommendations): their ids first, then fetched like the rest.
        const compIds = wantsComplementary
          ? this.storefront('query($id:ID!,' + ctxVars + ') ' + inCtx + '{productRecommendations(productId:$id,intent:COMPLEMENTARY){id}}',
            Object.assign({ id: 'gid://shopify/Product/' + this.dataset.productId }, locale))
            .then((res) => ((res.data && res.data.productRecommendations) || []).map((p) => p.id)).catch(() => [])
          : Promise.resolve([]);
        compIds.then((comp) => {
          this.kCompIds = comp;
          comp.forEach(add);
          if (!ids.length && !collectionGids.length) return { data: { nodes: [] } };
          // Products by id, plus (collection breaks) the products of any selected collections — that
          // part only when there are collections, so the common query stays small.
          const withCollections = collectionGids.length > 0;
          const query = 'query($ids:[ID!]!' + (withCollections ? ',$cids:[ID!]!' : '') + ',$sp:Boolean!,$qty:Boolean!,' + ctxVars + ') ' + inCtx + '{' +
            'nodes(ids:$ids){... on Product{' + KB.PRODUCT_FIELDS + '}}' +
            (withCollections ? ' collections: nodes(ids:$cids){... on Collection{products(first:50){nodes{' + KB.PRODUCT_FIELDS + '}}}}' : '') + '}';
          return this.storefront(query, Object.assign({ ids: ids, sp: !!cfg.includeSellingPlans, qty: !!cfg.includeAvailableQuantity },
            withCollections ? { cids: collectionGids } : {}, locale));
        })
          .then((res) => {
            this.giftProducts = {};
            const keep = (p) => {
              if (!p) return;
              this.giftProducts[p.id] = this.giftProducts[p.id] || p;
              if (!this.kProd[p.id]) this.kProd[p.id] = KB.normalizeProduct(p, this.kRate);
            };
            ((res.data && res.data.nodes) || []).forEach(keep);
            // Collection-break eligible products: listed ids + the selected collections' products,
            // minus exclusions, this product first.
            if (this.kCollection) {
              const own = 'gid://shopify/Product/' + this.dataset.productId;
              const list = this.kCollection.productIds.length || this.kCollection.collectionIds.length
                ? ids.filter((g) => g === own || this.kCollection.productIds.some((p) => String(g).split('/').pop() === String(p && p.id || p).split('/').pop()))
                : [own];
              ((res.data && res.data.collections) || []).forEach((c) => ((c && c.products && c.products.nodes) || []).forEach((p) => {
                keep(p);
                if (list.indexOf(p.id) === -1) list.push(p.id);
              }));
              this.kCollection.eligible = [own].concat(list.filter((g) => g !== own))
                .filter((g) => this.kCollection.excludedGids.indexOf(g) === -1 && (g === own || this.pickVariant(g)));
            }
            this.kComp = (this.kCompIds || []).filter((g) => { const p = this.kp(g); return p && p.availableForSale; });
          })
          .catch(() => { this.giftProducts = this.giftProducts || {}; })
          .finally(() => { this.giftLoading = false; done(); });
      }

      // A product variant of the page product (collection breaks) or a loaded product, as the light
      // {id, price, compareAt, title, productTitle, img} the slot picker works with.
      pickVariant(productGID, allowed, preferred) {
        const own = productGID === 'gid://shopify/Product/' + this.dataset.productId;
        const p = own ? this.pageProd : this.kp(productGID);
        if (!p) return null;
        const ids = allowed && allowed.length ? allowed.map(KB.numId) : null;
        const pref = preferred ? KB.numId(preferred) : null;
        const ok = (v) => v.availableForSale && (!ids || ids.indexOf(v.id) !== -1);
        const v = p.variants.find((x) => x.id === pref && ok(x)) || p.variants.find(ok);
        if (!v) return null;
        return {
          id: v.id,
          price: v.price,
          compareAt: v.compareAtPrice || 0,
          title: p.title + (KB.variantTitle(v) ? ' – ' + KB.variantTitle(v) : ''),
          productTitle: p.title,
          img: v.image || p.image || '',
        };
      }

      // ---------------------------------------------------------------------------------------
      // Add-on pricing (upsells, checkbox upsells): Kaching's own rules, per product line.
      // ---------------------------------------------------------------------------------------
      // The product an upsell points at; a "complementary" upsell takes one of the Storefront
      // recommendations, (index + a per-page random offset) % count, as the widget does.
      addonProduct(u, index) {
        if (u.source === 'complementary') {
          const list = this.kComp || [];
          this.kOffset = this.kOffset == null ? Math.floor(Math.random() * 1000) : this.kOffset;
          return list.length ? this.kp(list[(index + this.kOffset) % list.length]) : null;
        }
        return this.kp(u.productGID);
      }

      // The plan an add-on line is bought under: "deal" follows the purchase-type card (the same
      // plan, else same name, else the variant's first); "always" is the add-on's own pick.
      addonPlan(u, product, variant, own) {
        if (u.sub === false) return undefined;
        if (u.subType === 'always') {
          const plans = KB.plansOf(product, [variant]);
          return plans.find((p) => p.id === own) || plans.find((p) => { const cur = this.dealPlan(); return cur && p.id === cur.id; }) || plans[0];
        }
        const cur = this.dealPlan();
        return cur ? KB.planFor(product, variant, cur) : undefined;
      }

      // Priced add-on: {product, variant, v, full, price, plan} or null when unavailable. `qty` is
      // the number of units; `variantId` / `planId` the shopper's picks.
      addonOffer(u, index, qty, variantId, planId) {
        const product = this.addonProduct(u, index);
        if (!product || !product.availableForSale) return null;
        const list = KB.allowedVariants(product, u.variantGIDs);
        if (!list.length) return null;
        const pref = variantId || (u.defaultVariantGID && KB.numId(u.defaultVariantGID));
        const variant = list.find((x) => x.id === pref) || list[0];
        const plan = this.addonPlan(u, product, variant, planId);
        const unitFull = this.kDeal && this.kDeal.useProductCompareAtPrice === false ? variant.price : Math.max(variant.price, variant.compareAtPrice || 0);
        const price = KB.lineTotal({ discountType: u.dtype, discountValue: u.dval, quantity: u.qty || 1 }, variant, qty, this.kRate, this.kRounding, plan,
          this.kFlags.percentage_cents_rounding_workaround);
        return {
          product: product, variant: variant, variants: list, plan: plan,
          full: unitFull * qty, price: price,
          v: { id: variant.id, productTitle: product.title, title: product.title + (KB.variantTitle(variant) ? ' – ' + KB.variantTitle(variant) : ''), img: variant.image || product.image || '' },
        };
      }

      // Context for the {{placeholders}} of an add-on text: per-unit prices, as Kaching fills them.
      addonCtx(o, qty) {
        return {
          money: this.moneyFn, product: o.product, selectedVariants: [{ variant: o.variant, quantity: qty }], totalFull: o.full, totalCompareAt: o.full,
          totalDiscounted: o.price, quantity: qty, sellingPlan: o.plan, unitQuantity: null,
        };
      }

      // The image an add-on shows: custom image, else the variant's, else the product's.
      addonImage(mediaGID, o) {
        return this.media(mediaGID) || (o && (o.variant.image || o.product.image)) || '';
      }

      imgTag(src, size, cls) {
        if (!src) return '';
        const w = Math.round(size * 2);
        return '<img class="' + (cls || 'product-buy__gift-img') + '" src="' + KB.esc(src) + (src.indexOf('?') === -1 ? '?' : '&') + 'width=' + w + '" alt="" loading="lazy" width="' + size + '" height="' + size + '">';
      }

      // `<option>` list of variants, `value` selected.
      variantOptions(variants, value) {
        // "Hide unavailable options": sold-out variants are left out of the selects (unless all are).
        if (this.kDeal && this.kDeal.hideUnavailableOptions && variants.some((v) => v.availableForSale)) {
          variants = variants.filter((v) => v.availableForSale || v.id === value);
        }
        return variants.map((v) => '<option value="' + v.id + '"' + (v.id === value ? ' selected' : '') + (v.availableForSale ? '' : ' disabled') + '>' +
          KB.esc(KB.variantTitle(v) || v.title || '') + '</option>').join('');
      }

      planOptions(plans, value) {
        return plans.map((p) => '<option value="' + p.id + '"' + (p.id === value ? ' selected' : '') + '>' + KB.esc(p.name) + '</option>').join('');
      }

      // How many times the bar's own quantity fits the units the shopper chose (the quantity stepper
      // on a quantity-break bar): add-ons that scale with the deal are multiplied by it.
      setsOf(t, c) {
        return t.qtyBased && t.bq > 0 ? Math.max(1, Math.floor(c.qty / t.bq)) : 1;
      }

      // ---------------------------------------------------------------------------------------
      // The box under a bar.
      // ---------------------------------------------------------------------------------------
      // Picks survive re-renders (variant switch): {barId: {selectorId: [variantId, ...]}}.
      renderGifts() {
        if (!this.giftProducts) return;
        this.giftPicks = this.giftPicks || {};
        this.optionsEl.querySelectorAll('[data-gifts-for]').forEach((box) => {
          const tier = this.tiers.find((t) => t.barId === box.dataset.giftsFor);
          if (!tier) return;
          const html = this.boxHTML(tier, tier.barId === this.kChecked);
          box.innerHTML = html;
          box.hidden = !html;
          this.bindBox(box, tier);
        });
      }

      boxHTML(t, selected) {
        const c = this.calcTier(t);
        let html = '';
        html += this.qtyStepperHTML(t, selected);
        html += this.planPickHTML(t, selected);
        html += this.itemsHTML(t, c, selected);
        if (selected) html += this.selectorsHTML(t);
        html += this.freeGiftsHTML(t, c, selected);
        html += this.dealBarGiftsHTML(t, selected);
        html += this.upsellsHTML(t, c, selected);
        if (selected) html += this.slotsHTML(t) + this.persoHTML(t);
        return html;
      }

      // Quantity stepper inside a quantity-break bar (`quantitySelector`).
      qtyStepperHTML(t, selected) {
        if (!selected || !t.quantitySelector) return '';
        const q = this.tierQty(t);
        const label = KB.esc(this.sys('system.quantity'));
        return '<div class="product-buy__bar-qty" data-bar-qty-box><button type="button" data-bar-qty="-1" aria-label="' + label + ' -"' + (q <= t.bq ? ' disabled' : '') + '>−</button>' +
          '<input type="number" class="product-buy__bar-qty-input" min="' + t.bq + '" step="1" value="' + q + '" aria-label="' + label + '">' +
          '<button type="button" data-bar-qty="1" aria-label="' + label + ' +">+</button></div>';
      }

      // A bar that lets the shopper choose its selling plan (sellingPlanEnabled without a fixed plan).
      planPickHTML(t, selected) {
        if (!selected || !t.kaching || !t.raw.sellingPlanEnabled || t.raw.sellingPlanGid) return '';
        const plans = this.barPlans(t);
        if (plans.length < 2) return '';
        const cur = this.tierPlan(t);
        return '<div class="product-buy__bar-pick"><select data-pick="bplan" aria-label="' + KB.esc(this.sys('system.subscription_plan')) + '">' + this.planOptions(plans, cur && cur.id) + '</select></div>';
      }

      // Bundle products / mix-and-match slots / the sku variant picker.
      itemsHTML(t, c, selected) {
        if (t.qtyBased || !c.sel) return '';
        const esc = KB.esc;
        if (t.type === 'sku') {
          const s = c.sel;
          if (!selected || s.product.variants.length < 2) return '';
          const list = KB.allowedVariants(s.product, t.raw.variantGIDs);
          if (list.length < 2) return '';
          return '<div class="product-buy__bar-pick"><select data-pick="sv" aria-label="' + esc(s.product.title) + '">' + this.variantOptions(list, s.variant.id) + '</select></div>';
        }
        if (t.raw.showProductsOnlyWhenSelected && !selected) return '';
        const rows = t.raw.bundleProducts.filter((bp) => bp.productGID).map((bp) => {
          const s = c.sel[bp.id];
          if (!s) return '';
          const img = this.imgTag(this.media(bp.mediaImageGID) || s.variant.image || s.product.image, 50, 'product-buy__bp-img');
          let title;
          let priceHTML = '';
          let pick = '';
          if (t.type === 'bundle') {
            const rate = this.kRate;
            const total = KB.discountedTotal({ discountType: bp.discountType, discountValue: bp.discountValue, discountQuantity: bp.quantity, variantQuantities: [s],
              currencyRate: rate, priceRounding: this.kRounding, sellingPlan: undefined });
            const full = s.variant.price * s.quantity;
            const ctx = { money: this.moneyFn, product: s.product, selectedVariants: [s], totalFull: full, totalCompareAt: full, totalDiscounted: total, quantity: s.quantity };
            title = (s.quantity > 1 ? s.quantity + '× ' : '') + (bp.title ? KB.fill(this.kT(bp.title), ctx) : s.product.title);
            priceHTML = '<span class="product-buy__bp-price">' + (total < full ? '<s>' + this.money(full) + '</s>' : '') + '<b>' + this.money(total) + '</b></span>';
            const list = this.bundleVariants(bp, s.product);
            if (selected && list.length > 1) pick = '<select data-pick="bv:' + esc(bp.id) + '" aria-label="' + esc(s.product.title) + '">' + this.variantOptions(list, s.variant.id) + '</select>';
          } else {
            title = s.product.title + (KB.variantTitle(s.variant) ? ' – ' + KB.variantTitle(s.variant) : '');
            const opts = this.mixOptions(bp);
            const flat = [];
            opts.forEach((o) => o.variants.forEach((v) => flat.push({ pid: o.product.id, v: v, label: o.product.title + (KB.variantTitle(v) ? ' – ' + KB.variantTitle(v) : '') })));
            // Kaching shows a swap control only when the slot has something else to hold (its swap button / modal),
            // never a picker repeating the current product: a swap select, listing only the other choices.
            const others = flat.filter((f) => !(f.pid === s.product.id && f.v.id === s.variant.id));
            if (others.length) {
              pick = '<select class="product-buy__swap" data-pick="mx:' + esc(bp.id) + '" aria-label="' + esc(title) + '"><option value="" selected disabled hidden>' +
                esc(t.raw.buttonText ? this.kT(t.raw.buttonText) : (this.dataset.swapLabel || '⇄')) + '</option>' + others.map((f) => '<option value="' + f.pid + ':' + f.v.id + '">' + esc(f.label) + '</option>').join('') + '</select>';
            }
          }
          // Mix-and-match "show product prices": each product's share of the set price.
          if (t.type === 'mix' && t.raw.showProductPrices && c.shares && c.shares[bp.id]) {
            const sh = c.shares[bp.id];
            priceHTML = '<span class="product-buy__bp-price">' + (sh.discountedPrice < sh.fullPrice ? '<s>' + this.money(sh.fullPrice) + '</s>' : '') + '<b>' + this.money(sh.discountedPrice) + '</b></span>';
          }
          return '<div class="product-buy__bp">' + img + '<span class="product-buy__bp-text"><span class="product-buy__bp-title">' + esc(title) + '</span>' + pick + '</span>' + priceHTML + '</div>';
        });
        return rows.length ? '<div class="product-buy__bp-list">' + rows.join('<div class="product-buy__bp-plus" aria-hidden="true">+</div>') + '</div>' : '';
      }

      // Multi-gift selectors: pick N of M gift products (existing behaviour).
      selectorsHTML(t) {
        const esc = KB.esc;
        const picks = (this.giftPicks[t.barId] = this.giftPicks[t.barId] || {});
        return t.gifts.map((g) => {
          const chosen = picks[g.id] = (picks[g.id] || []).slice(0, g.max);
          const opts = [];
          g.products.forEach((ref) => {
            const p = this.kp(ref.id);
            if (!p) return;
            const list = KB.allowedVariants(p, ref.variantGIDs);
            list.forEach((v) => {
              const price = Math.max(v.price, v.compareAtPrice || 0);
              const img = v.image || p.image;
              const name = p.title + (KB.variantTitle(v) ? ' – ' + KB.variantTitle(v) : '');
              const id = String(v.id);
              opts.push('<label class="product-buy__gift-opt' + (chosen.indexOf(id) !== -1 ? ' is-active' : '') + '">' +
                '<input class="product-buy__gift-input" type="' + (g.max === 1 ? 'radio' : 'checkbox') + '" form="kaching-gift-none"' +
                  ' name="kaching-gift-' + esc(t.barId) + '-' + esc(g.id) + '" value="' + id + '" data-selector="' + esc(g.id) + '"' +
                  (chosen.indexOf(id) !== -1 ? ' checked' : '') + '>' +
                this.imgTag(img, 48) +
                '<span class="product-buy__gift-text">' +
                  '<span class="product-buy__gift-name">' + esc(name) + '</span>' +
                  '<span class="product-buy__gift-prices"><s>' + this.money(price) + '</s>' +
                    (this.kFreeWord ? '<span class="product-buy__gift-free">' + esc(this.kFreeWord) + '</span>' : '') + '</span>' +
                '</span>' +
              '</label>');
            });
          });
          if (!opts.length) return '';
          return '<div class="product-buy__gift-group" data-selector="' + esc(g.id) + '">' +
            '<p class="product-buy__gift-heading">' + esc(chosen.length ? g.after || g.before : g.before) + '</p>' +
            opts.join('') + '</div>';
        }).join('');
      }

      // Free gifts of a bar (`freeGifts`): one row each under the bar, or - while the bar isn't
      // selected and the merchant turned on the summary - one line with the gift photos.
      freeGiftRows(t, c) {
        const plan = this.dealPlan();
        const st = this.kState(t.barId);
        st.fg = st.fg || {};
        const rows = [];
        t.freeGifts.forEach((g) => {
          if (g.onlySub && !plan) return;
          if (g.type === 'shipping' || !g.productGID) { rows.push({ g: g }); return; }
          const p = this.kp(g.productGID);
          if (!p || !p.availableForSale) return;
          const variants = KB.allowedVariants(p, g.variantGIDs);
          if (!variants.length) return;
          const v = variants.find((x) => x.id === st.fg[g.id]) ||
            (!(this.kDeal && this.kDeal.disableVariantOptionSync) && variants.find((x) => x.id === Number(this.variantId))) || variants[0];
          rows.push({ g: g, p: p, variants: variants, v: v });
        });
        return rows;
      }

      giftImage(row) {
        const url = this.media(row.g.mediaGID);
        if (url) return url;
        if (row.g.type === 'shipping' || !row.p) return '';
        return row.v.image || row.p.image || '';
      }

      freeGiftsHTML(t, c, selected) {
        if (!t.freeGifts.length) return '';
        const esc = KB.esc;
        const rows = this.freeGiftRows(t, c);
        if (!rows.length) return '';
        const sets = this.setsOf(t, c);
        if (!selected && t.giftsSummary && rows.length >= 2) {
          const imgs = t.giftsSummary.showImages
            ? '<span class="product-buy__freegift-images">' + rows.map((r) => {
              const u = this.giftImage(r);
              return u ? this.imgTag(u, Math.round(rows[0].g.imageSize || 30), 'product-buy__freegift-photo') : (r.g.type === 'shipping' ? '<span class="product-buy__freegift-photo is-icon">' + TRUCK_ICON + '</span>' : '');
            }).join('') + '</span>' : '';
          const txt = KB.fill(t.giftsSummary.title, { money: this.moneyFn, quantity: 1, totalFull: 0, totalDiscounted: 0, freeGiftsCount: rows.length });
          return '<div class="product-buy__freegift is-summary">' + imgs + (txt ? '<span class="product-buy__freegift-text">' + esc(txt) + '</span>' : '') + '</div>';
        }
        const st = this.kState(t.barId);
        return rows.map((r) => {
          const g = r.g;
          const qty = g.qty * sets;
          let full = 0;
          if (r.v) {
            const unit = this.kDeal && this.kDeal.useProductCompareAtPrice === false ? r.v.price : Math.max(r.v.price, r.v.compareAtPrice || 0);
            full = unit * qty;
          }
          const ctx = { money: this.moneyFn, product: r.p, selectedVariants: r.v ? [{ variant: r.v, quantity: qty }] : [], totalFull: full, totalCompareAt: full, totalDiscounted: 0, quantity: qty };
          const text = KB.fill(g.text, ctx);
          const url = this.giftImage(r);
          const size = Math.round(g.imageSize || 30);
          const img = url ? this.imgTag(url, size, 'product-buy__freegift-photo')
            : (g.type === 'shipping' ? '<span class="product-buy__freegift-photo is-icon" style="width:' + size + 'px;height:' + size + 'px">' + TRUCK_ICON + '</span>' : '');
          const pick = selected && r.variants && r.variants.length > 1
            ? '<select data-pick="fg:' + esc(g.id) + '" aria-label="' + esc(r.p.title) + '">' + this.variantOptions(r.variants, r.v.id) + '</select>' : '';
          return '<div class="product-buy__freegift" data-free-gift="' + esc(g.id) + '">' + img +
            '<span class="product-buy__freegift-text">' + (text ? esc(text) : '') + pick + '</span>' +
            (g.showPrice && full > 0 ? '<span class="product-buy__freegift-price"><s>' + this.money(full) + '</s></span>' : '') + '</div>';
        }).join('');
      }

      // Progressive gifts laid out inside each bar (`layout: "deal-bar"`): the gifts that bar unlocks,
      // locked unless the SELECTED bar unlocks them.
      dealBarGiftsHTML(t, selected) {
        const pg = this.kProgressive;
        if (!pg || !pg.dealBarLayout || !t.kaching || !this.giftProducts) return '';
        const esc = KB.esc;
        const plan = this.dealPlan();
        const selPos = this.kChecked ? this.barPosition(this.kChecked) : 0;
        const lockedUrl = this.media(pg.dealBarLockedGID);
        const out = pg.gifts.filter((g) => this.progressiveUnlocked(g, t.idx + 1)).map((g) => {
          const isProduct = g.type !== 'shipping';
          const p = isProduct ? this.kp(g.productGID) : null;
          if (isProduct && (!p || !p.availableForSale)) return '';
          const variants = isProduct ? KB.allowedVariants(p, g.variantGIDs) : [];
          if (isProduct && !variants.length) return '';
          const v = variants[0];
          const locked = !this.progressiveUnlocked(g, selPos) || (g.onlySub && !plan);
          if (locked && pg.hideLocked) return '';
          const full = v ? v.price * g.qty : 0;
          const ctx = { money: this.moneyFn, product: p, selectedVariants: v ? [{ variant: v, quantity: g.qty }] : [], totalFull: full, totalCompareAt: full, totalDiscounted: 0, quantity: g.qty };
          const productImg = this.media(g.mediaGID) || (v && (v.image || p.image)) || '';
          let media;
          if (locked) {
            if (lockedUrl) media = this.imgTag(lockedUrl, 40, 'product-buy__dbg-img');
            else if (pg.showLockedProducts && productImg) media = this.imgTag(productImg, 40, 'product-buy__dbg-img is-locked-product');
            else media = '<span class="product-buy__dbg-img is-icon">' + KB.LOCK_SVG + '</span>';
          } else media = productImg ? this.imgTag(productImg, 40, 'product-buy__dbg-img') : '<span class="product-buy__dbg-img is-icon">' + TRUCK_ICON + '</span>';
          const text = locked && !pg.showLockedProducts ? (g.locked || KB.fill(g.title, ctx)) : KB.fill(g.title, ctx);
          const label = (!locked || pg.showLockedLabels) && (g.label || g.crossed)
            ? '<span class="product-buy__dbg-label">' + esc(KB.fill(g.label, ctx)) + (g.crossed ? ' <s>' + esc(KB.fill(g.crossed, ctx)) + '</s>' : '') + '</span>' : '';
          return '<div class="product-buy__dbg' + (locked ? ' is-locked' : '') + '">' + media + '<span class="product-buy__dbg-text">' + esc(text) + '</span>' + label + '</div>';
        }).join('');
        return out ? '<div class="product-buy__dbg-list">' + out + '</div>' : '';
      }

      // Bar upsells: another product at a Kaching discount. Shown under the bar even while it isn't
      // selected (without the tick box), unless the merchant limits it to the selected bar.
      upsellsHTML(t, c, selected) {
        if (!t.upsells.length) return '';
        const esc = KB.esc;
        this.upsellPicks = this.upsellPicks || {};
        const picks = this.upsellPicks[t.barId] = this.upsellPicks[t.barId] ||
          Object.fromEntries(t.upsells.map((u) => [u.id, u.preselected]));
        const st = this.kState(t.barId);
        st.uv = st.uv || {}; st.up = st.up || {};
        const sets = this.setsOf(t, c);
        let html = '';
        t.upsells.forEach((u) => {
          if (u.selectedOnly && !selected) return;
          const qty = u.qty * sets;
          const o = this.addonOffer(u, u.index, qty, st.uv[u.id], st.up[u.id]);
          if (!o) return;
          const ctx = this.addonCtx(o, qty);
          const text = KB.fill(u.text || '{{product}}', ctx);
          const on = selected && !!picks[u.id];
          const perItem = this.kDeal.showPricesPerItem;
          const unit = (v) => Math.ceil(v / qty);
          const both = this.kDeal.showBothPrices && qty > 1;
          const strike = o.full > o.price;
          const priceHTML = both
            ? '<span class="product-buy__gift-prices">' + (strike ? '<s>' + this.money(unit(o.full)) + '</s>' : '') + '<span class="product-buy__upsell-price">' + this.money(unit(o.price)) + '</span></span>' +
              '<span class="product-buy__gift-prices is-secondary">' + (strike ? '<s>' + this.money(o.full) + '</s>' : '') + '<span>' + this.money(o.price) + '</span></span>'
            : '<span class="product-buy__gift-prices">' + (strike ? '<s>' + this.money(perItem ? unit(o.full) : o.full) + '</s>' : '') +
              '<span class="product-buy__upsell-price">' + this.money(perItem ? unit(o.price) : o.price) + '</span></span>';
          const badges = KB.badgesFor(this.kDeal, { type: 'barUpsell', dealBarId: t.barId, upsellId: u.id })
            .map((b) => Object.assign({}, b, { text: KB.fill(this.kT(b.text), ctx) }));
          const bbox = KB.badgeBoxAttrs(badges);
          const pick = selected && o.variants.length > 1
            ? '<select data-pick="up:' + esc(u.id) + '" aria-label="' + esc(o.product.title) + '">' + this.variantOptions(o.variants, o.variant.id) + '</select>' : '';
          const plans = u.sub && u.subType === 'always' ? KB.plansOf(o.product, [o.variant]) : [];
          const planPick = selected && on && plans.length > 1
            ? '<select data-pick="upplan:' + esc(u.id) + '" aria-label="' + esc(this.sys('system.subscription_plan')) + '">' + this.planOptions(plans, o.plan && o.plan.id) + '</select>' : '';
          const imgUrl = this.addonImage(u.mediaGID, o);
          const img = u.imageSize > 0 ? this.imgTag(imgUrl, u.imageSize) : '';
          const inner = (selected ? '<input class="product-buy__upsell-input" type="checkbox" form="kaching-gift-none" data-upsell="' + esc(u.id) + '"' + (on ? ' checked' : '') + '>' : '') +
            '<span class="product-buy__check" aria-hidden="true"></span>' + img +
            '<span class="product-buy__gift-text">' +
              '<span class="product-buy__gift-name">' + esc(text) + '</span>' +
              '<span class="product-buy__upsell-product">' + esc(o.v.title) + '</span>' + pick + planPick +
            '</span>' + priceHTML;
          html += '<div class="product-buy__upsell-wrap' + (bbox.cls ? ' ' + bbox.cls : '') + '" style="' + esc(bbox.style) + '">' +
            badges.map((b) => KB.badgeHTML(b, this.kDeal, (g) => this.media(g))).join('') +
            (selected
              ? '<label class="product-buy__gift-opt product-buy__upsell' + (on ? ' is-active' : '') + '">' + inner + '</label>'
              : '<div class="product-buy__gift-opt product-buy__upsell is-idle">' + inner + '</div>') + '</div>';
        });
        return html;
      }

      // Collection-break slots: unit 1 is this product; each further unit can be swapped for an
      // eligible product via the picker (an unchosen slot stays this product).
      slotsHTML(t) {
        const c = this.kCollection;
        if (!(c && t.kaching && t.qtyBased && this.tierQty(t) > 1 && c.eligible && c.eligible.length)) return '';
        const esc = KB.esc;
        const qty = this.tierQty(t);
        this.kSlots = this.kSlots || [];
        const own = this.pickVariant('gid://shopify/Product/' + this.dataset.productId, null, 'gid://shopify/ProductVariant/' + this.variantId);
        const placeholder = this.media(c.mediaGID);
        let s = '<div class="product-buy__slots">';
        for (let k = 0; k < qty; k++) {
          const chosen = k > 0 ? this.kSlots[k] : null;
          const v = chosen || own;
          const src = v ? v.img : (placeholder || '');
          s += '<div class="product-buy__slot' + (chosen ? ' is-chosen' : '') + '" data-slot="' + k + '">' +
            '<span class="product-buy__slot-n">' + (k + 1) + '</span>' + this.imgTag(k > 0 && !chosen && placeholder ? placeholder : src, c.photoSize) +
            '<span class="product-buy__slot-name">' + (c.showName && v ? esc(v.title) : '') + '</span>' +
            (k === 0 ? '' : chosen
              ? '<button type="button" class="product-buy__slot-remove" data-slot-remove aria-label="' + esc(this.sys('system.remove')) + '">×</button>'
              : '<button type="button" class="product-buy__slot-choose" data-slot-choose>' + esc(c.button) + '</button>') +
            '</div>';
        }
        return s + '</div>';
      }

      // Product personalisation: tick (or required), one text field per unit.
      persoRules() {
        const a = (this.kDeal && this.kDeal.addPersonalisationModal) || {};
        return {
          letters: a.lettersAllowed == null ? true : !!a.lettersAllowed,
          numbers: a.numbersAllowed == null ? true : !!a.numbersAllowed,
          limit: a.characterLimitEnabled ? (a.characterLimit != null ? Number(a.characterLimit) : 30) : null,
          placeholder: a.placeholderText ? this.kT(a.placeholderText) : '',
        };
      }

      persoHTML(t) {
        const p = t.perso;
        if (!p) return '';
        const esc = KB.esc;
        const rules = this.persoRules();
        this.persoOn = this.persoOn || {};
        this.persoValues = this.persoValues || {};
        const qty = this.tierQty(t);
        const on = p.required || !!this.persoOn[t.barId];
        const vals = this.persoValues[t.barId] = (this.persoValues[t.barId] || []).slice(0, qty);
        const price = Math.round(p.price * 100 * this.kRate);
        const cmp = Math.round(p.compareAt * 100 * this.kRate);
        const img = this.media(p.mediaGID) ? this.imgTag(this.media(p.mediaGID), 48) : '';
        return '<div class="product-buy__perso' + (on ? ' is-active' : '') + '">' +
          '<label class="product-buy__gift-opt product-buy__upsell' + (on ? ' is-active' : '') + '">' +
            '<input class="product-buy__perso-toggle" type="checkbox" form="kaching-gift-none"' + (on ? ' checked' : '') + (p.required ? ' disabled' : '') + '>' +
            '<span class="product-buy__check" aria-hidden="true"></span>' + img +
            '<span class="product-buy__gift-text">' +
              '<span class="product-buy__gift-name">' + esc(p.title) + '</span>' +
              (p.subtitle ? '<span class="product-buy__upsell-product">' + esc(p.subtitle) + '</span>' : '') +
              '<span class="product-buy__gift-prices">' + (cmp > price ? '<s>' + this.money(cmp) + '</s>' : '') +
                '<span class="product-buy__upsell-price">' + this.money(price) + (p.unitLabel ? ' ' + esc(p.unitLabel) : '') + '</span></span>' +
            '</span></label>' +
          (on ? '<div class="product-buy__perso-fields">' + Array.from({ length: qty }, (x, i) =>
            '<input class="product-buy__perso-input" type="text"' + (rules.limit != null ? ' maxlength="' + rules.limit + '"' : '') + ' form="kaching-gift-none" data-i="' + i + '"' +
              ' placeholder="' + esc(rules.placeholder || (p.valueLabel + (qty > 1 ? ' #' + (i + 1) : ''))) + '"' +
              ' aria-label="' + esc(p.title + (qty > 1 ? ' #' + (i + 1) : '')) + '" value="' + esc(vals[i] || '') + '">').join('') +
            '<span class="product-buy__perso-error" hidden></span></div>' : '') +
        '</div>';
      }

      // Product picker for a collection-break slot, in the gift-modal style the theme already has:
      // optional search, sort, variant image / selector per product.
      openSlotPicker(k) {
        const c = this.kCollection;
        if (!c || !c.eligible) return;
        const esc = KB.esc;
        const own = 'gid://shopify/Product/' + this.dataset.productId;
        const products = c.eligible.map((g) => (g === own ? this.pageProd : this.kp(g))).filter((p) => p && p.variants.some((v) => v.availableForSale));
        const modal = document.createElement('div');
        modal.className = 'gift-selection-modal product-buy__picker';
        if (c.vars) modal.setAttribute('style', c.vars);
        const sorts = [
          ['relevance', 'system.sort_relevance'], ['newest-first', 'system.sort_newest_first'],
          ['price-low-to-high', 'system.sort_price_low_to_high'], ['price-high-to-low', 'system.sort_price_high_to_low'],
        ];
        let sort = sorts.some((s) => s[0] === c.sort) ? c.sort : 'relevance';
        let query = '';
        const firstPrice = (p) => ((p.variants.find((v) => v.availableForSale) || p.variants[0]) || {}).price || 0;
        const list = () => {
          let out = products.slice();
          if (c.showSearch && query.trim()) out = out.filter((p) => p.title.toLowerCase().indexOf(query.toLowerCase().trim()) !== -1);
          if (sort === 'newest-first') out.sort((a, b) => (Date.parse(b.createdAt) || 0) - (Date.parse(a.createdAt) || 0) || b.id - a.id);
          else if (sort === 'price-low-to-high') out.sort((a, b) => firstPrice(a) - firstPrice(b));
          else if (sort === 'price-high-to-low') out.sort((a, b) => firstPrice(b) - firstPrice(a));
          return out;
        };
        const cards = () => {
          const out = list();
          if (!out.length) return '<p class="product-buy__picker-empty">' + esc(this.sys(query.trim() ? 'system.no_results_found' : 'system.no_products_available')) + '</p>';
          return out.map((p) => {
            const variants = p.variants.filter((v) => v.availableForSale);
            const v0 = variants[0];
            const img = c.variantImage ? (v0.image || p.image) : (p.image || v0.image);
            const select = variants.length > 1
              ? '<select class="product-buy__picker-variant" aria-label="' + esc(p.title) + '">' + this.variantOptions(variants, v0.id) + '</select>' : '';
            return '<div class="product-buy__picker-card" data-product="' + esc(p.gid || 'gid://shopify/Product/' + p.id) + '">' +
              this.imgTag(img, c.modalPhoto, 'product-buy__picker-img') +
              '<span class="product-buy__picker-title">' + esc(p.title) + '</span>' +
              '<span class="product-buy__picker-price">' + this.money(v0.price) + (v0.compareAtPrice > v0.price ? ' <s>' + this.money(v0.compareAtPrice) + '</s>' : '') + '</span>' + select +
              '<button type="button" class="product-buy__picker-choose">' + esc(c.modalButton) + '</button></div>';
          }).join('');
        };
        modal.innerHTML = '<div class="gift-selection-modal__overlay"></div>' +
          '<div class="gift-selection-modal__dialog" role="dialog" aria-modal="true">' +
            '<button type="button" class="gift-selection-modal__close" aria-label="' + esc(this.sys('system.cancel')) + '">&times;</button>' +
            (c.modalTitle ? '<h2 class="gift-selection-modal__title">' + esc(c.modalTitle) + '</h2>' : '') +
            '<div class="product-buy__picker-tools">' +
              (c.showSearch ? '<input type="search" class="product-buy__picker-search" placeholder="' + esc(this.sys('system.search')) + '" aria-label="' + esc(this.sys('system.search')) + '">' : '') +
              '<select class="product-buy__picker-sort" aria-label="' + esc(this.sys('system.sort_by')) + '">' +
                sorts.map((s) => '<option value="' + s[0] + '"' + (s[0] === sort ? ' selected' : '') + '>' + esc(this.sys(s[1])) + '</option>').join('') + '</select>' +
            '</div>' +
            '<div class="product-buy__picker-grid">' + cards() + '</div></div>';
        document.body.appendChild(modal);
        requestAnimationFrame(() => modal.classList.add('gift-selection-modal--open'));
        const close = () => modal.remove();
        const grid = modal.querySelector('.product-buy__picker-grid');
        const redraw = () => { grid.innerHTML = cards(); };
        modal.querySelector('.gift-selection-modal__overlay').addEventListener('click', close);
        modal.querySelector('.gift-selection-modal__close').addEventListener('click', close);
        const search = modal.querySelector('.product-buy__picker-search');
        if (search) search.addEventListener('input', () => { query = search.value; redraw(); });
        modal.querySelector('.product-buy__picker-sort').addEventListener('change', (e) => { sort = e.target.value; redraw(); });
        grid.addEventListener('click', (e) => {
          const btn = e.target.closest('.product-buy__picker-choose');
          if (!btn) return;
          const card = btn.closest('.product-buy__picker-card');
          const sel = card.querySelector('.product-buy__picker-variant');
          const v = this.pickVariant(card.dataset.product, null, sel ? 'gid://shopify/ProductVariant/' + sel.value : null);
          close();
          if (!v) return;
          this.kSlots = this.kSlots || [];
          this.kSlots[k] = v; // a chosen slot, even when it is this product's own variant
          this.render();
        });
      }

      bindBox(box, tier) {
        this.bindExtras(box, tier);
        box.querySelectorAll('[data-pick]').forEach((sel) => sel.addEventListener('change', () => this.onPick(sel.dataset.pick, sel.value, tier)));
        box.querySelectorAll('[data-bar-qty]').forEach((b) => b.addEventListener('click', () => {
          this.kQty = this.kQty || {};
          this.kQty[tier.barId] = Math.max(tier.bq, this.tierQty(tier) + parseInt(b.dataset.barQty, 10));
          this.afterQtyChange();
        }));
        const qi = box.querySelector('.product-buy__bar-qty-input');
        if (qi) qi.addEventListener('change', () => {
          const n = parseInt(qi.value, 10);
          this.kQty = this.kQty || {};
          this.kQty[tier.barId] = Math.max(tier.bq, isNaN(n) ? tier.bq : n);
          this.afterQtyChange();
        });
      }

      // The bar's quantity changed: prices, units, add-ons and personalisation fields follow.
      afterQtyChange() {
        this.render();
      }

      onPick(key, value, tier) {
        const st = this.kState(tier.barId);
        const [kind, id] = key.split(/:(.+)/);
        if (kind === 'bv') { st.bv = st.bv || {}; st.bv[id] = Number(value); this.render(); } else if (kind === 'mx') {
          const [pid, vid] = value.split(':').map(Number);
          st.mx = st.mx || {}; st.mx[id] = { pid: pid, vid: vid }; this.render();
        } else if (kind === 'bplan') { st.plan = Number(value); this.syncCardToBar(tier, st.plan); this.render(); } else if (kind === 'sv') { st.sv = Number(value); this.render(); } else if (kind === 'fg') { st.fg = st.fg || {}; st.fg[id] = Number(value); this.renderGifts(); this.renderSavings(); } else if (kind === 'up') {
          st.uv = st.uv || {}; st.uv[id] = Number(value); this.renderGifts(); this.renderSavings();
        } else if (kind === 'upplan') { st.up = st.up || {}; st.up[id] = Number(value); this.renderGifts(); this.renderSavings(); }
      }

      bindExtras(box, tier) {
        box.querySelectorAll('[data-slot-choose]').forEach((b) => b.addEventListener('click', () =>
          this.openSlotPicker(parseInt(b.closest('[data-slot]').dataset.slot, 10))));
        box.querySelectorAll('[data-slot-remove]').forEach((b) => b.addEventListener('click', () => {
          this.kSlots[parseInt(b.closest('[data-slot]').dataset.slot, 10)] = null;
          this.render();
        }));
        box.querySelectorAll('.product-buy__gift-input').forEach((input) => input.addEventListener('change', () => {
          const picks = this.giftPicks[tier.barId];
          const g = tier.gifts.find((x) => x.id === input.dataset.selector);
          let list = picks[g.id] || [];
          if (input.type === 'radio') list = [input.value];
          else if (input.checked) list = list.concat(input.value).slice(-g.max); // over the cap: drop the oldest pick
          else list = list.filter((x) => x !== input.value);
          picks[g.id] = list;
          this.renderGifts();
          this.renderSavings();
        }));
        box.querySelectorAll('.product-buy__upsell-input').forEach((input) => input.addEventListener('change', () => {
          this.upsellPicks[tier.barId][input.dataset.upsell] = input.checked;
          this.renderGifts();
          this.renderSavings();
        }));
        const toggle = box.querySelector('.product-buy__perso-toggle');
        if (toggle) toggle.addEventListener('change', () => {
          this.persoOn[tier.barId] = toggle.checked;
          this.renderGifts();
          const first = box.querySelector('.product-buy__perso-input');
          if (first) first.focus();
        });
        // Typing must not re-render (it would drop focus) — just store the value. Characters the
        // merchant's rules don't allow (letters / numbers only, a length cap) are dropped, with Kaching's message.
        box.querySelectorAll('.product-buy__perso-input').forEach((input) => input.addEventListener('input', () => {
          const rules = this.persoRules();
          let v = input.value;
          if (rules.letters !== rules.numbers) v = rules.letters ? v.replace(/\p{N}/gu, '') : v.replace(/\p{L}\p{M}*/gu, '');
          const rejected = v.length < input.value.length;
          if (rules.limit != null) v = v.slice(0, rules.limit);
          if (v !== input.value) input.value = v;
          const err = box.querySelector('.product-buy__perso-error');
          if (err) {
            err.textContent = rejected ? this.sys(rules.letters ? 'system.letters_only' : 'system.numbers_only') : '';
            err.hidden = !rejected;
          }
          this.persoValues[tier.barId][parseInt(input.dataset.i, 10)] = v;
          input.classList.remove('is-error');
        }));
      }

      // 1-based position of a bar in the deal (all bar types), as Kaching's progressive gifts count.
      barPosition(barId) {
        return this.kDeal ? this.kDeal.dealBars.findIndex((b) => b.id === barId) + 1 : 0;
      }

      progressiveUnlocked(g, pos) {
        return pos > 0 && (g.only ? pos === g.at : pos >= g.at);
      }

      // Kaching "progressive gifts": a row of tiles under the bars, unlocked by the checked bar.
      // A locked tile selects the bar that unlocks it (or the subscription card, for a gift that
      // needs a plan). Not drawn here when the merchant laid them out inside each bar.
      renderProgressive() {
        let el = this.querySelector('.product-buy__progressive');
        const pg = this.kProgressive;
        if (!pg || pg.dealBarLayout || !this.giftProducts || !this.kachingBars.length) { if (el) el.remove(); return; }
        el = this.afterSlot('product-buy__progressive');
        const esc = KB.esc;
        const checked = this.optionsEl.querySelector('.product-buy__bundle-radio:checked');
        const pos = checked && checked.dataset.barId ? this.barPosition(checked.dataset.barId) : 0;
        const plan = this.dealPlan();
        const lockedUrl = this.media(pg.lockedGID);
        const truck = TRUCK_ICON.replace('<svg ', '<svg class="product-buy__pg-icon" ');
        const lock = KB.LOCK_SVG.replace('<svg ', '<svg class="product-buy__pg-icon" ');
        const tiles = pg.gifts.map((g) => {
          const needsPlan = g.onlySub && !plan;
          const open = this.progressiveUnlocked(g, pos) && !needsPlan;
          if (!open && pg.hideLocked) return '';
          // Same product-vs-shipping test as getKachingGiftLines, so a tile and its cart line agree.
          const isProduct = g.type !== 'shipping';
          const p = isProduct ? this.kp(g.productGID) : null;
          const variants = isProduct && p && p.availableForSale ? KB.allowedVariants(p, g.variantGIDs) : [];
          if (isProduct && !variants.length) return '';
          const v = variants[0];
          const ctx = { money: this.moneyFn, product: p, selectedVariants: v ? [{ variant: v, quantity: g.qty }] : [], totalFull: v ? v.price * g.qty : 0, totalCompareAt: v ? v.price * g.qty : 0, totalDiscounted: 0, quantity: g.qty };
          const fill = (s) => KB.fill(s, ctx);
          const labels = open || pg.showLockedLabels
            ? '<span class="product-buy__pg-label">' + esc(fill(g.label)) + (g.crossed ? ' <s>' + esc(fill(g.crossed)) + '</s>' : '') + '</span>'
            : '';
          const productImg = this.media(g.mediaGID) || (v && (v.image || p.image)) || '';
          let media;
          if (!open) {
            if (lockedUrl) media = '<img src="' + esc(lockedUrl) + '" alt="" width="64" height="64" loading="lazy">';
            else if (pg.showLockedProducts && productImg) media = '<img class="is-locked-product" src="' + esc(productImg) + (productImg.indexOf('?') === -1 ? '?' : '&') + 'width=160" alt="" width="64" height="64" loading="lazy">';
            else media = lock;
          } else media = productImg ? '<img src="' + esc(productImg) + (productImg.indexOf('?') === -1 ? '?' : '&') + 'width=160" alt="" loading="lazy" width="64" height="64">' : truck;
          return '<button type="button" class="product-buy__pg-tile' + (open ? ' is-unlocked' : ' is-locked') + '" data-unlock-at="' + g.at + '"' + (needsPlan ? ' data-needs-plan' : '') + (open ? ' tabindex="-1"' : '') + '>' +
            labels + '<span class="product-buy__pg-media">' + media + '</span>' +
            '<span class="product-buy__pg-title">' + esc(open ? fill(g.title) : (g.locked || fill(g.title))) + '</span></button>';
        }).join('');
        if (!tiles) { el.remove(); return; }
        el.innerHTML = (pg.title ? '<p class="product-buy__pg-heading">' + esc(pg.title) + '</p>' : '') +
          (pg.subtitle ? '<p class="product-buy__pg-sub">' + esc(pg.subtitle) + '</p>' : '') +
          '<div class="product-buy__pg-tiles">' + tiles + '</div>';
        el.querySelectorAll('.product-buy__pg-tile.is-locked').forEach((tile) => tile.addEventListener('click', () => {
          if (tile.hasAttribute('data-needs-plan')) {
            const scope = this.closest('.product-main') || document;
            const sub = scope.querySelector('.product-buy__type-radio[value="subscribe"]');
            if (sub && !sub.checked) { sub.checked = true; const purchase = scope.querySelector('product-purchase'); if (purchase && purchase.syncType) purchase.syncType(); }
            return;
          }
          const bar = this.kDeal.dealBars[parseInt(tile.dataset.unlockAt, 10) - 1];
          const radio = bar && this.optionsEl.querySelector('.product-buy__bundle-radio[data-bar-id="' + bar.id + '"]:not(:disabled)');
          if (radio) { radio.checked = true; radio.dispatchEvent(new Event('change', { bubbles: true })); }
        }));
      }

      // ---------------------------------------------------------------------------------------
      // Deal-level checkbox upsells under the bars.
      // ---------------------------------------------------------------------------------------
      // The bar quantity the checked radio adds (what "match quantity with the deal bar" follows).
      barQty() {
        const c = this.optionsEl.querySelector('.product-buy__bundle-radio:checked');
        return c ? parseInt(c.dataset.addQty || c.value, 10) || 1 : 1;
      }

      checkboxQty(u) {
        this.cbQty = this.cbQty || {};
        if (this.cbQty[u.id]) return this.cbQty[u.id];
        return u.matchQty ? this.barQty() : 1;
      }

      // A checkbox upsell's units, as [{variant, quantity}], from its per-unit variant picks.
      checkboxUnits(u, product, qty) {
        const list = KB.allowedVariants(product, u.variantGIDs);
        if (!list.length) return null;
        const pref = [u.defaultVariantGID].concat(u.variantGIDs || []).filter(Boolean).map(KB.numId);
        let def = null;
        for (let i = 0; i < pref.length && !def; i++) def = list.find((v) => v.id === pref[i]);
        def = def || list[0];
        this.cbVariants = this.cbVariants || {};
        const picks = this.cbVariants[u.id] || [];
        const vq = [];
        for (let k = 0; k < qty; k++) {
          const v = list.find((x) => x.id === picks[k]) || def;
          const hit = vq.find((x) => x.variant.id === v.id);
          if (hit) hit.quantity += 1; else vq.push({ variant: v, quantity: 1 });
        }
        return { list: list, vq: vq, def: def };
      }

      // Priced checkbox upsell, per Kaching: units priced together, one plan for all.
      checkboxOffer(u, index) {
        const product = this.addonProduct(u, index);
        if (!product || !product.availableForSale) return null;
        const qty = this.checkboxQty(u);
        const units = this.checkboxUnits(u, product, qty);
        if (!units) return null;
        const first = units.vq[0].variant;
        this.cbPlan = this.cbPlan || {};
        const plan = this.addonPlan(u, product, first, this.cbPlan[u.id]);
        const total = KB.discountedTotal({
          discountType: u.dtype, discountValue: u.dval, discountQuantity: 1, variantQuantities: units.vq, currencyRate: this.kRate,
          priceRounding: this.kRounding, sellingPlan: plan,
        });
        const full = units.vq.reduce((a, x) => a + Math.max(x.variant.price, x.variant.compareAtPrice || 0) * x.quantity, 0);
        return { product: product, qty: qty, units: units, plan: plan, price: total, full: full, first: first };
      }

      renderCheckboxUpsells() {
        const cu = this.kCheckboxUpsells;
        let el = this.querySelector('.product-buy__checkbox-upsells');
        // Independent of which bar is picked, but only while the deal card is on screen.
        // Not on our own n+1 / kiekinis tiers: their add can't carry a bar-less __kaching_bundles group.
        const own = this.optionsEl.querySelector('.product-buy__bundle-radio:checked:not([data-bar-id]):not([data-base])');
        if (!cu || !this.giftProducts || !this.kachingBars.length || !this.tiers.length || own) { if (el) el.remove(); return; }
        this.cbPicks = this.cbPicks || Object.fromEntries(cu.upsells.map((u) => [u.id, u.preselected]));
        const esc = KB.esc;
        this.cbOffers = {};
        const rows = cu.upsells.map((u, index) => {
          const o = this.checkboxOffer(u, index);
          if (!o) return '';
          this.cbOffers[u.id] = o;
          const on = !!this.cbPicks[u.id];
          const ctx = { money: this.moneyFn, product: o.product, selectedVariants: o.units.vq, totalFull: o.full, totalCompareAt: o.full, totalDiscounted: o.price,
            quantity: o.qty, sellingPlan: o.plan, unitQuantity: null };
          const fill = (s) => KB.fill(s, ctx);
          const showOpts = on || !u.optionsOnlyWhenSelected;
          const imgUrl = this.addonImage(u.mediaGID, { variant: o.first, product: o.product });
          const img = imgUrl ? this.imgTag(imgUrl, 48) : '';
          const badges = KB.badgesFor(this.kDeal, { type: 'checkboxUpsell', upsellId: u.id },
            { id: u.id, badgeStyle: u.badgeStyle, badgeText: u.badgeText, badgeImageGID: u.badgeImageGID })
            .map((b) => Object.assign({}, b, { text: KB.fill(this.kT(b.text), ctx) }));
          const bbox = KB.badgeBoxAttrs(badges);
          // Variant select: one per unit when there is more than one unit.
          let opts = '';
          if (showOpts && o.units.list.length > 1) {
            const per = o.qty > 1 ? o.qty : 1;
            const picks = this.cbVariants && this.cbVariants[u.id] || [];
            let selects = '';
            for (let k = 0; k < per; k++) {
              const cur = (picks[k] != null && o.units.list.find((x) => x.id === picks[k])) ? picks[k] : o.units.def.id;
              selects += '<select data-cb-variant="' + k + '" aria-label="' + esc(o.product.title + (per > 1 ? ' #' + (k + 1) : '')) + '">' + this.variantOptions(o.units.list, cur) + '</select>';
            }
            opts += '<div class="product-buy__cb-options">' + selects + '</div>';
          }
          const plans = u.sub && u.subType === 'always' ? KB.plansOf(o.product, [o.first]) : [];
          if (showOpts && plans.length > 1) {
            opts += '<div class="product-buy__cb-options"><select data-cb-plan aria-label="' + esc(this.sys('system.subscription_plan')) + '">' + this.planOptions(plans, o.plan && o.plan.id) + '</select></div>';
          }
          // The stepper shows whenever the options do, even next to "match the deal quantity" (as Kaching does).
          const stepper = showOpts && u.qtySelector
            ? '<span class="product-buy__cb-qty"><button type="button" data-cb-step="-1" aria-label="' + esc(this.sys('system.quantity')) + ' -"' + (o.qty <= 1 ? ' disabled' : '') + '>−</button><span>' + o.qty +
              '</span><button type="button" data-cb-step="1" aria-label="' + esc(this.sys('system.quantity')) + ' +">+</button></span>' : '';
          const hasPrice = o.price > 0 || o.full > 0;
          return '<div class="product-buy__cb-row' + (bbox.cls ? ' ' + bbox.cls : '') + '" data-cb="' + esc(u.id) + '" style="' + esc(bbox.style) + '">' +
            badges.map((b) => KB.badgeHTML(b, this.kDeal, (g) => this.media(g))).join('') +
            '<label class="product-buy__gift-opt product-buy__upsell' + (on ? ' is-active' : '') + '">' +
              '<input class="product-buy__upsell-input" type="checkbox" form="kaching-gift-none" data-cb-input' + (on ? ' checked' : '') + '>' +
              '<span class="product-buy__check" aria-hidden="true"></span>' + img +
              '<span class="product-buy__gift-text">' +
                '<span class="product-buy__gift-name">' + esc(fill(u.title)) + '</span>' +
                (u.subtitle ? '<span class="product-buy__upsell-product">' + esc(fill(u.subtitle)) + '</span>' : '') +
                (hasPrice ? '<span class="product-buy__gift-prices">' + (o.full > o.price ? '<s>' + this.money(o.full) + '</s>' : '') +
                  '<span class="product-buy__upsell-price">' + this.money(o.price) + '</span></span>' : '') +
              '</span></label>' + opts + stepper + '</div>';
        }).join('');
        if (!rows) { if (el) el.remove(); return; }
        el = this.afterSlot('product-buy__checkbox-upsells');
        el.innerHTML = (cu.title ? '<p class="product-buy__gift-heading">' + esc(cu.title) + '</p>' : '') + rows;
        KB.layoutBorderBadges(el);
        el.querySelectorAll('[data-cb]').forEach((row) => {
          const id = row.dataset.cb;
          row.querySelector('[data-cb-input]').addEventListener('change', (e) => {
            this.cbPicks[id] = e.target.checked;
            this.renderCheckboxUpsells();
            this.renderSavings();
          });
          row.querySelectorAll('[data-cb-step]').forEach((b) => b.addEventListener('click', () => {
            const u = cu.upsells.find((x) => x.id === id);
            this.cbQty = this.cbQty || {};
            this.cbQty[id] = Math.max(1, this.checkboxQty(u) + parseInt(b.dataset.cbStep, 10));
            this.renderCheckboxUpsells();
            this.renderSavings();
          }));
          row.querySelectorAll('[data-cb-variant]').forEach((s) => s.addEventListener('change', () => {
            this.cbVariants = this.cbVariants || {};
            const arr = this.cbVariants[id] = (this.cbVariants[id] || []).slice();
            arr[parseInt(s.dataset.cbVariant, 10)] = Number(s.value);
            this.renderCheckboxUpsells();
            this.renderSavings();
          }));
          const plan = row.querySelector('[data-cb-plan]');
          if (plan) plan.addEventListener('change', () => {
            this.cbPlan = this.cbPlan || {};
            this.cbPlan[id] = Number(plan.value);
            this.renderCheckboxUpsells();
            this.renderSavings();
          });
        });
      }

      // Kaching sticky bar: once the card has scrolled above the viewport, a bottom bar with the
      // product and a button that scrolls back to the card. It never adds to cart (nor does Kaching's).
      renderSticky() {
        const st = this.kSticky;
        if (!st || !this.kachingBars.length) { if (this.stickyEl) { this.stickyEl.remove(); this.stickyEl = null; } return; }
        const esc = KB.esc;
        const checked = this.optionsEl.querySelector('.product-buy__bundle-radio:checked');
        const tier = checked && checked.dataset.barId && this.tiers.find((t) => t.barId === checked.dataset.barId);
        const total = checked ? parseInt(checked.dataset.totalCents, 10) || 0 : 0;
        const ctx = tier ? this.calcTier(tier).ctx : { money: this.moneyFn, product: this.pageProd, totalFull: total, totalDiscounted: total, quantity: checked ? parseInt(checked.value, 10) || 1 : 1 };
        const title = KB.fill(st.title, ctx);
        if (!this.stickyEl) {
          if (document.querySelector('.product-buy__sticky')) return; // one per page
          this.stickyEl = document.createElement('div');
          this.stickyEl.className = 'product-buy__sticky';
          this.stickyEl.setAttribute('aria-hidden', 'true');
          document.body.appendChild(this.stickyEl);
          let raf = 0;
          const onScroll = () => {
            if (raf) return;
            raf = requestAnimationFrame(() => {
              raf = 0;
              const show = !this.hidden && this.isConnected && this.getBoundingClientRect().bottom < 0;
              this.stickyEl.classList.toggle('is-visible', show);
              this.stickyEl.setAttribute('aria-hidden', show ? 'false' : 'true');
            });
          };
          window.addEventListener('scroll', onScroll, { passive: true });
          this.stickyEl.addEventListener('click', (e) => {
            if (!e.target.closest('.product-buy__sticky-button')) return;
            const top = this.getBoundingClientRect().top + window.scrollY - 100;
            window.scrollTo({ top: top, behavior: 'smooth' });
            // Smooth scrolling can be dropped (reduced motion, throttled tabs): make sure we arrive.
            setTimeout(() => { if (Math.abs(window.scrollY - top) > 50) window.scrollTo(0, top); }, 700);
          });
        }
        this.stickyEl.setAttribute('style', st.vars);
        const img = this.dataset.productImage;
        this.stickyEl.innerHTML = '<div class="product-buy__sticky-inner">' +
          (img ? '<img src="' + esc(img) + '" alt="" width="40" height="40" loading="lazy">' : '') +
          '<span class="product-buy__sticky-title">' + esc(title) + '</span>' +
          '<button type="button" class="product-buy__sticky-button">' + esc(st.button || '') + '</button></div>';
      }

      // Kaching "savings summary": one line under the bars for the checked bar's saving.
      renderSavings() {
        let el = this.querySelector('.product-buy__bundle-savings');
        const ss = this.kSavings;
        if (!ss || !ss.title) { if (el) el.remove(); return; }
        el = this.afterSlot('product-buy__bundle-savings', 'div');
        const checked = this.optionsEl.querySelector('.product-buy__bundle-radio:checked');
        const barId = checked && checked.dataset.barId;
        const tier = barId && this.tiers.find((t) => t.barId === barId);
        const c = tier ? this.calcTier(tier) : null;
        let saved = checked ? parseInt(checked.dataset.savedCents, 10) || 0 : 0;
        // Ticked upsells of the checked bar count too (as in Kaching); personalisation doesn't. Free
        // gifts count only when the merchant asks for "include gift value".
        const ups = tier && (this.upsellPicks || {})[tier.barId];
        const st = tier ? this.kState(tier.barId) : {};
        const sets = tier ? this.setsOf(tier, c) : 1;
        if (tier && ups && this.giftProducts) tier.upsells.forEach((u) => {
          const o = ups[u.id] && this.addonOffer(u, u.index, u.qty * sets, (st.uv || {})[u.id], (st.up || {})[u.id]);
          if (o) saved += Math.max(0, o.full - o.price);
        });
        if (this.kCheckboxUpsells && this.cbPicks && this.giftProducts) this.kCheckboxUpsells.upsells.forEach((u, i) => {
          const o = this.cbPicks[u.id] && this.checkboxOffer(u, i);
          if (o) saved += Math.max(0, o.full - o.price);
        });
        if (ss.includeGift && tier && this.giftProducts) saved += this.giftValue(tier, c);
        el.hidden = ss.hideZero && saved <= 0;
        const s = ss.s;
        const align = { start: 'flex-start', center: 'center', end: 'flex-end' }[s.titleAlignment];
        el.setAttribute('style', KB.vars({
          'kb-sv-bg': KB.rgba(s.backgroundColor), 'kb-sv-color': KB.rgba(s.textColor), 'kb-sv-value': KB.rgba(s.valueColor), 'kb-sv-icon': KB.rgba(s.iconColor),
          'kb-sv-border-color': KB.rgba(s.borderColor), 'kb-sv-align': align || '', 'kb-sv-border-width': s.borderWidth != null ? s.borderWidth + 'px' : '',
          'kb-sv-radius': s.borderRadius != null ? s.borderRadius + 'px' : '', 'kb-sv-size': s.titleFont && s.titleFont.size ? s.titleFont.size + 'px' : '',
          'kb-sv-weight': s.titleFont ? KB.fontWeight(s.titleFont.style) : '', 'kb-sv-style': s.titleFont ? KB.fontStyle(s.titleFont.style) : '',
        }));
        // Only the saving is substituted here; each value is highlighted like Kaching's.
        const ctx = { money: this.moneyFn, quantity: 1, totalFull: saved, totalCompareAt: saved, totalDiscounted: 0, product: this.pageProd, wrap: (v) => '\u0001' + v + '\u0002' };
        const text = KB.fill(ss.title, ctx);
        const url = ss.iconType === 'custom' ? this.media(ss.iconGID) : '';
        const icon = url ? '<img src="' + KB.esc(url) + '" alt="">' : (KB.ICONS[ss.iconType || 'checkmark'] || '');
        el.innerHTML = (icon ? '<span class="product-buy__savings-icon">' + icon + '</span>' : '') +
          '<span class="product-buy__savings-title">' + KB.esc(text).replace(/\u0001/g, '<strong>').replace(/\u0002/g, '</strong>') + '</span>';
      }

      // Value of the free / progressive product gifts of the checked bar (savings "include gift value").
      giftValue(t, c) {
        let sum = 0;
        const sets = this.setsOf(t, c);
        this.freeGiftRows(t, c).forEach((r) => {
          if (!r.v || !r.g.showPrice) return;
          sum += Math.max(r.v.price, r.v.compareAtPrice || 0) * r.g.qty * sets;
        });
        const pg = this.kProgressive;
        if (pg) {
          const pos = this.barPosition(t.barId);
          const plan = this.dealPlan();
          pg.gifts.forEach((g) => {
            if (!this.progressiveUnlocked(g, pos) || (g.onlySub && !plan) || g.type === 'shipping') return;
            const p = this.kp(g.productGID);
            const v = p && p.availableForSale ? KB.allowedVariants(p, g.variantGIDs)[0] : null;
            if (v) sum += v.price * g.qty;
          });
        }
        return sum;
      }

      // ---------------------------------------------------------------------------------------
      // Cart lines.
      // ---------------------------------------------------------------------------------------
      abTest() {
        const d = this.kDeal || {};
        const ab = d.abTestVariantLetter != null ? d.abTestVariantLetter : (d.abTestVariantNumber ? String.fromCharCode(64 + d.abTestVariantNumber) : undefined);
        return { abid: d.abTestVariantId || undefined, ab: ab || undefined };
      }

      // Does the bar's discount fix a price per plan ("specific")? Then the plan rides in the JSON (Kaching's jg()).
      barSpecific(t) {
        if (!t) return true;
        const r = t.raw;
        if (t.type === 'qb') return r.discountType === 'specific';
        if (t.type === 'bxgy') return r.buyDiscountType === 'specific';
        if (t.type === 'bundle') return !!(r.bundleProducts[0] && r.bundleProducts[0].discountType === 'specific');
        if (t.type === 'mix') return r.discountType === 'specific';
        return false;
      }

      // The loaded product a variant id belongs to (Kaching product shape).
      productOfVariant(variantId) {
        const id = Number(variantId);
        const gids = Object.keys(this.kProd);
        for (let i = 0; i < gids.length; i++) if (this.kProd[gids[i]].variants.some((v) => v.id === id)) return this.kProd[gids[i]];
        return null;
      }

      // The plan to buy a product line under, given the shopper's own choice on the page.
      linePlan(product, variant) {
        if (!product) return undefined;
        return KB.planFor(product, variant, this.dealPlan());
      }

      // Everything that rides along with the checked bar besides the bar's own units: gifts,
      // upsells, checkbox upsells, personalisation, collection slots. Lines are in Kaching's order.
      collectAddOns(tier, c) {
        const deal = this.kDeal;
        const lines = [];
        const rever = this.kFlags.rever_non_returnable ? { rever_non_returnable: 'true' } : {};
        const sets = tier ? this.setsOf(tier, c) : 1;
        const plan = this.dealPlan();
        let shipping = false;
        let mainQty = null;
        let blocked = null;
        let blockedColors = null;
        const sell = (product, variant) => {
          const p = this.linePlan(product, variant);
          return p || undefined;
        };
        const barId = tier ? tier.barId : null;
        if (tier) {
          // Multi-gift selector picks (variant ids): one gift unit each.
          const picks = (this.giftPicks || {})[barId] || {};
          (tier.gifts || []).forEach((g) => (picks[g.id] || []).forEach((v) => {
            const p = this.productOfVariant(v);
            const variant = p && p.variants.find((x) => x.id === Number(v));
            lines.push({ variantId: Number(v), qty: 1, props: { gift: String(v) }, gift: true, extra: rever, plan: p && variant ? sell(p, variant) : undefined });
          }));
          // Free gifts of the bar.
          this.freeGiftRows(tier, c).forEach((r) => {
            if (!r.v) return; // shipping: Kaching's discount grants it from the main line
            lines.push({ variantId: r.v.id, qty: r.g.qty * sets, props: { gift: r.g.id }, gift: true, extra: rever, plan: sell(r.p, r.v) });
          });
        }
        // Progressive gifts unlocked by this bar. Free shipping adds no line — Kaching's discount
        // grants it from the main line — but it does need the main line's deal properties.
        if (this.kProgressive && tier) {
          const pos = this.barPosition(barId);
          this.kProgressive.gifts.forEach((g) => {
            if (!this.progressiveUnlocked(g, pos) || (g.onlySub && !plan)) return;
            if (g.type === 'shipping') { shipping = true; return; }
            const p = this.kp(g.productGID);
            const v = p && p.availableForSale ? KB.allowedVariants(p, g.variantGIDs)[0] : null;
            if (v) lines.push({ variantId: v.id, qty: g.qty, props: { gift: g.id }, gift: true, extra: rever, plan: sell(p, v) });
          });
        }
        // Deal-level checkbox upsells: {…, checkboxUpsell: <id>}, one line per distinct variant.
        if (this.kCheckboxUpsells && this.cbPicks && this.giftProducts) this.kCheckboxUpsells.upsells.forEach((u, i) => {
          if (!this.cbPicks[u.id]) return;
          const o = this.checkboxOffer(u, i);
          if (!o) return;
          o.units.vq.forEach((x) => {
            const props = { checkboxUpsell: u.id };
            if (o.plan && o.plan.priceAdjustment && u.dtype === 'specific') props.sellingPlan = { id: o.plan.id, priceAdjustment: o.plan.priceAdjustment };
            lines.push({ variantId: x.variant.id, qty: x.quantity, props: props, plan: o.plan });
          });
        });
        // Collection-break slots swapped for other products: top-level lines (no parent_id)
        // {…, collectionBreaksProduct: true}, one per distinct variant; the main line keeps the
        // remaining units (unchosen slots are this product, when the merchant fills them in).
        if (tier && this.kCollection && tier.qtyBased && this.tierQty(tier) > 1 && this.kSlots) {
          const qty = this.tierQty(tier);
          const others = {};
          let mainUnits = 1;
          let chosen = 1;
          for (let k = 1; k < qty; k++) {
            const s = this.kSlots[k];
            if (!s) { if (this.kCollection.autoFill) mainUnits++; continue; }
            chosen++;
            if (s.id === Number(this.variantId)) mainUnits++;
            else others[s.id] = (others[s.id] || 0) + 1;
          }
          if (this.kCollection.requireAll && chosen < qty) {
            blocked = this.kCollection.requireAlert || this.sys('system.select_variant_alert');
            blockedColors = this.kCollection.requireColors;
          }
          if (Object.keys(others).length || mainUnits !== qty) {
            mainQty = mainUnits;
            Object.keys(others).forEach((vid) => {
              const p = this.productOfVariant(vid);
              const v = p && p.variants.find((x) => x.id === Number(vid));
              const props = { collectionBreaksProduct: true };
              if (tier.barKey === 'bar') props.bar = barId;
              lines.unshift({ variantId: Number(vid), qty: others[vid], props: props, top: true, plan: p && v ? sell(p, v) : undefined });
            });
          }
        }
        // Bar upsells (ticked, bar selected).
        if (tier) {
          const ups = (this.upsellPicks || {})[barId] || {};
          const st = this.kState(barId);
          (tier.upsells || []).forEach((u) => {
            if (!ups[u.id]) return;
            const o = this.addonOffer(u, u.index, u.qty * sets, (st.uv || {})[u.id], (st.up || {})[u.id]);
            if (!o) return;
            const props = { upsell: u.id };
            if (o.plan && o.plan.priceAdjustment && u.dtype === 'specific') props.sellingPlan = { id: o.plan.id, priceAdjustment: o.plan.priceAdjustment };
            lines.push({ variantId: o.variant.id, qty: u.qty * sets, props: props, plan: o.plan });
          });
        }
        const p = tier && tier.perso;
        if (p && (p.required || (this.persoOn || {})[barId])) {
          const qty = tier.qtyBased ? this.tierQty(tier) : 1;
          const vals = ((this.persoValues || {})[barId] || []).slice(0, qty).map((s) => String(s || '').trim());
          const filled = vals.filter(Boolean);
          if (p.required && filled.length < qty) {
            this.optionsEl.querySelectorAll('[data-gifts-for="' + barId + '"] .product-buy__perso-input').forEach((input) => {
              if (!input.value.trim()) input.classList.add('is-error');
            });
            blocked = this.kPersoAlert || p.title;
            blockedColors = null; // the personalisation message is not the collection-break alert
          }
          // `_kaching_upsell: "no-loyalty"` keeps kika-extended-discount's loyalty / Kennel %
          // off the add-on (Kaching prices it itself); it reuses the one line attribute that
          // function's input query can afford (IO-5815, see its run.js NO_LOYALTY_MARKER).
          filled.forEach((text) => lines.push({
            variantId: p.variantId, qty: 1, props: { personalisation: p.id },
            extra: { [p.valueLabel]: text, _kaching_upsell: 'no-loyalty' },
          }));
        }
        return { lines: lines, shipping: shipping, mainQty: mainQty, blocked: blocked, blockedColors: blockedColors, deal: deal };
      }

      // IO-5815: cart lines for the checked bar's add-ons, in the shape Kaching's own widget
      // posts (its Eg()), so its function prices them: the main line carries {deal, pp, main, v, id,
      // bid|bar}; every add-on line {id, deal, v} + one of gift / upsell / checkboxUpsell /
      // personalisation / collectionBreaksProduct, plus parent_id, in Kaching's order. `id` only
      // groups the lines (random in the widget too, no signature).
      // Returns null when nothing beyond a plain qty line is needed -> the normal add path (Kaching's
      // own interceptor decorates it); {blocked: message} when the add must wait for the shopper
      // (required personalisation / variant / collection slots); an object with `main` + `lines` when
      // the form's own line carries the bar; with `items` when the bar is not a line of this product
      // (bundle / mix-and-match / sku) and the whole request is ours.
      getKachingGiftLines(radio) {
        const deal = this.kDeal;
        if (!deal || !radio) return null;
        const barId = radio.dataset.barId;
        const tier = (barId && this.tiers.find((t) => t.barId === barId)) || null;
        // A bar that needs a variant the shopper has not picked yet (our picker owns that choice).
        if (tier && tier.requireVariant && this.pageProd.variants.length > 1 && !this.kVariantPicked && !this.variantInUrl()) {
          return { blocked: this.blockMsg('') };
        }
        const c = tier ? this.calcTier(tier) : null;
        // Our own n+1 / kiekinis tiers carry no Kaching group at all (their discount is native).
        if (!tier && !radio.hasAttribute('data-base')) return null;
        const add = this.collectAddOns(tier, c);
        if (add.blocked) return { blocked: this.blockMsg(add.blocked), colors: add.blockedColors || null };
        const ab = this.abTest();
        const chars = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz';
        let group = '';
        for (let i = 0; i < 4; i++) group += chars[Math.floor(Math.random() * chars.length)];
        const base = { id: group, deal: deal.nanoId };
        if (deal.versionId) base.v = deal.versionId;
        if (ab.abid) base.abid = ab.abid;
        if (ab.ab) base.ab = ab.ab;
        const mainProps = (variantProduct, pp) => {
          const m = { deal: deal.nanoId, pp: pp, main: true };
          if (deal.marketId) m.market = deal.marketId;
          if (deal.versionId) m.v = deal.versionId;
          m.id = group;
          if (tier) m[tier.barKey] = barId;
          if (ab.abid) m.abid = ab.abid;
          if (ab.ab) m.ab = ab.ab;
          return m;
        };
        const pp = Number(this.dataset.productId);
        const meta = {
          skipCart: !!deal.skipCart,
          split: !!this.kFlags.split_quantity_break_items && tier && tier.type === 'qb',
          noNest: !!this.kFlags.disable_nested_cart_lines,
          nonce: Math.random().toString(16).slice(2),
        };
        // Non-quantity bars: the request is built here, line by line.
        if (tier && !tier.qtyBased) return this.buildBarItems(tier, c, add, base, mainProps(null, pp), meta);
        // Quantity bars (and our own tiers): the form's line stays the main line.
        let lines = add.lines;
        let mainQty = add.mainQty;
        const pv = this.pageVariant();
        // Kaching splits a bxgy bar into a "buy" and a "get" line when the product has selling plans.
        const bxgySplit = !!(tier && tier.type === 'bxgy' && (pv.sellingPlans.length || this.kFlags.split_bxgy_items));
        if (bxgySplit) {
          const split = KB.bxgySplit(c.vq, Number(tier.raw.getQuantity) * Math.floor(c.qty / tier.qty));
          const getQty = split.get.reduce((a, x) => a + x.quantity, 0);
          if (getQty > 0) {
            mainQty = (mainQty != null ? mainQty : c.qty) - getQty;
            const rever = this.kFlags.rever_non_returnable ? { rever_non_returnable: 'true' } : {};
            lines = lines.slice();
            const getProps = { main: true, bxgy: true, bar: barId };
            const getPlan = this.linePlan(this.pageProd, pv);
            // A "specific" get price rides with the plan on its line, as in Kaching.
            if (getPlan && getPlan.priceAdjustment && tier.raw.getDiscountType === 'specific') getProps.sellingPlan = { id: getPlan.id, priceAdjustment: getPlan.priceAdjustment };
            lines.unshift({ variantId: pv.id, qty: getQty, props: getProps, top: true, extra: rever, plan: this.linePlan(this.pageProd, pv), bxgy: true });
          }
        }
        // A bar with its own selling plan is always an explicit add (the plan rides on the main line).
        const barPlan = tier ? this.tierPlan(tier) : undefined;
        if (!lines.length && !add.shipping && !barPlan) return null;
        const main = mainProps(this.pageProd, pp);
        // A split bxgy bar is keyed by `bar` with a group id, as in Kaching (dm() is true for it).
        if (bxgySplit) { delete main.bid; main.bar = barId; main.id = group; }
        // Without add-ons the main line carries just the bar (Kaching groups lines with an id only when it must).
        if (!lines.length && !add.shipping && tier.barKey !== 'bar' && !bxgySplit) delete main.id;
        const mainPlan = this.linePlan(this.pageProd, pv);
        if (mainPlan && mainPlan.priceAdjustment && this.barSpecific(tier)) main.sellingPlan = { id: mainPlan.id, priceAdjustment: mainPlan.priceAdjustment };
        return Object.assign({
          main: main,
          hasGift: lines.some((l) => l.gift),
          mainQty: mainQty, // set when collection-break slots moved units off the main line
          mainPlanId: barPlan && mainPlan ? mainPlan.id : null, // the bar's own plan overrides the purchase card's
          lines: lines.map((l) => ({
            variantId: l.variantId, qty: l.qty, props: Object.assign({}, base, l.props), extra: l.extra || null, top: !!l.top,
            sellingPlan: l.plan ? l.plan.id : null,
          })),
        }, meta);
      }

      // Never an empty message: the add stops, so the shopper must be told something.
      blockMsg(m) {
        return m || this.sys('system.select_variant_alert') || this.kPersoAlert || '!';
      }

      variantInUrl() {
        try { return new URL(window.location.href).searchParams.has('variant'); } catch (e) { return false; }
      }

      // bundle / mix-and-match / sku: the whole add. Top-level lines first (reversed), then the
      // lines nested under the main one, as Kaching orders them.
      buildBarItems(tier, c, add, base, mainMeta, meta) {
        const deal = this.kDeal;
        const items = [];
        const native = (p) => !!(p && p.isNativeBundle);
        const entries = tier.type === 'sku'
          ? [{ id: null, product: c.sel.product, variant: c.sel.variant, quantity: 1 }]
          : tier.raw.bundleProducts.filter((bp) => bp.productGID && c.sel[bp.id]).map((bp) => ({
            id: bp.id, product: c.sel[bp.id].product, variant: c.sel[bp.id].variant, quantity: c.sel[bp.id].quantity,
          }));
        if (!entries.length) return null;
        const first = entries[0];
        const parent = !this.kFlags.disable_nested_cart_lines && !native(first.product) && tier.type !== 'mix' ? first.variant.id : undefined;
        const mixCount = tier.type === 'mix' ? tier.raw.bundleProducts.length : undefined;
        const lineFor = (variant, quantity, props, extra, plan, product) => {
          const it = { id: variant.id, quantity: quantity, properties: Object.assign({ __kaching_bundles: JSON.stringify(props) }, extra || {}) };
          if (plan) it.selling_plan = plan.id;
          if (parent && parent !== variant.id && !native(product)) it.parent_id = parent;
          return it;
        };
        const mainP = Object.assign({}, mainMeta);
        if (mixCount) mainP.mixAndMatchItems = mixCount;
        const firstPlan = this.linePlan(first.product, first.variant);
        if (firstPlan && firstPlan.priceAdjustment && this.barSpecific(tier)) mainP.sellingPlan = { id: firstPlan.id, priceAdjustment: firstPlan.priceAdjustment };
        const mainItem = { id: first.variant.id, quantity: first.quantity, properties: { __kaching_bundles: JSON.stringify(mainP) } };
        if (firstPlan) mainItem.selling_plan = firstPlan.id;
        items.push(mainItem);
        entries.slice(1).forEach((e) => {
          const props = Object.assign({}, base, { bar: tier.barId, bundleProduct: e.id });
          if (mixCount) props.mixAndMatchItems = mixCount;
          const plan = this.linePlan(e.product, e.variant);
          if (plan && plan.priceAdjustment && tier.type === 'bundle') {
            const bp = tier.raw.bundleProducts.find((x) => x.id === e.id);
            if (bp && bp.discountType === 'specific') props.sellingPlan = { id: plan.id, priceAdjustment: plan.priceAdjustment };
          }
          items.push(lineFor(e.variant, e.quantity, props, null, plan, e.product));
        });
        add.lines.forEach((l) => {
          const props = Object.assign({}, base, l.props);
          if (l.top) { delete props.bar; }
          const p = this.productOfVariant(l.variantId);
          const variant = p && p.variants.find((x) => x.id === l.variantId);
          const it = lineFor(variant || { id: l.variantId }, l.qty, props, l.extra, l.plan, p);
          if (l.top) delete it.parent_id;
          items.push(it);
        });
        const top = items.filter((it) => !it.parent_id).reverse();
        const nested = items.filter((it) => it.parent_id);
        return Object.assign({
          items: top.concat(nested),
          hasGift: add.lines.some((l) => l.gift),
        }, meta);
      }

      // Whether the add should go on to checkout (deal setting "skip cart"): only when a Kaching bar
      // is the picked tier, not one of our own.
      skipCartEnabled(radio) {
        return !!(this.kDeal && this.kDeal.skipCart && radio && radio.dataset.barId);
      }

      // "Skip cart": the deal's earlier lines (cart keys, read BEFORE the add) leave the cart once the
      // new add is confirmed, so a repeat buy doesn't double up and a failed add loses nothing.
      dealCartKeys() {
        const deal = this.kDeal;
        if (!deal) return Promise.resolve([]);
        const key = deal.nanoId || deal.id;
        const pp = Number(this.dataset.productId);
        const parse = (it) => { try { return JSON.parse(it.properties && it.properties.__kaching_bundles); } catch (e) { return null; } };
        return fetch('/cart.js', { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
          .then((r) => (r.ok ? r.json() : null))
          .then((cart) => {
            if (!cart) return [];
            const keys = new Set();
            const groups = new Set();
            cart.items.forEach((it) => {
              const p = parse(it);
              if (p && p.deal === key && p.pp === pp) { keys.add(it.key); if (p.id) groups.add(p.id); }
            });
            if (!keys.size) return [];
            cart.items.forEach((it) => { const p = parse(it); if (p && p.id && groups.has(p.id)) keys.add(it.key); });
            return Array.from(keys);
          })
          .catch(() => []);
      }

      removeCartKeys(keys) {
        if (!keys || !keys.length) return Promise.resolve();
        const updates = {};
        keys.forEach((k) => { updates[k] = 0; });
        return fetch('/cart/update.js', {
          method: 'POST', headers: { 'Content-Type': 'application/json', 'X-Kaching-Cart-Ignore': '1' }, body: JSON.stringify({ updates: updates }),
        }).catch(() => {});
      }

      // Variant changed by the shopper: the n+1 and kiekinis tiers are per-variant (sku-gated), so
      // re-derive and rebuild (this also reprices the Kaching/kiekinis tiers against the new variant
      // price, and hides the card if the new variant has no n+1, kiekinis, or Kaching tier).
      // rebuild() reads kiekinisByVariant[this.variantId] directly, so setting this.variantId is enough.
      setVariant(variantId) {
        this.variantId = variantId;
        this.kVariantPicked = true; // an explicit pick satisfies "require variant selection"
        this.readNplus1(variantId);
        this.rebuild();
      }

      readNplus1(variantId) {
        const n = this.nplus1ByVariant[variantId] || {};
        this.nplus1Qty = parseInt(n.q, 10) || 0;
        // Free units can't reach the total (that would be a 0+N tier); fall back to one free.
        const free = parseInt(n.f, 10) || 1;
        this.nplus1Free = free < this.nplus1Qty ? free : 1;
      }
    }
    window.customElements.define('kaching-bundles-ui', KachingBundlesUI);
  }

  function initQty(root) {
    const widget = root.querySelector('[data-qty-widget]');
    if (!widget) return;
    const input = widget.querySelector('.product-buy__qty-input');
    const scope = root.closest('.product-main');
    const step = parseInt(input.getAttribute('step'), 10) || 1;
    const min = parseInt(input.getAttribute('min'), 10) || 1;
    // Mirror the stepper into the base ("single") tier's data-add-qty so product-form.js adds
    // the intended single quantity — Kaching clobbers the visible input on ATC, but not this.
    // Before the shopper actually touches the stepper, the intent is the minimum (1) — we must
    // NOT capture whatever Kaching may have preset into the visible input, or the OTP/sub price
    // and ATC qty would inherit the bundle tier size (e.g. 6). Only a real user change (which
    // sets data-userTouched via the input listener below) feeds the live stepper value in.
    const syncIntent = () => {
      const base = scope && scope.querySelector('.product-buy__bundle-radio[data-base]');
      if (!base) return;
      const v = widget.dataset.userTouched === '1' ? (parseInt(input.value, 10) || min) : min;
      base.dataset.addQty = String(v);
    };
    // Dispatch a bubbling input event so scope-level listeners (e.g. the OTP/sub price
    // recompute) react to +/- clicks, not just direct typing. syncIntent runs via the
    // input listener below, so it fires exactly once per change.
    const emitInput = () => input.dispatchEvent(new Event('input', { bubbles: true }));
    widget.querySelector('[name="minus"]').addEventListener('click', () => {
      input.value = Math.max(min, (parseInt(input.value, 10) || min) - step); emitInput();
    });
    widget.querySelector('[name="plus"]').addEventListener('click', () => {
      input.value = (parseInt(input.value, 10) || min) + step; emitInput();
    });
    input.addEventListener('input', () => { widget.dataset.userTouched = '1'; syncIntent(); });
    syncIntent();
  }

  // Kaching's app script (kaching-bundles.js) owns the bundle quantity: on the ATC click it writes
  // the chosen tier qty into the stepper itself. So while a bundle card is visible the manual
  // stepper is meaningless (and would visibly jump 1 -> tier on click) — hide it. When no bundle
  // card shows (no Kaching deals), the stepper stays and drives the quantity as normal.
  function syncBundleStepper(scope) {
    if (!scope) return;
    const card = scope.querySelector('kaching-bundles-ui:not([hidden])');
    const widget = scope.querySelector('[data-qty-widget]');
    if (!widget) return;
    // Hide the manual stepper only when a DISCOUNT tier is chosen — Kaching then writes that
    // tier's qty on ATC, so the stepper is meaningless. For the base "single" tier
    // (value === quantity_rule.min) keep it, so shoppers can pick how many singles. No card /
    // no deals -> always shown.
    let hide = false;
    if (card) {
      const qtyMin = parseInt(card.getAttribute('data-qty-min'), 10) || 1;
      const checked = card.querySelector('[name="bundles"]:checked');
      const val = checked ? parseInt(checked.value, 10) : qtyMin;
      // A bundle / mix-and-match / sku bar fixes its own units, so the manual stepper means nothing.
      hide = val > qtyMin || !!(checked && checked.hasAttribute('data-fixed-qty'));
      if (!hide) {
        // Base/single tier: undo Kaching's preset of the visible stepper to its bundle tier qty,
        // restoring the shopper's intended single quantity (kept on the base radio's data-add-qty).
        const base = card.querySelector('.product-buy__bundle-radio[data-base]');
        const input = widget.querySelector('.product-buy__qty-input');
        if (base && input && base.dataset.addQty) {
          pinBaseQty(scope, input, base.dataset.addQty);
        }
      }
    }
    widget.hidden = hide;
  }

  // Kaching presets the visible stepper to its bundle tier qty at load via a programmatic
  // input.value write (fires no event), which can land AFTER our restore and leave a base
  // ("single") tier visibly showing e.g. 6 (this was the "preselected 6 vnt" bug). Re-assert
  // the intended qty across a short window so our value gets the last word. Stop early once the
  // shopper touches the stepper (data-userTouched) or a discount tier is chosen (its qty
  // legitimately differs and the stepper is hidden anyway).
  function pinBaseQty(scope, input, intended) {
    const widget = input.closest('[data-qty-widget]');
    const apply = () => {
      if (widget && widget.dataset.userTouched === '1') return;
      const card = scope.querySelector('kaching-bundles-ui:not([hidden])');
      const checked = card && card.querySelector('[name="bundles"]:checked');
      const qtyMin = card ? (parseInt(card.getAttribute('data-qty-min'), 10) || 1) : 1;
      if (checked && (parseInt(checked.value, 10) || qtyMin) > qtyMin) return; // discount tier now
      if (input.value !== String(intended)) input.value = intended;
    };
    apply();
    requestAnimationFrame(apply);
    [60, 160, 360, 700, 1200].forEach((ms) => setTimeout(apply, ms));
  }

  function formatMoney(cents) {
    // Minimal EUR formatter for the client-side sub-price swap only (server money filter drives render).
    return '€' + (cents / 100).toFixed(2).replace('.', ',');
  }

  // The quantity the OTP/subscription totals should reflect: the qty actually being added.
  // When a DISCOUNT bundle tier is selected, Kaching drives the cart qty to that tier and the
  // manual stepper is hidden — so the tier qty wins. Otherwise (base tier / no bundles) it's the
  // manual stepper value. Mirrors syncBundleStepper's base-vs-discount split.
  function effectiveQty(scope) {
    const card = scope.querySelector('kaching-bundles-ui:not([hidden])');
    if (card) {
      const qtyMin = parseInt(card.getAttribute('data-qty-min'), 10) || 1;
      const checked = card.querySelector('[name="bundles"]:checked');
      if (checked) {
        const val = parseInt(checked.value, 10) || qtyMin;
        if (checked.hasAttribute('data-fixed-qty')) return val; // bundle / mix / sku bar: its own units
        if (val > qtyMin) return parseInt(checked.dataset.addQty, 10) || val; // discount tier: its own qty (plus the bar's stepper)
        // Base "single" tier: use the intended single-qty (data-add-qty), NOT the raw stepper.
        // Kaching presets the visible stepper to its bundle tier qty at load (a programmatic
        // value write that fires no event), so reading input.value here fed a stale qty (e.g. 6)
        // into the OTP/sub price — "Lojalumo kaina" showed 6× (€57,00 instead of €9,50).
        // data-add-qty tracks the stepper on real user input (syncIntent) but is immune to
        // Kaching's clobber, so it stays the shopper's true intent.
        const intended = parseInt(checked.dataset.addQty, 10);
        if (!isNaN(intended) && intended > 0) return intended;
      }
    }
    const input = scope.querySelector('.product-buy__qty-input');
    if (!input) return 1;
    // Floor at the input's own min, not a literal 1: pack-only SKUs (IO-4768) start at a full
    // pack, so a blank/unparseable value must fall back to the pack, not to a single unit.
    const qtyMin = parseInt(input.getAttribute('min'), 10) || 1;
    return Math.max(qtyMin, parseInt(input.value, 10) || qtyMin);
  }

  // Total price (cents) of the selected DISCOUNT/n+1 bundle tier, exactly as shown on the card,
  // else null. The base tier carries no bundle discount, so it returns null (OTP/sub use their
  // own price). Using the card's exact total (not a rounded per-unit × qty) keeps the OTP/sub
  // total penny-consistent with the bundle card — important for n+1, whose total (base × (qty-free))
  // does not divide evenly by qty.
  function bundleTotalCents(scope) {
    const card = scope.querySelector('kaching-bundles-ui:not([hidden])');
    if (!card) return null;
    const qtyMin = parseInt(card.getAttribute('data-qty-min'), 10) || 1;
    const checked = card.querySelector('[name="bundles"]:checked');
    if (!checked) return null;
    if (checked.hasAttribute('data-fixed-qty')) return null; // not a deal on this product's own units
    if (checked.hasAttribute('data-plan-priced')) return null; // priced under a subscription plan: not a one-time figure
    const val = parseInt(checked.value, 10) || qtyMin;
    if (val <= qtyMin) return null; // base tier: no bundle discount
    const t = parseInt(checked.dataset.totalCents, 10);
    return isNaN(t) ? null : t;
  }

  // Float an info tooltip over the cards, anchored to its icon. The pop is a child of
  // .product-buy__types (its positioned, non-clipped offset parent), placed above the icon when
  // there's room and below otherwise; --caret-x points the caret at the icon centre. Everything
  // is measured in the container's own coordinate space, so it tracks layout without scroll math.
  function positionInfoPop(btn, pop) {
    pop.classList.remove('product-buy__info-pop--below');
    pop.style.visibility = 'hidden';
    pop.removeAttribute('hidden'); // display it (still invisible) so it can be measured
    const anchor = pop.offsetParent || pop.parentElement;
    const cRect = anchor.getBoundingClientRect();
    const bRect = btn.getBoundingClientRect();
    const pRect = pop.getBoundingClientRect();
    const gap = 12;
    const pad = 10;
    const iconCx = bRect.left + bRect.width / 2 - cRect.left;
    let top = bRect.top - cRect.top - pRect.height - gap;
    let below = false;
    if (top < 0) { top = bRect.bottom - cRect.top + gap; below = true; } // no room above -> below
    const maxLeft = Math.max(pad, cRect.width - pRect.width - pad);
    let left = Math.max(pad, Math.min(iconCx - pRect.width / 2, maxLeft));
    const caretX = Math.max(16, Math.min(iconCx - left, pRect.width - 16));
    pop.style.top = top + 'px';
    pop.style.left = left + 'px';
    pop.style.setProperty('--caret-x', caretX + 'px');
    pop.classList.toggle('product-buy__info-pop--below', below);
    pop.style.visibility = '';
  }

  // Recompute the OTP (loyalty) and subscription prices for the effective qty.
  // Per-unit bases live in data-unit-price (server-rendered; sub base updates on freq change).
  //
  // The bundle tier's discount reaches the ONE-TIME price only. When a discount/n+1 tier is
  // selected, the OTP price is best-of on TOTALS: the smaller of its own per-unit × qty and the
  // tier's exact total (as shown on the card). Best-of on totals rather than a rounded per-unit
  // × qty keeps it penny-consistent with the bundle card — for a quantity-break tier the total
  // is unit×qty so this is unchanged, but for n+1 the total is base×(qty-free), which doesn't
  // divide evenly by qty (the 1¢ mismatch fix).
  //
  // The SUBSCRIPTION price never takes that best-of for OUR OWN tiers (kiekinis / discount_data,
  // n+1): their discount and the selling plan's discount do not stack at checkout. Subscribing on
  // such a tier adds its qty at the PLAN price, so the card shows plan-unit × qty. Best-of showed
  // whichever discount was deeper — on a 12-unit / -40% tier against a -30% plan that meant the
  // card advertised the bundle's €9,96 while the cart correctly charged the plan's €11,64.
  // Kaching bars are the opposite: their discount DOES stack on the plan price (bar discount on
  // top of the plan; Duo 2 × ceil(1749 × 0.85) = €29,74 at checkout), so the card shows the bar's
  // total under the chosen plan (<kaching-bundles-ui>.subTotalCents()).
  function updatePurchasePrices(scope) {
    if (!scope) return;
    const qty = effectiveQty(scope);
    const bundleTotal = bundleTotalCents(scope);
    // Deal setting "update native price: per item": while a Kaching bar is picked every card price (one-time,
    // "Įprasta kaina", subscription) reads per unit; our own n+1 / kiekinis / base tiers keep totals.
    const bcard = scope.querySelector('kaching-bundles-ui:not([hidden])');
    const bchk = bcard && bcard.querySelector('[name="bundles"]:checked');
    const perItem = !!(bcard && bcard.hasAttribute('data-native-item') && bchk && bchk.dataset.barId && qty > 0);
    const setPrice = (el, bundleApplies) => {
      if (!el || el.dataset.unitPrice == null) return null;
      const typeTotal = parseInt(el.dataset.unitPrice, 10) * qty;
      const shown = bundleApplies && bundleTotal != null ? Math.min(typeTotal, bundleTotal) : typeTotal;
      el.textContent = formatMoney(perItem ? Math.round(shown / qty) : shown);
      return shown;
    };
    const otpShown = setPrice(scope.querySelector('.product-buy__type--otp .product-buy__type-price'), true);
    // A checked Kaching bar discounts the plan price too (bar discount on top of the plan), so the subscription
    // card shows the bar's total under the chosen plan; our own tiers keep plan price x quantity.
    const subEl = scope.querySelector('.product-buy__sub-price');
    const subBar = bchk && bchk.dataset.barId && bcard.subTotalCents ? bcard.subTotalCents() : null;
    if (subEl && subBar != null) subEl.textContent = formatMoney(perItem && qty > 0 ? Math.round(subBar / qty) : subBar);
    else setPrice(subEl, false);
    // "Įprasta kaina" = undiscounted total for the effective qty (base × qty, NO bundle discount).
    // Shown only when the OTP price actually beats it — i.e. a unit-level loyalty discount and/or a
    // selected bundle tier — so it also surfaces for bundle-only products that have no unit discount.
    const reg = scope.querySelector('.product-buy__type--otp .product-buy__type-regular');
    if (reg && reg.dataset.unitRegular != null && otpShown != null) {
      const regularTotal = parseInt(reg.dataset.unitRegular, 10) * qty;
      const amt = reg.querySelector('.product-buy__regular-amount');
      if (amt) amt.textContent = formatMoney(perItem ? parseInt(reg.dataset.unitRegular, 10) : regularTotal);
      reg.hidden = !(otpShown < regularTotal);
    }
    syncLoyaltyInfo(scope);
    updateTypeHeader(scope);
  }

  // Figma review 1483:497 #6 — the loyalty (i) shows on any product that gets a discount. The
  // unit-level half is server-known (data-has-loyalty); the bundle half is not, because Kaching's
  // quantity-break deals resolve client-side, so mirror the bundle card's visibility here. Runs
  // from every updatePurchasePrices() caller, which already covers the card's [hidden] flip, tier
  // changes, a client-side re-render, variant refresh and init.
  function syncLoyaltyInfo(scope) {
    const info = scope.querySelector('.product-buy__type--otp .product-buy__info[data-info="otp"]');
    if (!info) return;
    const hasBundles = !!scope.querySelector('kaching-bundles-ui:not([hidden])');
    info.hidden = !(info.dataset.hasLoyalty === 'true' || hasBundles);
  }

  // The OTP "Lojalumo kaina" card header reflects the selected bundle tier's deal text
  // (data-header) — kiekinis discount_message, Kaching "Sutaupykite N%", or the n+1 "N+1". The
  // base "single" tier carries no data-header, so the header restores its server-rendered loyalty
  // default. `data-loyalty-default` caches that default the first time it's seen on a fresh
  // element (the purchase block is replaced wholesale on variant change, so it re-caches then).
  function updateTypeHeader(scope) {
    const el = scope.querySelector('.product-buy__type--otp .product-buy__type-header');
    if (!el) return;
    if (el.dataset.loyaltyDefault == null) el.dataset.loyaltyDefault = el.textContent.trim();
    const card = scope.querySelector('kaching-bundles-ui:not([hidden])');
    const checked = card && card.querySelector('[name="bundles"]:checked');
    const header = checked && checked.dataset.header ? checked.dataset.header : '';
    el.textContent = header || el.dataset.loyaltyDefault;
  }

  // N+1 ("buy N get 1 free", from custom.nplus1_qty + a native automatic BXGY discount) and the
  // subscription plan are mutually exclusive discounts — they can't stack at checkout. Reflect the
  // current selection into visibility: while subscription is selected the n+1 bundle tier(s) are
  // hidden, and while an n+1 tier is selected the subscription card is hidden. Pure reflection —
  // the two scope change-handlers below resolve a conflicting pick (reverting the losing side)
  // before calling this, so a user action never leaves both selected.
  function syncIncompat(scope) {
    const subRadio = scope.querySelector('.product-buy__type-radio[value="subscribe"]');
    const subCard = scope.querySelector('.product-buy__type--sub');
    const subActive = !!(subRadio && subRadio.checked);
    scope.querySelectorAll('.product-buy__bundle-radio[data-nplus1]').forEach((r) => {
      const opt = r.closest('.product-buy__bundle-opt');
      if (opt) opt.hidden = subActive;
    });
    const nplusChecked = !!scope.querySelector('.product-buy__bundle-radio[data-nplus1]:checked');
    if (subCard) subCard.hidden = nplusChecked;
  }

  // The integration metafields ship rich text padded with BLANK-LOOKING markup: <p>&nbsp;</p>,
  // <p><br></p>, <p><span> </span></p>, stray <li></li>. The CSS `:empty` rule catches only the
  // genuinely empty ones — a non-breaking space is a real text node, so no selector can reach it
  // (`:blank` never shipped). Strip them in the DOM instead, where the entity is already decoded
  // to the character — and JS `\s` matches U+00A0 — so &nbsp;, &#160; and a raw nbsp all fall
  // out of the same test.
  //
  // Runs per (re-)render via initBlock, i.e. before the drawer can clone a panel, and before
  // rows are counted — see the row/group sweep below.
  const ACC_BLANKABLE = 'p, li, ul, ol, span, div, h1, h2, h3, h4, h5, h6, strong, em, b, i';
  // <br> is deliberately not "media": <p><br></p> is the editor's blank line and should go,
  // while <p>a<br>b</p> survives on its own text anyway.
  const accIsBlank = (el) =>
    !el.querySelector('img, svg, picture, video, iframe, input') &&
    !el.textContent.replace(/\s+/g, '');

  function cleanAccordions(root) {
    root.querySelectorAll('.product-acc__panel').forEach((panel) => {
      // Point rows (benefits / without / usp) are an icon + text pair. When the value is blank
      // only the check icon is left, and the media guard below would preserve it — so judge
      // these on their text alone, before the generic sweep runs.
      panel.querySelectorAll('.product-acc__point').forEach((point) => {
        const text = point.querySelector('.product-acc__point-text');
        if (!text || !text.textContent.replace(/\s+/g, '')) point.remove();
      });
      // Deepest-first: emptying a <span> has to leave its parent <p> removable in the same pass.
      // (querySelectorAll is document order — parent before child — so reverse it.)
      Array.from(panel.querySelectorAll(ACC_BLANKABLE)).reverse().forEach((el) => {
        if (accIsBlank(el)) el.remove();
      });
    });
    // A row whose whole body was blank padding would otherwise leave a link opening an empty
    // drawer. REMOVE it rather than hide it: the lead row is picked by :first-child, and a
    // display:none first row would still match, stranding the group with no expanded content.
    root.querySelectorAll('.product-acc__row').forEach((row) => {
      const panel = row.querySelector('.product-acc__panel');
      if (!panel || accIsBlank(panel)) row.remove();
    });
    // Same reasoning one level up — a group left with no rows drops its header too, matching
    // what product-accordion-group already does server-side for empty metafields.
    root.querySelectorAll('.product-acc__group').forEach((group) => {
      if (!group.querySelector('.product-acc__row')) group.remove();
    });
  }

  // Wire non-custom-element controls in a block wrapper. <product-purchase>/<kaching-bundles-ui> self-init.
  function initBlock(node) {
    if (node.getAttribute('data-block-type') === 'buy_buttons') initQty(node);
    if (node.getAttribute('data-block-type') === 'accordions') cleanAccordions(node);
  }

  // On variant change, re-render the section and swap each purchase block's markup.
  let refreshToken = 0;
  function refreshVariant(scope, variantId) {
    // The wishlist button lives in the gallery toolbar, which is not re-rendered — hand it the
    // new variant itself. `xb-variant-id` is one of the app element's observed attributes, so
    // setting it re-checks whether THIS variant is saved.
    const wishlist = scope.querySelector('.product-gallery__wishlist xb-wishlist-button');
    if (wishlist) wishlist.setAttribute('xb-variant-id', variantId);

    const blocks = scope.querySelectorAll('[data-variant-refresh]');
    if (!blocks.length) return;
    const url = new URL(PRODUCT_URL, window.location.origin);
    url.searchParams.set('variant', variantId);
    url.searchParams.set('section_id', SECTION_ID);
    const token = ++refreshToken;
    fetch(url.toString())
      .then((r) => r.text())
      .then((html) => {
        if (token !== refreshToken) return; // superseded by a newer selection
        const doc = new DOMParser().parseFromString(html, 'text/html');
        blocks.forEach((node) => {
          const id = node.getAttribute('data-block');
          const fresh = doc.querySelector('[data-variant-refresh][data-block="' + id + '"]');
          if (!fresh) return;
          node.innerHTML = fresh.innerHTML; // <product-form>/<product-purchase> re-upgrade on insert
          initBlock(node);
        });
        syncBundleStepper(scope); // buy_buttons re-render brought the stepper back
        syncIncompat(scope);      // purchase block reset to OTP default -> reconcile n+1 visibility
        updatePurchasePrices(scope); // re-cache the fresh loyalty header default + apply selection
        // The fresh purchase card is back on one-time: a checked bar with its own plan puts it on that plan again.
        scope.dispatchEvent(new CustomEvent('purchase:plan', { bubbles: true }));
      })
      .catch(() => {});
  }

  // Initial wiring.
  document.querySelectorAll(scopeSel + ' [data-variant-refresh]').forEach(initBlock);

  // Keep BOTH single purchase and bundles working despite Kaching's client. After ATC, Kaching
  // fires `cart/update.js?kaching-cart=true` to force its deal tier qty (e.g. 6) onto the line.
  // For a DISCOUNT tier that's what we want (qty N + its Function discount) — let it through.
  // For the base "single" tier we must NOT let it bump the shopper's qty to the bundle size.
  // BUT the cart drawer's own quantity edits also use `cart/update?kaching-cart=true`, so we
  // can't just drop every such request while the single tier is checked (that broke in-cart
  // editing). Instead we arm a ONE-SHOT: when WE add a single (`/cart/add` with the base tier
  // checked), drop only the single Kaching enforcement request that immediately follows, then
  // disarm. Later cart edits pass through untouched. A timeout disarms if it never comes.
  if (!window.__kikaKachingUpdateGuard) {
    window.__kikaKachingUpdateGuard = true;
    const _fetch = window.fetch.bind(window);
    window.fetch = function (input, init) {
      try {
        const url = typeof input === 'string' ? input : (input && input.url) || '';
        if (url.indexOf('/cart/add') !== -1) {
          const checked = document.querySelector('[name="bundles"]:checked');
          if (checked && checked.hasAttribute('data-base')) {
            window.__kikaDropNextKachingUpdate = true;
            clearTimeout(window.__kikaDropTimer);
            window.__kikaDropTimer = setTimeout(function () {
              window.__kikaDropNextKachingUpdate = false;
            }, 4000);
          }
        } else if (window.__kikaDropNextKachingUpdate &&
                   url.indexOf('/cart/update') !== -1 && url.indexOf('kaching-cart=true') !== -1) {
          window.__kikaDropNextKachingUpdate = false; // one-shot: only the post-add enforcement
          clearTimeout(window.__kikaDropTimer);
          return _fetch('/cart.js', { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
        }
      } catch (e) { /* fall through to the real fetch */ }
      return _fetch(input, init);
    };
  }

  // Hide the manual qty stepper whenever a bundle card is visible (Kaching owns the qty then).
  // This lives at top level (re-runs on every theme-dev hot-reload) instead of inside
  // <kaching-bundles-ui>: a custom element keeps its FIRST-registered class across hot-reloads
  // (customElements can't be redefined), so logic inside build() silently stops running after
  // the first edit. Whichever class version un-hides the card, its `hidden` attribute flips and
  // the observer catches it.
  document.querySelectorAll(scopeSel).forEach((scope) => {
    scope.querySelectorAll('kaching-bundles-ui').forEach((ui) => {
      syncBundleStepper(scope); // reflect current state before observing
      new MutationObserver(() => { syncBundleStepper(scope); updatePurchasePrices(scope); })
        .observe(ui, { attributes: true, attributeFilter: ['hidden'] });
    });
    // Re-evaluate when the shopper switches bundle tiers (radios are rendered client-side). N+1
    // and subscription are mutually exclusive: picking the n+1 tier reverts subscription back to
    // OTP, then syncIncompat reflects the visibility. (The subscription->n+1 direction is handled
    // in product-purchase's apply() — its card click sets the radio programmatically, so no
    // `change` event reaches here.)
    scope.addEventListener('change', (e) => {
      const t = e.target;
      if (!t || t.name !== 'bundles') return;
      if (t.checked && t.hasAttribute('data-nplus1')) {
        const subRadio = scope.querySelector('.product-buy__type-radio[value="subscribe"]');
        if (subRadio && subRadio.checked) {
          const otp = scope.querySelector('.product-buy__type-radio[value="onetime"]');
          if (otp) otp.checked = true;
          const purchase = scope.querySelector('product-purchase');
          if (purchase && purchase.syncType) purchase.syncType();
        }
      }
      syncIncompat(scope); syncBundleStepper(scope); updatePurchasePrices(scope);
    });
    // A client-side tier re-render (variant switch, deal resolve) doesn't flip [hidden], so
    // reconcile the mutual exclusion and refresh prices/header whenever the options (re)render.
    scope.addEventListener('bundles:rendered', () => { syncIncompat(scope); updatePurchasePrices(scope); });
    // OTP + subscription totals also track the manual quantity stepper.
    scope.addEventListener('input', (e) => {
      if (e.target && e.target.classList && e.target.classList.contains('product-buy__qty-input')) {
        updatePurchasePrices(scope);
      }
    });
    // Loyalty / subscription "i" tooltips: toggle the info text on icon click, and close any
    // open tooltip when clicking elsewhere. preventDefault stops the enclosing <label> from
    // toggling its purchase-type radio (the card's own click handler already ignores .product-buy__info).
    scope.addEventListener('click', (e) => {
      const infoBtn = e.target.closest && e.target.closest('[data-info]');
      if (infoBtn) {
        e.preventDefault();
        e.stopPropagation();
        const key = infoBtn.getAttribute('data-info');
        const pop = scope.querySelector('.product-buy__info-pop[data-info-pop="' + key + '"]');
        const willOpen = pop && pop.hasAttribute('hidden');
        scope.querySelectorAll('.product-buy__info-pop').forEach((p) => p.setAttribute('hidden', ''));
        scope.querySelectorAll('[data-info]').forEach((b) => b.setAttribute('aria-expanded', 'false'));
        if (willOpen) { infoBtn.setAttribute('aria-expanded', 'true'); positionInfoPop(infoBtn, pop); }
        return;
      }
      // A click anywhere outside a tooltip closes any open one (clicks inside the text pass through).
      if (!(e.target.closest && e.target.closest('.product-buy__info-pop'))) {
        scope.querySelectorAll('.product-buy__info-pop:not([hidden])').forEach((p) => p.setAttribute('hidden', ''));
        scope.querySelectorAll('[data-info][aria-expanded="true"]').forEach((b) => b.setAttribute('aria-expanded', 'false'));
      }
    });
    // Accordion detail drawer: a row link opens the section's sidebar with that row's title +
    // content. The content is CLONED out of the row's hidden .product-acc__panel rather than
    // moved, so the panel stays in the DOM (crawlable) and a variant refresh can replace the
    // accordions block without stranding markup inside the drawer. Listeners are delegated on
    // the section, so they survive that refresh; the drawer itself lives at section level.
    const accDrawer = scope.querySelector('[data-acc-drawer]');
    if (accDrawer) {
      const accTitle = accDrawer.querySelector('.product-acc-drawer__title');
      const accBody = accDrawer.querySelector('.product-acc-drawer__body');
      let accReturnFocus = null;

      const closeAccDrawer = () => {
        if (!accDrawer.classList.contains('is-open')) return;
        accDrawer.classList.remove('is-open');
        document.body.classList.remove('product-acc-drawer-open');
        if (accReturnFocus) { accReturnFocus.focus(); accReturnFocus = null; }
      };

      const openAccDrawer = (title, html, returnFocus) => {
        accReturnFocus = returnFocus || null;
        accTitle.textContent = title;
        accBody.innerHTML = html;
        accBody.scrollTop = 0;
        accDrawer.classList.add('is-open');
        document.body.classList.add('product-acc-drawer-open');
        const closeBtn = accDrawer.querySelector('.product-acc-drawer__close');
        if (closeBtn) closeBtn.focus();
      };

      scope.addEventListener('click', (e) => {
        const opener = e.target.closest && e.target.closest('[data-acc-open]');
        if (opener) {
          const panel = opener.parentElement && opener.parentElement.querySelector('.product-acc__panel');
          if (!panel) return;
          e.preventDefault();
          openAccDrawer(opener.textContent.trim(), panel.innerHTML, opener);
          return;
        }
        // Delivery card, "check stock in stores": the store list is rendered by the theme's
        // <pickup-availability>, which appends a Dawn <pickup-availability-drawer> to <body>.
        // We show that LIST inside our own drawer instead of opening Dawn's (Figma review
        // 1445:656 #2 — the store-stock popup has to look like the section's other drawers;
        // Dawn's own sits at z-index 4, under the header, and carries none of this styling).
        // The list is cloned, not moved, so a variant refresh can rebuild it freely.
        const pickupLink = e.target.closest && e.target.closest('[data-open-pickup]');
        if (pickupLink) {
          e.preventDefault();
          const src = document.querySelector('pickup-availability-drawer .pickup-availability-list');
          if (!src) return; // fetch still in flight — nothing to show yet
          const list = src.cloneNode(true);
          // Dawn's pickup section marks locations with no stock `hide` (display:none). We show
          // them, flagged and labelled, instead of dropping them — "no store has it right now"
          // is a valid answer to this question, and a drawer of nothing but hidden rows isn't.
          list.querySelectorAll('.pickup-availability-list__item.hide').forEach((li) => {
            li.classList.remove('hide');
            li.classList.add('product-acc__store--out');
            const label = pickupLink.getAttribute('data-out-label');
            if (label) {
              const note = document.createElement('p');
              note.className = 'product-acc__store-out-note';
              note.textContent = label;
              li.appendChild(note);
            }
          });
          openAccDrawer(pickupLink.textContent.trim(), list.outerHTML, pickupLink);
          return;
        }
        if (e.target.closest && e.target.closest('[data-acc-close]')) {
          e.preventDefault();
          closeAccDrawer();
        }
      });
      document.addEventListener('keydown', (e) => { if (e.key === 'Escape') closeAccDrawer(); });
    }
    updatePurchasePrices(scope); // initial totals for the default selection
    syncIncompat(scope);         // initial n+1 <-> subscription visibility (in case a tier already rendered)
  });
})();
